import React, { useState, useEffect, useMemo, useRef } from "react";
import DriveWorkspace from "./components/DriveWorkspace";
import ChatWorkspace from "./components/ChatWorkspace";
import InputEngine from "./components/InputEngine";
import AIExporterModal from "./components/AIExporterModal";
import {
  WorkspaceItem,
  ChatMessage,
  AppMode,
  AttachedFile,
  DriveLoadedContent,
  DriveExtractedImage,
  ActiveContextPayload,
  GeneratedImageAsset,
} from "./types";
import {
  extractFolderId,
  fetchDriveFolderRecursively,
  downloadDriveLoadedContent,
  uploadFileToDriveFolder,
  fetchDriveFolderName,
  buildDriveChatPayload,
} from "./lib/gdrive";
import {
  googleSignIn,
  initAuth,
  logout,
  setCustomerBenchDeviceMode,
  getCustomerBenchDeviceMode,
  ensureGoogleDriveToken,
} from "./lib/auth";
import {
  saveWorkspaceState,
  loadWorkspaceState,
  loadLastSavedWorkspaceState,
  clearLastSavedWorkspaceState,
  saveChatArchive,
  listChatArchives,
  loadChatArchive,
  deleteChatArchive,
  saveSavedChatFolders,
  loadSavedChatFolders,
} from "./lib/db";
import { User } from "firebase/auth";
import {
  Lock,
  ShieldAlert,
  LogIn,
  LogOut,
  Send,
  Maximize2,
  Paperclip,
  Activity,
  FileText,
  X,
  RefreshCw,
  MessageSquare,
  Save,
  Edit3,
  MoveRight,
  Trash2,
  ChevronDown,
} from "lucide-react";

const ThemedChatWorkspace = ChatWorkspace as React.ComponentType<any>;
const ThemedInputEngine = InputEngine as React.ComponentType<any>;

const DEFAULT_DRIVE_FOLDER_ID = "1V3Hgblrd2mIKTkopoqSU0dzhJi_1tMHs";

// Phase 13 payload rules:
// - The visible live chat keeps real images/screenshots in memory for export.
// - Cloud workspace save must never replace image data with a fake/truncated data URL.
// - Model requests must not resend every historical screenshot at full size.
// - Current turn images remain highest priority. Recent visual turns are preserved only while size caps allow.
const CLOUD_SAVE_MAX_INLINE_MEDIA_CHARS = 30000;
const MODEL_PAYLOAD_MAX_TOTAL_ATTACHMENT_BASE64_CHARS = 4200000;
const MODEL_PAYLOAD_MAX_SINGLE_ATTACHMENT_BASE64_CHARS = 1800000;
const MODEL_PAYLOAD_KEEP_RECENT_VISUAL_TURNS = 4;
const MODEL_PAYLOAD_MAX_TEXT_CHARS_USER = 14000;
const MODEL_PAYLOAD_MAX_TEXT_CHARS_ASSISTANT = 9000;
const MODEL_PAYLOAD_MAX_DOCUMENT_ATTACHMENT_CHARS = 70000;
const MODEL_PAYLOAD_MAX_TOTAL_DOCUMENT_ATTACHMENT_CHARS = 160000;
const CONTINUITY_PACKET_MAX_CHARS = 14000;
const ATTACHMENT_METADATA_ONLY_NOTE =
  "[Attachment metadata only in model payload. Full visual payload was not resent to prevent request failure. Use confirmed prior notes only unless the current turn includes the visual payload.]";

const LOCAL_MEDIA_DB_NAME = "rodgerspc_command_center_media_v1";
const LOCAL_MEDIA_STORE_NAME = "media";
const LOCAL_SAVED_CHATS_STORE_NAME = "savedChats";
const LOCAL_MEDIA_PLACEHOLDER_PREFIX = "[RodgersPC local media vault";

type LocalMediaRecord = {
  id: string;
  value: string;
  mimeType?: string;
  name?: string;
  updatedAt: string;
};

const isBrowserRuntime = () =>
  typeof window !== "undefined" && typeof window.indexedDB !== "undefined";

const openLocalMediaDb = (): Promise<IDBDatabase | null> => {
  if (!isBrowserRuntime()) return Promise.resolve(null);

  return new Promise((resolve) => {
    const request = window.indexedDB.open(LOCAL_MEDIA_DB_NAME, 2);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(LOCAL_MEDIA_STORE_NAME)) {
        db.createObjectStore(LOCAL_MEDIA_STORE_NAME, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(LOCAL_SAVED_CHATS_STORE_NAME)) {
        db.createObjectStore(LOCAL_SAVED_CHATS_STORE_NAME, { keyPath: "id" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      console.warn("[Local Media Vault] IndexedDB open failed:", request.error);
      resolve(null);
    };
  });
};

const putLocalMediaRecord = async (record: LocalMediaRecord): Promise<void> => {
  const db = await openLocalMediaDb();
  if (!db) return;

  await new Promise<void>((resolve) => {
    const tx = db.transaction(LOCAL_MEDIA_STORE_NAME, "readwrite");
    tx.objectStore(LOCAL_MEDIA_STORE_NAME).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => {
      console.warn("[Local Media Vault] Media put failed:", tx.error);
      resolve();
    };
  });

  db.close();
};

const getLocalMediaRecord = async (id: string): Promise<LocalMediaRecord | null> => {
  const db = await openLocalMediaDb();
  if (!db) return null;

  const result = await new Promise<LocalMediaRecord | null>((resolve) => {
    const tx = db.transaction(LOCAL_MEDIA_STORE_NAME, "readonly");
    const request = tx.objectStore(LOCAL_MEDIA_STORE_NAME).get(id);
    request.onsuccess = () => resolve((request.result as LocalMediaRecord) || null);
    request.onerror = () => {
      console.warn("[Local Media Vault] Media get failed:", request.error);
      resolve(null);
    };
  });

  db.close();
  return result;
};


type LocalSavedChatMemoryPayload = {
  id: string;
  value: {
    version: number;
    folders: SavedChatFolder[];
    chats: SavedChatRecord[];
    activeSavedChatId: string | null;
    savedAt: string;
  };
};

const putSavedChatMemoryPayload = async (uid: string, value: LocalSavedChatMemoryPayload["value"]): Promise<void> => {
  const db = await openLocalMediaDb();
  if (!db) return;

  await new Promise<void>((resolve) => {
    const tx = db.transaction(LOCAL_SAVED_CHATS_STORE_NAME, "readwrite");
    tx.objectStore(LOCAL_SAVED_CHATS_STORE_NAME).put({
      id: getSavedChatStorageKey(uid),
      value,
    } as LocalSavedChatMemoryPayload);
    tx.oncomplete = () => resolve();
    tx.onerror = () => {
      console.warn("[Saved Chat Memory] IndexedDB write failed:", tx.error);
      resolve();
    };
  });

  db.close();
};

const getSavedChatMemoryPayload = async (uid: string): Promise<LocalSavedChatMemoryPayload["value"] | null> => {
  const db = await openLocalMediaDb();
  if (!db) return null;

  const result = await new Promise<LocalSavedChatMemoryPayload | null>((resolve) => {
    const tx = db.transaction(LOCAL_SAVED_CHATS_STORE_NAME, "readonly");
    const request = tx.objectStore(LOCAL_SAVED_CHATS_STORE_NAME).get(getSavedChatStorageKey(uid));
    request.onsuccess = () => resolve((request.result as LocalSavedChatMemoryPayload) || null);
    request.onerror = () => {
      console.warn("[Saved Chat Memory] IndexedDB read failed:", request.error);
      resolve(null);
    };
  });

  db.close();
  return result?.value || null;
};

const getAttachmentLocalMediaKey = (messageId: string, attachmentId: string, field: "base64" | "content") =>
  `chat_${messageId}_attachment_${attachmentId}_${field}`;

const getGeneratedAssetLocalMediaKey = (messageId: string, assetId: string) =>
  `chat_${messageId}_generated_${assetId}_dataUrl`;

const isLargeRenderableMediaValue = (value: unknown): boolean => {
  const raw = String(value || "");
  return raw.startsWith("data:image/") || raw.startsWith("data:application/pdf") || raw.startsWith("data:video/");
};

const makeLocalMediaPlaceholder = (key: string, name = "media") =>
  `${LOCAL_MEDIA_PLACEHOLDER_PREFIX}: ${key}; ${name}]`;

const extractLocalMediaPlaceholderKey = (value: unknown): string => {
  const raw = String(value || "");
  if (!raw.startsWith(LOCAL_MEDIA_PLACEHOLDER_PREFIX)) return "";
  const match = raw.match(/^\[RodgersPC local media vault:\s*([^;\]]+)/i);
  return match?.[1]?.trim() || "";
};

const persistMessageMediaToLocalVault = async (messagesToPersist: ChatMessage[]): Promise<void> => {
  if (!Array.isArray(messagesToPersist) || messagesToPersist.length === 0) return;

  const writes: Promise<void>[] = [];

  messagesToPersist.forEach((message) => {
    const messageId = String(message.id || "");

    (message.attachments || []).forEach((attachment) => {
      const attachmentId = String(attachment.id || "");
      if (!messageId || !attachmentId) return;

      const base64 = String(attachment.base64 || "");
      if (isLargeRenderableMediaValue(base64) && !hasOptimizedPayloadPlaceholder(base64)) {
        writes.push(
          putLocalMediaRecord({
            id: getAttachmentLocalMediaKey(messageId, attachmentId, "base64"),
            value: base64,
            mimeType: attachment.mimeType,
            name: attachment.name,
            updatedAt: new Date().toISOString(),
          }),
        );
      }

      const content = String(attachment.content || "");
      if (isLargeRenderableMediaValue(content) && !hasOptimizedPayloadPlaceholder(content)) {
        writes.push(
          putLocalMediaRecord({
            id: getAttachmentLocalMediaKey(messageId, attachmentId, "content"),
            value: content,
            mimeType: attachment.mimeType,
            name: attachment.name,
            updatedAt: new Date().toISOString(),
          }),
        );
      }
    });

    const assets = [
      ...(message.imageAsset ? [message.imageAsset] : []),
      ...(message.imageAssets || []),
    ].filter(Boolean) as GeneratedImageAsset[];

    assets.forEach((asset) => {
      const assetId = String(asset.id || "");
      const dataUrl = String(asset.dataUrl || "");
      if (!messageId || !assetId || !isLargeRenderableMediaValue(dataUrl)) return;

      writes.push(
        putLocalMediaRecord({
          id: getGeneratedAssetLocalMediaKey(messageId, assetId),
          value: dataUrl,
          mimeType: asset.mimeType,
          name: asset.name,
          updatedAt: new Date().toISOString(),
        }),
      );
    });
  });

  await Promise.all(writes);
};

const hydrateMessagesFromLocalMediaVault = async (messagesToHydrate: ChatMessage[]): Promise<ChatMessage[]> => {
  if (!Array.isArray(messagesToHydrate) || messagesToHydrate.length === 0) return messagesToHydrate;

  const hydratedMessages = await Promise.all(
    messagesToHydrate.map(async (message) => {
      const messageId = String(message.id || "");

      const hydratedAttachments = await Promise.all(
        (message.attachments || []).map(async (attachment) => {
          const attachmentId = String(attachment.id || "");
          if (!messageId || !attachmentId) return attachment;

          let nextBase64 = attachment.base64;
          let nextContent = attachment.content;

          const base64PlaceholderKey = extractLocalMediaPlaceholderKey(nextBase64);
          const base64Key = base64PlaceholderKey || getAttachmentLocalMediaKey(messageId, attachmentId, "base64");
          if (!isLargeRenderableMediaValue(nextBase64) || hasOptimizedPayloadPlaceholder(nextBase64)) {
            const storedBase64 = await getLocalMediaRecord(base64Key);
            if (storedBase64?.value) nextBase64 = storedBase64.value;
          }

          const contentPlaceholderKey = extractLocalMediaPlaceholderKey(nextContent);
          const contentKey = contentPlaceholderKey || getAttachmentLocalMediaKey(messageId, attachmentId, "content");
          if (!isLargeRenderableMediaValue(nextContent) || hasOptimizedPayloadPlaceholder(nextContent)) {
            const storedContent = await getLocalMediaRecord(contentKey);
            if (storedContent?.value) nextContent = storedContent.value;
          }

          return {
            ...attachment,
            base64: nextBase64,
            content: nextContent,
          };
        }),
      );

      const hydrateAsset = async (asset?: GeneratedImageAsset): Promise<GeneratedImageAsset | undefined> => {
        if (!asset) return undefined;
        if (isLargeRenderableMediaValue(asset.dataUrl)) return asset;

        const assetId = String(asset.id || "");
        if (!messageId || !assetId) return asset;

        const stored = await getLocalMediaRecord(getGeneratedAssetLocalMediaKey(messageId, assetId));
        if (!stored?.value) return asset;

        return {
          ...asset,
          dataUrl: stored.value,
          mimeType: asset.mimeType || stored.mimeType,
          name: asset.name || stored.name,
        };
      };

      const hydratedImageAsset = await hydrateAsset(message.imageAsset);
      const hydratedImageAssets = (await Promise.all((message.imageAssets || []).map(hydrateAsset))).filter(Boolean) as GeneratedImageAsset[];
      const renderUrl =
        getRenderableImageFromAsset(hydratedImageAsset) ||
        hydratedImageAssets.map((asset) => getRenderableImageFromAsset(asset)).find(Boolean) ||
        (isRenderableImageUrl(message.imageUrl) ? String(message.imageUrl) : "");

      return {
        ...message,
        attachments: hydratedAttachments,
        imageAsset: hydratedImageAsset,
        imageAssets: hydratedImageAssets.length ? hydratedImageAssets : message.imageAssets,
        imageUrl: renderUrl || message.imageUrl,
      };
    }),
  );

  return hydratedMessages.map(sanitizeRestoredMessageMedia);
};


const hasOptimizedPayloadPlaceholder = (value: unknown): boolean => {
  return String(value || "").includes("[Payload size optimized]");
};

const isInlineMediaString = (value: unknown): boolean => {
  const raw = String(value || "");
  return raw.startsWith("data:image/") || raw.startsWith("data:application/pdf") || raw.startsWith("data:video/");
};

const parseImageMimeFromDataUrl = (value: unknown): string => {
  const match = String(value || "").match(/^data:([^;]+);base64,/);
  return match?.[1] || "image/png";
};

const isRenderableImageUrl = (value: unknown): boolean => {
  const raw = String(value || "").trim();
  if (!raw || hasOptimizedPayloadPlaceholder(raw)) return false;
  return raw.startsWith("data:image/") || raw.startsWith("blob:") || raw.startsWith("http://") || raw.startsWith("https://");
};

const getRenderableImageFromAsset = (asset?: GeneratedImageAsset | null): string => {
  if (!asset) return "";
  if (isRenderableImageUrl(asset.dataUrl)) return String(asset.dataUrl);
  if (isRenderableImageUrl(asset.url)) return String(asset.url);
  return "";
};

const normalizeGeneratedImageAsset = (
  asset: GeneratedImageAsset | undefined,
  fallbackImageUrl?: string,
): GeneratedImageAsset | undefined => {
  const renderUrl = getRenderableImageFromAsset(asset) || (isRenderableImageUrl(fallbackImageUrl) ? String(fallbackImageUrl) : "");

  if (!asset && !renderUrl) return undefined;

  return {
    id: asset?.id || `gen_asset_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name: asset?.name || "RodgersPC generated image.png",
    mimeType: asset?.mimeType || parseImageMimeFromDataUrl(renderUrl),
    width: asset?.width,
    height: asset?.height,
    sizeBytes: asset?.sizeBytes,
    dataUrl: asset?.dataUrl || (renderUrl.startsWith("data:image/") ? renderUrl : undefined),
    url: asset?.url || (!renderUrl.startsWith("data:image/") ? renderUrl : undefined),
    storagePath: asset?.storagePath,
    source: asset?.source || "generated_image",
    prompt: asset?.prompt,
    createdAt: asset?.createdAt || new Date().toISOString(),
  };
};

const buildGeneratedImageAssetFromResponse = (data: any): GeneratedImageAsset | undefined => {
  const suppliedAsset = data?.imageAsset as GeneratedImageAsset | undefined;
  const imageUrl = isRenderableImageUrl(data?.imageUrl) ? String(data.imageUrl) : "";
  const asset = normalizeGeneratedImageAsset(suppliedAsset, imageUrl);

  if (!asset) return undefined;

  return {
    ...asset,
    name: asset.name || data?.imageName || "RodgersPC generated image.png",
    mimeType: data?.imageMimeType || asset.mimeType || parseImageMimeFromDataUrl(asset.dataUrl || asset.url || imageUrl),
    width: Number.isFinite(Number(data?.imageWidth)) ? Number(data.imageWidth) : asset.width,
    height: Number.isFinite(Number(data?.imageHeight)) ? Number(data.imageHeight) : asset.height,
    prompt: asset.prompt || data?.imagePrompt,
  };
};

const stripGeneratedImageAssetForModelPayload = (
  asset: GeneratedImageAsset | undefined,
  allowFullVisual: boolean,
  remainingBase64Budget: { value: number },
): GeneratedImageAsset | undefined => {
  if (!asset) return undefined;

  const dataUrl = isRenderableImageUrl(asset.dataUrl) ? String(asset.dataUrl) : "";
  const keepDataUrl =
    allowFullVisual &&
    dataUrl.startsWith("data:image/") &&
    dataUrl.length <= MODEL_PAYLOAD_MAX_SINGLE_ATTACHMENT_BASE64_CHARS &&
    dataUrl.length <= remainingBase64Budget.value;

  if (keepDataUrl) {
    remainingBase64Budget.value -= dataUrl.length;
    return asset;
  }

  return {
    ...asset,
    dataUrl: undefined,
  };
};

const truncateForModelPayload = (value: unknown, maxChars: number): string => {
  const cleaned = String(value || "");
  if (cleaned.length <= maxChars) return cleaned;
  return `${cleaned.slice(0, maxChars).trim()}\n\n[Text trimmed from model payload for request stability.]`;
};


type SafeApiResponsePayload = {
  ok: boolean;
  data: any;
  rawText: string;
  contentType: string;
  status: number;
};

const isLikelyHtmlResponse = (value: unknown): boolean => {
  const raw = String(value || "").trim().toLowerCase();
  return raw.startsWith("<!doctype") || raw.startsWith("<html") || raw.includes("<body") || raw.includes("</html>");
};

const clipApiText = (value: unknown, maxChars = 1800): string => {
  const cleaned = String(value || "")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return "No readable server error text was returned.";
  return cleaned.length > maxChars ? `${cleaned.slice(0, maxChars).trim()}…` : cleaned;
};

const readApiResponseSafely = async (response: Response): Promise<SafeApiResponsePayload> => {
  const contentType = response.headers.get("content-type") || "";
  const rawText = await response.text().catch(() => "");
  const looksJson = contentType.toLowerCase().includes("application/json") || /^[\s\r\n]*[\[{]/.test(rawText);

  if (!looksJson) {
    return {
      ok: response.ok,
      data: {
        error: isLikelyHtmlResponse(rawText)
          ? "The server returned HTML instead of JSON for /api/chat. This is a route/crash/fallback issue, not a model answer."
          : "The server returned a non-JSON response for /api/chat.",
        rawText: clipApiText(rawText),
      },
      rawText,
      contentType,
      status: response.status,
    };
  }

  try {
    return {
      ok: response.ok,
      data: rawText ? JSON.parse(rawText) : {},
      rawText,
      contentType,
      status: response.status,
    };
  } catch (err: any) {
    return {
      ok: false,
      data: {
        error: "The server response looked like JSON but could not be parsed.",
        parseError: err?.message || String(err),
        rawText: clipApiText(rawText),
      },
      rawText,
      contentType,
      status: response.status,
    };
  }
};

const isDocumentPayloadPlaceholder = (value: unknown): boolean => {
  const raw = String(value || "").trim();
  return (
    raw.startsWith("[Uploaded binary document:") ||
    raw.startsWith("[Uploaded PDF:") ||
    raw.startsWith("[Attachment binary payload") ||
    raw.startsWith("[Attachment metadata only") ||
    raw.startsWith("[Payload size optimized]")
  );
};

const isPdfUploadFile = (file: File): boolean => {
  const name = file.name || "";
  const mime = file.type || "";
  return mime === "application/pdf" || /\.pdf$/i.test(name);
};

const isImageUploadFile = (file: File): boolean => {
  return (file.type || "").startsWith("image/") || /\.(png|jpe?g|webp|gif|bmp)$/i.test(file.name || "");
};

const isVideoUploadFile = (file: File): boolean => {
  const name = file.name || "";
  const mime = file.type || "";
  return mime.startsWith("video/") || /\.(mp4|mov|m4v|webm|mpeg|mpg|avi)$/i.test(name);
};

const isBinaryOfficeUploadName = (file: File): boolean => {
  const name = file.name || "";
  const mime = file.type || "";
  return (
    /\.(docx|xlsx|pptx)$/i.test(name) ||
    mime.includes("officedocument") ||
    mime === "application/msword"
  );
};

const looksLikeBinaryDocumentText = (value: string): boolean => {
  const sample = value.slice(0, 4000);
  if (sample.startsWith("PK\u0003\u0004")) return true;
  const nulCount = (sample.match(/\u0000/g) || []).length;
  if (nulCount >= 3) return true;
  const replacementCount = (sample.match(/ /g) || []).length;
  return sample.length > 0 && replacementCount / sample.length > 0.08;
};

const readFileAsTextSafely = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error(`Could not read ${file.name} as text.`));
    reader.readAsText(file);
  });
};

const readFileAsDataUrlSafely = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error(`Could not read ${file.name} as binary payload.`));
    reader.readAsDataURL(file);
  });
};

const sanitizeAttachmentForCloudSave = (att: AttachedFile, messageId?: string): AttachedFile => {
  const base64 = att.base64 || "";
  const content = String(att.content || "");
  const attachmentId = String(att.id || "");
  const base64Key = messageId && attachmentId ? getAttachmentLocalMediaKey(messageId, attachmentId, "base64") : "";
  const contentKey = messageId && attachmentId ? getAttachmentLocalMediaKey(messageId, attachmentId, "content") : "";

  if (
    hasOptimizedPayloadPlaceholder(base64) ||
    hasOptimizedPayloadPlaceholder(content) ||
    base64.length > CLOUD_SAVE_MAX_INLINE_MEDIA_CHARS ||
    (isInlineMediaString(content) && content.length > CLOUD_SAVE_MAX_INLINE_MEDIA_CHARS)
  ) {
    return {
      ...att,
      base64: base64Key ? makeLocalMediaPlaceholder(base64Key, att.name || "image") : undefined,
      content: contentKey ? makeLocalMediaPlaceholder(contentKey, att.name || "attachment") : `[${att.type === "image" ? "Image" : "Attachment"} not stored in cloud backup: ${att.name || "attachment"}. Full media is stored in the local media vault when available.]`,
    };
  }

  return att;
};

const sanitizeMessageForCloudSave = (m: ChatMessage): ChatMessage => {
  const imageAsset = normalizeGeneratedImageAsset(m.imageAsset, m.imageUrl);
  const imageAssets = (m.imageAssets || [])
    .map((asset) => normalizeGeneratedImageAsset(asset))
    .filter(Boolean) as GeneratedImageAsset[];
  const renderUrl = getRenderableImageFromAsset(imageAsset) || (isRenderableImageUrl(m.imageUrl) ? String(m.imageUrl) : "");

  return {
    ...m,
    reports: [],
    imageUrl: renderUrl || undefined,
    imageAsset,
    imageAssets: imageAssets.length ? imageAssets : m.imageAssets,
    attachments: m.attachments?.map((att) => sanitizeAttachmentForCloudSave(att, String(m.id || ""))),
  };
};

const sanitizeRestoredMessageMedia = (m: ChatMessage): ChatMessage => {
  const imageAsset = normalizeGeneratedImageAsset(m.imageAsset, m.imageUrl);
  const renderUrl = getRenderableImageFromAsset(imageAsset) || (isRenderableImageUrl(m.imageUrl) ? String(m.imageUrl) : "");

  return {
    ...m,
    imageUrl: renderUrl || undefined,
    imageAsset,
    imageAssets: m.imageAssets?.map((asset) => normalizeGeneratedImageAsset(asset)).filter(Boolean) as GeneratedImageAsset[] | undefined,
    attachments: m.attachments?.map((att) => {
      if (
        hasOptimizedPayloadPlaceholder(att.base64) ||
        hasOptimizedPayloadPlaceholder(att.content)
      ) {
        return {
          ...att,
          base64: undefined,
          content: `[Attachment restored without full media payload: ${att.name || "attachment"}. Reattach the image if visual inspection/export is needed.]`,
        };
      }

      return att;
    }),
  };
};

type ModelPayloadMessage = Partial<ChatMessage> & {
  role: "user" | "assistant";
  modeAtSend?: AppMode;
  isContinuityPacket?: boolean;
  isModeChangeIndicator?: boolean;
  newMode?: AppMode;
  prevMode?: AppMode;
};

const isModelReadableDocumentAttachment = (att: AttachedFile): boolean => {
  const rawBase64 = String(att.base64 || "");
  const rawContent = String(att.content || "");
  const isVisual = att.type === "image" || att.type === "video" || Boolean(rawBase64 && isInlineMediaString(rawBase64));

  if (isVisual) return false;
  if (!rawContent.trim()) return false;
  if (isInlineMediaString(rawContent)) return false;
  if (hasOptimizedPayloadPlaceholder(rawContent)) return false;
  if (rawContent.includes(ATTACHMENT_METADATA_ONLY_NOTE)) return false;
  if (rawContent.startsWith(LOCAL_MEDIA_PLACEHOLDER_PREFIX)) return false;
  if (isDocumentPayloadPlaceholder(rawContent)) return false;

  return true;
};

const buildDocumentAttachmentEvidenceText = (
  att: AttachedFile,
  maxChars = MODEL_PAYLOAD_MAX_DOCUMENT_ATTACHMENT_CHARS,
): string => {
  const rawContent = String(att.content || "").trim();
  if (!rawContent) return "";

  const clipped =
    rawContent.length > maxChars
      ? `${rawContent.slice(0, maxChars).trim()}\n\n[Attached document text trimmed for request stability. Ask Joshua to narrow the document or attach a smaller excerpt if more detail is needed.]`
      : rawContent;

  return [
    "RODGERSPC ATTACHED DOCUMENT TEXT",
    `File: ${att.name || "attachment"}`,
    `Type: ${att.type || "file"}`,
    `MIME: ${att.mimeType || "unknown"}`,
    "Rule: this is current-turn file evidence. Read it before asking Joshua for facts already inside it.",
    "BEGIN ATTACHED DOCUMENT",
    clipped,
    "END ATTACHED DOCUMENT",
  ].join("\n");
};

const buildModelSafeAttachmentPayload = (
  att: AttachedFile,
  allowFullVisual: boolean,
  remainingBase64Budget: { value: number },
): AttachedFile => {
  const rawBase64 = att.base64 || "";
  const rawContent = String(att.content || "");
  const isVisual = att.type === "image" || att.type === "video" || Boolean(rawBase64 && isInlineMediaString(rawBase64));

  if (!isVisual && isModelReadableDocumentAttachment(att)) {
    return {
      ...att,
      base64: undefined,
      content: buildDocumentAttachmentEvidenceText(att),
    };
  }

  if (!isVisual && rawBase64 && !hasOptimizedPayloadPlaceholder(rawBase64)) {
    const keepBinaryDocumentPayload =
      rawBase64.length <= MODEL_PAYLOAD_MAX_SINGLE_ATTACHMENT_BASE64_CHARS &&
      rawBase64.length <= remainingBase64Budget.value;

    if (keepBinaryDocumentPayload) {
      remainingBase64Budget.value -= rawBase64.length;
      return {
        ...att,
        base64: rawBase64,
        content: rawContent || `[Attachment binary payload sent for server-side document parsing: ${att.name || "attachment"}. MIME: ${att.mimeType || "unknown"}.]`,
      };
    }
  }

  if (
    allowFullVisual &&
    isVisual &&
    rawBase64 &&
    !hasOptimizedPayloadPlaceholder(rawBase64) &&
    rawBase64.length <= MODEL_PAYLOAD_MAX_SINGLE_ATTACHMENT_BASE64_CHARS &&
    rawBase64.length <= remainingBase64Budget.value
  ) {
    remainingBase64Budget.value -= rawBase64.length;
    return att;
  }

  return {
    ...att,
    base64: undefined,
    content: `${ATTACHMENT_METADATA_ONLY_NOTE} Name: ${att.name || "attachment"}. Type: ${att.type || "file"}. MIME: ${att.mimeType || "unknown"}. Original text chars: ${rawContent.length}.`,
  };
};

const buildModelPayloadMessages = (
  rawMessages: ModelPayloadMessage[],
  currentUserMessageId: string,
): ModelPayloadMessage[] => {
  const attachmentBearingUserIds = rawMessages
    .filter((m) => m.role === "user" && Array.isArray(m.attachments) && m.attachments.length > 0)
    .slice(-MODEL_PAYLOAD_KEEP_RECENT_VISUAL_TURNS)
    .map((m) => String(m.id || ""));

  const remainingBase64Budget = { value: MODEL_PAYLOAD_MAX_TOTAL_ATTACHMENT_BASE64_CHARS };
  const remainingDocumentBudget = { value: MODEL_PAYLOAD_MAX_TOTAL_DOCUMENT_ATTACHMENT_CHARS };

  return rawMessages.map((m) => {
    const isCurrentUser = String(m.id || "") === currentUserMessageId;
    const isRecentVisualUserTurn = attachmentBearingUserIds.includes(String(m.id || ""));
    const allowFullVisual = isCurrentUser || isRecentVisualUserTurn;

    const attachmentPayloads = (m.attachments || []).map((att) =>
      buildModelSafeAttachmentPayload(att, allowFullVisual, remainingBase64Budget),
    );

    const documentEvidenceBlocks = attachmentPayloads
      .filter((att) => isModelReadableDocumentAttachment(att))
      .map((att) => String(att.content || "").trim())
      .filter(Boolean)
      .map((block) => {
        if (remainingDocumentBudget.value <= 0) return "";
        const clipped = block.length > remainingDocumentBudget.value
          ? `${block.slice(0, remainingDocumentBudget.value).trim()}\n\n[Additional attached document evidence omitted because request document budget was reached.]`
          : block;
        remainingDocumentBudget.value -= clipped.length;
        return clipped;
      })
      .filter(Boolean);

    const baseContent = m.isContinuityPacket
      ? truncateForModelPayload(m.content, CONTINUITY_PACKET_MAX_CHARS)
      : truncateForModelPayload(
          m.content,
          m.role === "user" ? MODEL_PAYLOAD_MAX_TEXT_CHARS_USER : MODEL_PAYLOAD_MAX_TEXT_CHARS_ASSISTANT,
        );

    const contentWithDocumentEvidence =
      documentEvidenceBlocks.length > 0
        ? `${baseContent}\n\n${documentEvidenceBlocks.join("\n\n")}`
        : baseContent;

    return {
      ...m,
      imageUrl:
        m.imageUrl && !hasOptimizedPayloadPlaceholder(m.imageUrl) && allowFullVisual && m.imageUrl.length <= remainingBase64Budget.value
          ? (() => {
              remainingBase64Budget.value -= m.imageUrl?.length || 0;
              return m.imageUrl;
            })()
          : undefined,
      imageAsset: stripGeneratedImageAssetForModelPayload(m.imageAsset, allowFullVisual, remainingBase64Budget),
      imageAssets: m.imageAssets
        ?.map((asset) => stripGeneratedImageAssetForModelPayload(asset, allowFullVisual, remainingBase64Budget))
        .filter(Boolean) as GeneratedImageAsset[] | undefined,
      content: contentWithDocumentEvidence,
      attachments: attachmentPayloads,
    };
  });
};

type AccuracyMode = "strict" | "balanced" | "creative";
type OutputLength = "tight" | "short" | "normal" | "long" | "full";
type LegacyResponseLength = "quick" | "normal" | "detailed";
type JobSensitivity = "routine" | "important" | "critical";
type UiTheme = "rodgerspc" | "light" | "dark";
type LeftPanelView = "drive" | "chats";

type SavedChatFolder = {
  id: string;
  name: string;
  mode?: AppMode;
  builtIn?: boolean;
  createdAt: string;
};

type SavedChatRecord = {
  id: string;
  title: string;
  folderId: string;
  mode: AppMode;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
};

const SAVED_CHAT_STORAGE_VERSION = 1;

const createSavedChatFolder = (
  id: string,
  name: string,
  mode?: AppMode,
  builtIn = false,
): SavedChatFolder => ({
  id,
  name,
  mode,
  builtIn,
  createdAt: "built-in",
});

const DEFAULT_SAVED_CHAT_FOLDERS: SavedChatFolder[] = [
  createSavedChatFolder("mode_tech_assistant", "Bench Tech", AppMode.TECH_ASSISTANT, true),
  createSavedChatFolder("mode_blog_engine", "Blog Engine", AppMode.BLOG_ENGINE, true),
  createSavedChatFolder("mode_personal_vault", "Personal Vault", AppMode.PERSONAL_VAULT, true),
  createSavedChatFolder("mode_inventory_scout", "Inventory Scout", AppMode.INVENTORY_SCOUT, true),
  createSavedChatFolder("mode_lead_responder", "Lead Responder", AppMode.LEAD_RESPONDER, true),
  createSavedChatFolder("mode_seo_listings", "SEO & Listings", AppMode.SEO_LISTINGS, true),
  createSavedChatFolder("mode_web_creative", "Web Creative", AppMode.WEB_CREATIVE, true),
  createSavedChatFolder("mode_front_desk", "Front Desk", AppMode.FRONT_DESK, true),
];

const getSavedChatStorageKey = (uid: string) =>
  `rodgerspc_command_center_saved_chats_v${SAVED_CHAT_STORAGE_VERSION}_${uid}`;

const sanitizeSavedChatTitle = (value: unknown, fallback = "Untitled Chat"): string => {
  const cleaned = String(value || "")
    .replace(/[#*_`>\[\](){}]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return fallback;
  return cleaned.length > 70 ? `${cleaned.slice(0, 70).trim()}…` : cleaned;
};

const stripAutomaticReportBlocksFromAssistantText = (value: unknown): string => {
  const text = String(value || "");
  if (!text.trim()) return text;

  const reportStartPatterns = [
    /^\s*\[Report\s+\d+\][^\n]*$/im,
    /^\s*Generated\s+Report\s*:?\s*$/im,
    /^\s*Job\s+Log\s*:?\s*$/im,
    /^\s*Report\s+Artifact\s*:?\s*$/im,
  ];

  const starts = reportStartPatterns
    .map((pattern) => {
      const match = text.match(pattern);
      return match?.index ?? -1;
    })
    .filter((index) => index >= 0);

  if (starts.length === 0) return text;

  const cutAt = Math.min(...starts);
  const kept = text.slice(0, cutAt).trimEnd();
  return kept || text;
};

const normalizeUiTheme = (value: unknown, fallback: UiTheme = "rodgerspc"): UiTheme => {
  const raw = String(value || "").toLowerCase();
  if (raw === "rodgerspc" || raw === "light" || raw === "dark") return raw;
  return fallback;
};


const OUTPUT_LENGTH_LEVELS: {
  value: OutputLength;
  label: string;
  helper: string;
}[] = [
  {
    value: "tight",
    label: "Tight",
    helper: "Bare minimum answer. Best when the mode is talking too much.",
  },
  {
    value: "short",
    label: "Short",
    helper: "Quick shop answer with the useful point first.",
  },
  {
    value: "normal",
    label: "Normal",
    helper: "Balanced RodgersPC answer. Enough detail to act.",
  },
  {
    value: "long",
    label: "Long",
    helper: "More explanation without changing accuracy or focus.",
  },
  {
    value: "full",
    label: "Full",
    helper: "Full structured answer for complex work.",
  },
];

const normalizeOutputLength = (value: unknown): OutputLength => {
  switch (String(value || "").toLowerCase()) {
    case "tight":
    case "minimum":
      return "tight";
    case "quick":
    case "short":
      return "short";
    case "normal":
    case "medium":
      return "normal";
    case "long":
      return "long";
    case "detailed":
    case "high":
    case "full":
      return "full";
    default:
      return "short";
  }
};

const outputLengthToLegacy = (value: OutputLength): LegacyResponseLength => {
  if (value === "normal") return "normal";
  if (value === "long" || value === "full") return "detailed";
  return "quick";
};

const outputLengthToVerbosity = (value: OutputLength): "minimum" | "medium" | "high" => {
  if (value === "normal") return "medium";
  if (value === "long" || value === "full") return "high";
  return "minimum";
};

const ALLOWED_EMAILS = [
  "joshua@rodgerspcservices.com",
  "admin@rodgerspcservices.com",
  "support@rodgerspcservices.com",
];

function SavedChatsPanel({
  folders,
  chats,
  activeChatId,
  globalTheme,
  leftPanelView,
  onLeftPanelViewChange,
  onOpenChat,
  onRenameChat,
  onMoveChat,
  onDeleteChat,
  onCreateFolder,
  onDeleteFolder,
}: {
  folders: SavedChatFolder[];
  chats: SavedChatRecord[];
  activeChatId: string | null;
  globalTheme: UiTheme;
  leftPanelView: "drive" | "chats";
  onLeftPanelViewChange: (view: "drive" | "chats") => void;
  onOpenChat: (chatId: string) => void;
  onRenameChat: (chatId: string, title: string) => void;
  onMoveChat: (chatId: string, folderId: string) => void;
  onDeleteChat: (chatId: string) => void;
  onCreateFolder: (folderName: string) => SavedChatFolder | null;
  onDeleteFolder: (folderId: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});
  const [newFolderName, setNewFolderName] = useState("");
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [movingChatId, setMovingChatId] = useState<string | null>(null);

  const isLight = globalTheme === "light";
  const filteredChats = chats.filter((chat) => {
    const needle = `${chat.title} ${folders.find((folder) => folder.id === chat.folderId)?.name || ""}`.toLowerCase();
    return needle.includes(search.trim().toLowerCase());
  });

  const visibleFolders = folders.filter((folder) => {
    if (!search.trim()) return true;
    return filteredChats.some((chat) => chat.folderId === folder.id) || folder.name.toLowerCase().includes(search.trim().toLowerCase());
  });

  const sortedChatsForFolder = (folderId: string) =>
    filteredChats
      .filter((chat) => chat.folderId === folderId)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const toggleFolder = (folderId: string) => {
    setExpandedFolders((prev) => ({ ...prev, [folderId]: prev[folderId] === false }));
  };

  const addFolder = () => {
    const created = onCreateFolder(newFolderName);
    if (created) {
      setExpandedFolders((prev) => ({ ...prev, [created.id]: true }));
      setNewFolderName("");
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col" id="saved-chats-panel">
      <div className="shrink-0 px-3 py-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className={`text-[8.5px] font-black uppercase tracking-[0.14em] leading-none ${isLight ? "text-[#143F8F]" : "text-[#F6A400]"}`}>
              Chats
            </div>
            <div className={`mt-1 text-[7px] leading-none ${isLight ? "text-slate-500" : "text-white/38"}`}>
              Manual saved chat memory
            </div>
          </div>
          <div
            className="flex shrink-0 origin-right items-center gap-[1px] opacity-38 transition hover:opacity-80"
            style={{ transform: "scale(0.42)", transformOrigin: "right center" }}
          >
            <button
              type="button"
              onClick={() => onLeftPanelViewChange("drive")}
              className={`text-[7px] font-medium uppercase tracking-[0.08em] leading-none transition ${
                leftPanelView === "drive"
                  ? isLight ? "text-[#143F8F]" : "text-[#F6A400]"
                  : isLight ? "text-slate-400 hover:text-slate-700" : "text-white/32 hover:text-white/62"
              }`}
              title="Show Drive tree"
            >
              Drive
            </button>
            <span className={isLight ? "text-slate-300 text-[6px]" : "text-white/10 text-[6px]"}>/</span>
            <button
              type="button"
              onClick={() => onLeftPanelViewChange("chats")}
              className={`text-[7px] font-medium uppercase tracking-[0.08em] leading-none transition ${
                leftPanelView === "chats"
                  ? isLight ? "text-[#143F8F]" : "text-[#F6A400]"
                  : isLight ? "text-slate-400 hover:text-slate-700" : "text-white/32 hover:text-white/62"
              }`}
              title="Show saved chats"
            >
              Chats
            </button>
            <span className={`ml-[2px] inline-flex h-[10px] min-w-[10px] items-center justify-center rounded-full border px-[2px] text-[6px] font-medium ${isLight ? "border-slate-200 bg-white/55 text-slate-400" : "border-white/10 bg-white/[0.025] text-white/32"}`}>
              {chats.length}
            </span>
          </div>
        </div>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search saved chats..."
          className={`mt-2 h-[21px] w-full rounded-[6px] border px-2 text-[8.25px] leading-none outline-none transition ${
            isLight
              ? "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
              : "border-white/10 bg-black/18 text-white placeholder:text-white/35"
          }`}
        />

        <div className="mt-1 flex gap-1">
          <input
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") addFolder();
            }}
            placeholder="New folder"
            className={`h-[20px] min-w-0 flex-1 rounded-[6px] border px-2 text-[8.25px] leading-none outline-none ${
              isLight
                ? "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
                : "border-white/10 bg-black/18 text-white placeholder:text-white/28"
            }`}
          />
          <button
            type="button"
            onClick={addFolder}
            className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border text-[8px] font-medium leading-none opacity-42 transition hover:opacity-82 ${
              isLight
                ? "border-slate-200 bg-white text-slate-500 hover:border-[#143F8F]/25 hover:text-[#143F8F]"
                : "border-white/10 bg-white/[0.035] text-white/42 hover:border-[#F6A400]/25 hover:text-[#F6A400]"
            }`}
            title="Create folder"
            aria-label="Create folder"
          >
            +
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-3 pb-4">
        {visibleFolders.map((folder) => {
          const folderChats = sortedChatsForFolder(folder.id);
          const expanded = expandedFolders[folder.id] !== false;

          if (search.trim() && folderChats.length === 0 && !folder.name.toLowerCase().includes(search.trim().toLowerCase())) {
            return null;
          }

          return (
            <div
              key={folder.id}
              className={`rounded-xl border ${
                isLight
                  ? "border-slate-200 bg-white/72 shadow-sm"
                  : "border-white/10 bg-white/[0.035]"
              }`}
            >
              <button
                type="button"
                onClick={() => toggleFolder(folder.id)}
                className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left"
              >
                <span className="min-w-0 truncate text-[9px] font-semibold leading-none">{folder.name}</span>
                <span className="flex items-center gap-1">
                  <span className={`rounded-full px-1 py-0.5 text-[6.5px] font-semibold leading-none ${isLight ? "bg-slate-100 text-slate-500" : "bg-black/20 text-white/38"}`}>
                    {folderChats.length}
                  </span>
                  <ChevronDown className={`h-2.5 w-2.5 transition ${expanded ? "rotate-180" : ""}`} />
                </span>
              </button>

              {expanded && (
                <div className="space-y-1 px-1.5 pb-1.5">
                  {folderChats.length === 0 ? (
                    <div className={`rounded-lg px-1.5 py-1.5 text-[8px] ${isLight ? "text-slate-400" : "text-white/32"}`}>
                      No saved chats.
                    </div>
                  ) : (
                    folderChats.map((chat) => {
                      const isActive = chat.id === activeChatId;
                      const isEditing = editingChatId === chat.id;
                      const isMoving = movingChatId === chat.id;

                      return (
                        <div
                          key={chat.id}
                          className={`rounded-lg border p-1.5 ${
                            isActive
                              ? isLight
                                ? "border-[#143F8F]/28 bg-[#143F8F]/6"
                                : "border-[#F6A400]/26 bg-[#F6A400]/8"
                              : isLight
                                ? "border-slate-100 bg-slate-50/70"
                                : "border-white/[0.06] bg-black/12"
                          }`}
                        >
                          {isEditing ? (
                            <div className="space-y-1.5">
                              <input
                                value={editingTitle}
                                onChange={(e) => setEditingTitle(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    onRenameChat(chat.id, editingTitle);
                                    setEditingChatId(null);
                                  }
                                }}
                                className={`h-5 w-full rounded-md border px-1.5 text-[8px] outline-none ${
                                  isLight
                                    ? "border-slate-200 bg-white text-slate-900"
                                    : "border-white/10 bg-black/22 text-white"
                                }`}
                              />
                              <div className="flex gap-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    onRenameChat(chat.id, editingTitle);
                                    setEditingChatId(null);
                                  }}
                                  className="rounded-md bg-emerald-600 px-1.5 py-0.5 text-[7.5px] font-semibold text-white"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingChatId(null)}
                                  className={`rounded-md px-1.5 py-0.5 text-[7.5px] font-semibold ${isLight ? "bg-slate-100 text-slate-500" : "bg-white/[0.06] text-white/55"}`}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => onOpenChat(chat.id)}
                                className="block w-full text-left"
                              >
                                <div className="line-clamp-2 text-[8.5px] font-semibold leading-3">
                                  {chat.title}
                                </div>
                                <div className={`mt-1 text-[7px] leading-none ${isLight ? "text-slate-400" : "text-white/30"}`}>
                                  {new Date(chat.updatedAt).toLocaleString()}
                                </div>
                              </button>

                              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingChatId(chat.id);
                                    setEditingTitle(chat.title);
                                  }}
                                  className={`rounded-md border px-1 py-0.5 text-[7px] font-semibold ${isLight ? "border-slate-200 bg-white text-slate-500" : "border-white/10 bg-white/[0.04] text-white/40"}`}
                                  title="Rename chat"
                                >
                                  <Edit3 className="h-2.5 w-2.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setMovingChatId(isMoving ? null : chat.id)}
                                  className={`rounded-md border px-1 py-0.5 text-[7px] font-semibold ${isLight ? "border-slate-200 bg-white text-slate-500" : "border-white/10 bg-white/[0.04] text-white/40"}`}
                                  title="Move chat"
                                >
                                  <MoveRight className="h-2.5 w-2.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => onDeleteChat(chat.id)}
                                  className={`rounded-md border px-1.5 py-1 text-[8.5px] font-semibold ${isLight ? "border-rose-200 bg-white text-rose-500" : "border-rose-500/20 bg-rose-500/8 text-rose-300"}`}
                                  title="Delete saved chat"
                                >
                                  <Trash2 className="h-2.5 w-2.5" />
                                </button>
                              </div>

                              {isMoving && (
                                <div className="mt-2 grid grid-cols-1 gap-1">
                                  {folders.map((targetFolder) => (
                                    <button
                                      key={`${chat.id}-move-${targetFolder.id}`}
                                      type="button"
                                      onClick={() => {
                                        onMoveChat(chat.id, targetFolder.id);
                                        setMovingChatId(null);
                                      }}
                                      className={`rounded-md px-1.5 py-0.5 text-left text-[7.5px] ${
                                        targetFolder.id === chat.folderId
                                          ? isLight
                                            ? "bg-[#143F8F] text-white"
                                            : "bg-[#F6A400]/16 text-[#F6A400]"
                                          : isLight
                                            ? "bg-white text-slate-500 hover:text-slate-900"
                                            : "bg-white/[0.035] text-white/45 hover:text-white"
                                      }`}
                                    >
                                      {targetFolder.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })
                  )}

                  {!folder.builtIn && folderChats.length === 0 && (
                    <button
                      type="button"
                      onClick={() => onDeleteFolder(folder.id)}
                      className={`ml-1 mt-1 text-[7px] font-semibold uppercase tracking-[0.04em] ${isLight ? "text-rose-500" : "text-rose-300/62"}`}
                    >
                      Delete folder
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [guestModeActive, setGuestModeActive] = useState(() =>
    getCustomerBenchDeviceMode(),
  );
  const [token, setToken] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [driveNotice, setDriveNotice] = useState<string | null>(null);
  const [items, setItems] = useState<WorkspaceItem[]>([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentMode, setCurrentMode] = useState<AppMode>(
    AppMode.TECH_ASSISTANT,
  );
  const [loading, setLoading] = useState(false);
  const [requestProgressSteps, setRequestProgressSteps] = useState<string[]>([]);
  const [isExporterOpen, setIsExporterOpen] = useState(false);

  // Theme Core Hooks
  const [globalTheme, setGlobalTheme] = useState<UiTheme>("rodgerspc");
  const [chatTheme, setChatTheme] = useState<UiTheme>("rodgerspc");
  const [inputTheme, setInputTheme] = useState<UiTheme>("rodgerspc");

  // 4 Real Console Control Toggles
  const [driveSyncEnabled, setDriveSyncEnabled] = useState(true);
  // Phase 19: this is now only the old UI preference value. Server-side WebRouter decides actual public-web use per request.
  const [searchGroundingEnabled, setSearchGroundingEnabled] = useState(false);
  const [imageGenerationEnabled, setImageGenerationEnabled] = useState(false);
  const [fastModeEnabled, setFastModeEnabled] = useState(false);

  // Bench AI Parameters & Layout Control Toggles
  const [driveSidebarVisible, setDriveSidebarVisible] = useState(true);
  const [bottomPanelVisible, setBottomPanelVisible] = useState(true);
  const [aiTemperature, setAiTemperature] = useState<number>(0.02);
  const [aiBehavior, setAiBehavior] = useState<AccuracyMode>("strict");
  const [aiVerbosity, setAiVerbosity] = useState<"minimum" | "medium" | "high">(
    "minimum",
  );

  // Plain English controls
  const [accuracyMode, setAccuracyMode] = useState<AccuracyMode>("strict");
  const [responseLength, setResponseLength] = useState<OutputLength>("short");
  const [outputLengthPanelVisible, setOutputLengthPanelVisible] = useState(false);
  const [jobSensitivity, setJobSensitivity] = useState<JobSensitivity>("important");
  const [aiPreset, setAiPreset] = useState<string>("bench_job");

  const [miniInput, setMiniInput] = useState("");
  const [capsuleImage, setCapsuleImage] = useState<string | null>(null);
  const [capsuleAttachedFileName, setCapsuleAttachedFileName] = useState<
    string | null
  >(null);
  const [capsuleAttachment, setCapsuleAttachment] =
    useState<AttachedFile | null>(null);
  const [showCapOverlay, setShowCapOverlay] = useState(false);
  const capOverlayRef = useRef<HTMLDivElement>(null);
  const outputLengthPopoverRef = useRef<HTMLDivElement>(null);
  const miniTextareaRef = useRef<HTMLTextAreaElement>(null);

  const tokenRef = useRef<string | null>(null);
  const userRef = useRef<User | null>(null);
  const driveSyncEnabledRef = useRef<boolean>(true);
  const guestModeActiveRef = useRef<boolean>(guestModeActive);
  const activeChatAbortControllerRef = useRef<AbortController | null>(null);
  const activeChatRequestIdRef = useRef<string | null>(null);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    driveSyncEnabledRef.current = driveSyncEnabled;
  }, [driveSyncEnabled]);

  useEffect(() => {
    guestModeActiveRef.current = guestModeActive;
  }, [guestModeActive]);

  const responseLengthIndex = Math.max(
    0,
    OUTPUT_LENGTH_LEVELS.findIndex((option) => option.value === responseLength),
  );
  const activeResponseLength =
    OUTPUT_LENGTH_LEVELS[responseLengthIndex] || OUTPUT_LENGTH_LEVELS[1];

  // Click-off outside auto-close & Escape for floating composer cap overlay
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        capOverlayRef.current &&
        !capOverlayRef.current.contains(event.target as Node)
      ) {
        setShowCapOverlay(false);
      }
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setShowCapOverlay(false);
      }
    }

    if (showCapOverlay) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showCapOverlay]);

  // Output length popover is a floating control. It must not reserve permanent vertical UI space.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        outputLengthPopoverRef.current &&
        !outputLengthPopoverRef.current.contains(event.target as Node)
      ) {
        setOutputLengthPanelVisible(false);
      }
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOutputLengthPanelVisible(false);
      }
    }

    if (outputLengthPanelVisible) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [outputLengthPanelVisible]);

  const handleSelectPreset = (presetName: string) => {
    setAiPreset(presetName);
    if (presetName === "bench_job") {
      setAccuracyMode("strict");
      setResponseLength("short");
      setJobSensitivity("important");
      setAiTemperature(0.02);
    } else if (presetName === "front_counter") {
      setAccuracyMode("balanced");
      setResponseLength("normal");
      setJobSensitivity("critical");
      setAiTemperature(0.2);
    } else if (presetName === "creative_seo") {
      setAccuracyMode("creative");
      setResponseLength("full");
      setJobSensitivity("important");
      setAiTemperature(0.55);
    }
  };

  const handleCustomerBenchModeChange = (isEnabled: boolean) => {
    setGuestModeActive(isEnabled);
    setCustomerBenchDeviceMode(isEnabled);
  };

  const getModeDisplayName = (modeToLabel: AppMode): string => {
    switch (modeToLabel) {
      case AppMode.TECH_ASSISTANT:
        return "Bench Tech";
      case AppMode.BLOG_ENGINE:
        return "Blog Engine";
      case AppMode.PERSONAL_VAULT:
        return "Personal Vault";
      case AppMode.INVENTORY_SCOUT:
        return "Inventory Scout";
      case AppMode.LEAD_RESPONDER:
        return "Lead Responder";
      case AppMode.SEO_LISTINGS:
        return "SEO & Listings";
      case AppMode.WEB_CREATIVE:
        return "Web Creative";
      case AppMode.FRONT_DESK:
        return "Front Desk";
      default:
        return String(modeToLabel);
    }
  };

  const getDefaultSavedChatFolderId = (modeToUse: AppMode = currentMode): string => {
    const found = DEFAULT_SAVED_CHAT_FOLDERS.find((folder) => folder.mode === modeToUse);
    return found?.id || DEFAULT_SAVED_CHAT_FOLDERS[0].id;
  };

  const getSavedChatFolderName = (folderId: string): string => {
    return savedChatFolders.find((folder) => folder.id === folderId)?.name || "Saved Chats";
  };

  const suggestSavedChatTitle = (sourceMessages: ChatMessage[] = messages): string => {
    const firstUsefulUserMessage = sourceMessages.find(
      (message) => message.role === "user" && String(message.content || "").trim(),
    );

    if (!firstUsefulUserMessage) {
      return `${getModeDisplayName(currentMode)} Chat`;
    }

    const raw = String(firstUsefulUserMessage.content || "")
      .replace(/Customer\s+/i, "")
      .replace(/brought\s+in\s+/i, "")
      .replace(/\s+/g, " ")
      .trim();

    return sanitizeSavedChatTitle(raw, `${getModeDisplayName(currentMode)} Chat`);
  };

  const createSavedChatFolderIfNeeded = (folderName: string): SavedChatFolder | null => {
    const name = sanitizeSavedChatTitle(folderName, "");
    if (!name) return null;

    const existing = savedChatFolders.find(
      (folder) => folder.name.toLowerCase() === name.toLowerCase(),
    );
    if (existing) return existing;

    const nextFolder: SavedChatFolder = {
      id: `folder_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name,
      createdAt: new Date().toISOString(),
    };

    setSavedChatFolders((prev) => [...prev, nextFolder]);
    return nextFolder;
  };

  const buildSavedChatArchiveState = (chatMessages: ChatMessage[], modeToSave: AppMode) => ({
    userId: user?.uid || "",
    email: user?.email || "",
    globalTheme,
    chatTheme,
    inputTheme,
    currentMode: modeToSave,
    driveSyncEnabled,
    searchGroundingEnabled,
    imageGenerationEnabled,
    fastModeEnabled,
    driveSidebarVisible,
    bottomPanelVisible,
    aiTemperature,
    aiBehavior,
    aiVerbosity,
    accuracyMode,
    responseLength,
    jobSensitivity,
    aiPreset,
    messages: chatMessages,
    items,
    isCleared: false,
    activeChatId: user ? `${user.uid}-active` : `local-${Date.now()}`,
  });

  const handleSaveCurrentChatToFolder = (folderId: string, title?: string) => {
    if (messages.length === 0) {
      setDriveNotice("### Nothing to save\n\nStart a chat before saving it into Chats memory.");
      return;
    }

    if (!user?.uid) {
      setDriveNotice("### Chat not saved\n\nSign in before saving this chat into durable Chats memory.");
      return;
    }

    const now = new Date().toISOString();
    const finalFolderId = savedChatFolders.some((folder) => folder.id === folderId)
      ? folderId
      : getDefaultSavedChatFolderId(currentMode);
    const finalTitle = sanitizeSavedChatTitle(title || suggestSavedChatTitle(), suggestSavedChatTitle());
    const chatMemoryMessages = messages.map(sanitizeRestoredMessageMedia);

    void persistMessageMediaToLocalVault(chatMemoryMessages);

    const optimisticChatId = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const optimisticChat: SavedChatRecord = {
      id: optimisticChatId,
      title: finalTitle,
      folderId: finalFolderId,
      mode: currentMode,
      messages: chatMemoryMessages,
      createdAt: now,
      updatedAt: now,
    };

    // Save Chat always creates a new saved item. Updating an existing saved chat must be an explicit action.
    setSavedChats((prev) => [optimisticChat, ...prev]);
    setActiveSavedChatId(optimisticChat.id);
    setLeftPanelView("chats");
    setDriveSidebarVisible(true);
    setDriveNotice(`### Saving chat\n\nSaving to ${getSavedChatFolderName(finalFolderId)} as: ${finalTitle}`);

    void saveChatArchive(
      user.uid,
      buildSavedChatArchiveState(chatMemoryMessages, currentMode),
      finalTitle,
      { chatId: optimisticChatId, folderId: finalFolderId, createdAtIso: now },
    )
      .then((cloudChatId) => {
        if (!cloudChatId) {
          throw new Error("Firestore did not return a saved chat id.");
        }

        setSavedChats((prev) =>
          prev.map((chat) =>
            chat.id === optimisticChatId
              ? { ...chat, id: cloudChatId, updatedAt: new Date().toISOString() }
              : chat,
          ),
        );
        setActiveSavedChatId(cloudChatId);
        setDriveNotice(`### Chat saved\n\nSaved to ${getSavedChatFolderName(finalFolderId)} as: ${finalTitle}`);
      })
      .catch((err) => {
        console.error("Cloud saved chat write failed:", err);
        setDriveNotice(`### Chat save failed\n\nThe chat was not confirmed in durable cloud memory. Try saving again.\n\nError: ${err?.message || err}`);
      });
  };

  const handleCreateSavedChatFolder = (folderName: string) => {
    return createSavedChatFolderIfNeeded(folderName);
  };

  const handleOpenSavedChat = async (chatId: string) => {
    let chat = savedChats.find((item) => item.id === chatId) || null;

    if (user?.uid) {
      const cloudChat = await loadChatArchive(user.uid, chatId).catch(() => null);
      if (cloudChat) {
        const archiveMessages = Array.isArray(cloudChat.messages)
          ? await hydrateMessagesFromLocalMediaVault(cloudChat.messages.map(sanitizeRestoredMessageMedia))
          : [];
        chat = {
          id: cloudChat.id || chatId,
          title: cloudChat.title || chat?.title || "RodgersPC Chat",
          folderId: (cloudChat as any).folderId || chat?.folderId || getDefaultSavedChatFolderId((cloudChat.mode || AppMode.TECH_ASSISTANT) as AppMode),
          mode: (cloudChat.mode || cloudChat.currentMode || chat?.mode || AppMode.TECH_ASSISTANT) as AppMode,
          messages: archiveMessages,
          createdAt: cloudChat.createdAtIso || chat?.createdAt || new Date().toISOString(),
          updatedAt: cloudChat.updatedAtIso || chat?.updatedAt || new Date().toISOString(),
        };
        setSavedChats((prev) => prev.some((item) => item.id === chatId) ? prev.map((item) => item.id === chatId ? chat as SavedChatRecord : item) : [chat as SavedChatRecord, ...prev]);
      }
    }

    if (!chat) return;

    handleCancelRequest();
    const restoredMessages = chat.messages.map(sanitizeRestoredMessageMedia);
    setMessages(restoredMessages);
    void hydrateMessagesFromLocalMediaVault(restoredMessages).then((hydratedMessages) => {
      setMessages(hydratedMessages);
    });
    setCurrentMode(chat.mode || AppMode.TECH_ASSISTANT);
    applyModeDefaultSettings(chat.mode || AppMode.TECH_ASSISTANT);
    setActiveSavedChatId(chat.id);
    setLeftPanelView("chats");
  };

  const handleRenameSavedChat = (chatId: string, nextTitle: string) => {
    const title = sanitizeSavedChatTitle(nextTitle, "Untitled Chat");
    const target = savedChats.find((chat) => chat.id === chatId);

    setSavedChats((prev) =>
      prev.map((chat) =>
        chat.id === chatId
          ? { ...chat, title, updatedAt: new Date().toISOString() }
          : chat,
      ),
    );

    if (user?.uid && target) {
      void saveChatArchive(
        user.uid,
        buildSavedChatArchiveState(target.messages || [], target.mode || currentMode),
        title,
        {
          chatId,
          folderId: target.folderId,
          createdAtIso: target.createdAt,
        },
      ).catch((err) => console.error("Saved chat rename cloud write failed:", err));
    }
  };

  const handleMoveSavedChat = (chatId: string, folderId: string) => {
    if (!savedChatFolders.some((folder) => folder.id === folderId)) return;

    const target = savedChats.find((chat) => chat.id === chatId);
    setSavedChats((prev) =>
      prev.map((chat) =>
        chat.id === chatId
          ? { ...chat, folderId, updatedAt: new Date().toISOString() }
          : chat,
      ),
    );

    if (user?.uid && target) {
      void saveChatArchive(
        user.uid,
        buildSavedChatArchiveState(target.messages || [], target.mode || currentMode),
        target.title,
        {
          chatId,
          folderId,
          createdAtIso: target.createdAt,
        },
      ).catch((err) => console.error("Saved chat move cloud write failed:", err));
    }
  };

  const handleDeleteSavedChat = (chatId: string) => {
    setSavedChats((prev) => prev.filter((chat) => chat.id !== chatId));
    if (activeSavedChatId === chatId) {
      setActiveSavedChatId(null);
    }

    if (user?.uid) {
      void deleteChatArchive(user.uid, chatId).catch((err) => console.error("Saved chat delete cloud write failed:", err));
    }
  };

  const handleDeleteSavedChatFolder = (folderId: string) => {
    const folder = savedChatFolders.find((item) => item.id === folderId);
    if (!folder || folder.builtIn) return;

    const fallbackFolderId = getDefaultSavedChatFolderId(currentMode);
    setSavedChats((prev) =>
      prev.map((chat) =>
        chat.folderId === folderId
          ? { ...chat, folderId: fallbackFolderId, updatedAt: new Date().toISOString() }
          : chat,
      ),
    );
    setSavedChatFolders((prev) => prev.filter((item) => item.id !== folderId));
  };

  const handleCreateNotes = () => {
    if (messages.length === 0) {
      setDriveNotice("### No notes created\n\nThere is no active chat context to compile into Notes.");
      return;
    }

    const notesPrompt = [
      "RODGERSPC MANUAL NOTES REQUEST",
      "Create concise internal Notes from this active chat only.",
      "Use the label Notes, not Generated Report, Job Log, Report Artifact, or Customer Report.",
      "This is for Joshua to print or copy when a job is being handed off, set aside, waiting on parts, waiting on customer, or going to Kyle for board-level work.",
      "Do not add customer-facing wording unless the chat specifically asks for it.",
      "Keep it practical and short: Customer, Device, Issue, Tested, Findings, Current suspicion, Next step, Do not repeat / warnings, Parts or tools needed.",
    ].join("\n");

    handleSendMessage(notesPrompt);
  };

  const refocusMiniInput = () => {
    window.setTimeout(() => {
      miniTextareaRef.current?.focus({ preventScroll: true });
    }, 0);
  };

  const handleAccuracyModeChange = (val: AccuracyMode) => {
    setAccuracyMode(val);
    setAiPreset("custom");
    updateTempDynamically(val, jobSensitivity);
  };

  const handleResponseLengthChange = (val: OutputLength | LegacyResponseLength) => {
    setResponseLength(normalizeOutputLength(val));
    setAiPreset("custom");
  };

  const handleOutputLengthSliderChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const nextIndex = Number(event.target.value);
    const nextValue = OUTPUT_LENGTH_LEVELS[nextIndex]?.value || "normal";
    setResponseLength(nextValue);
    setAiPreset("custom");
  };

  const handleJobSensitivityChange = (val: JobSensitivity) => {
    setJobSensitivity(val);
    setAiPreset("custom");
    updateTempDynamically(accuracyMode, val);
  };

  const updateTempDynamically = (accuracy: string, sensitivity: string) => {
    if (accuracy === "strict") {
      if (sensitivity === "critical") setAiTemperature(0.1);
      else if (sensitivity === "important") setAiTemperature(0.02);
      else setAiTemperature(0.2);
    } else if (accuracy === "balanced") {
      if (sensitivity === "critical") setAiTemperature(0.2);
      else if (sensitivity === "important") setAiTemperature(0.25);
      else setAiTemperature(0.3);
    } else if (accuracy === "creative") {
      if (sensitivity === "critical") setAiTemperature(0.5);
      else if (sensitivity === "important") setAiTemperature(0.55);
      else setAiTemperature(0.6);
    }
  };

  const applyModeDefaultSettings = (modeToApply: AppMode) => {
    /**
     * Mode defaults are intentional starting points only.
     * Joshua can still manually adjust Accuracy / Length / Sensitivity after switching modes.
     * Truth rules remain enforced server-side regardless of preset.
     */
    switch (modeToApply) {
      case AppMode.TECH_ASSISTANT:
        setAiPreset("bench_job");
        setAccuracyMode("strict");
        setResponseLength("short");
        setJobSensitivity("important");
        setAiBehavior("strict");
        setAiVerbosity("minimum");
        setAiTemperature(0.02);
        break;

      case AppMode.INVENTORY_SCOUT:
        setAiPreset("bench_job");
        setAccuracyMode("strict");
        setResponseLength("normal");
        setJobSensitivity("important");
        setAiBehavior("strict");
        setAiVerbosity("medium");
        setAiTemperature(0.02);
        break;

      case AppMode.PERSONAL_VAULT:
        setAiPreset("bench_job");
        setAccuracyMode("strict");
        setResponseLength("normal");
        setJobSensitivity("important");
        setAiBehavior("strict");
        setAiVerbosity("medium");
        setAiTemperature(0.02);
        break;

      case AppMode.BLOG_ENGINE:
        setAiPreset("creative_seo");
        setAccuracyMode("balanced");
        setResponseLength("full");
        setJobSensitivity("important");
        setAiBehavior("balanced");
        setAiVerbosity("high");
        setAiTemperature(0.35);
        // Blog Engine is a content worker. It needs prior blog memory, current web context, and image generation ready by default.
        setDriveSyncEnabled(true);
        driveSyncEnabledRef.current = true;
        setSearchGroundingEnabled(true);
        setImageGenerationEnabled(true);
        break;

      case AppMode.SEO_LISTINGS:
        setAiPreset("creative_seo");
        setAccuracyMode("balanced");
        setResponseLength("full");
        setJobSensitivity("important");
        setAiBehavior("balanced");
        setAiVerbosity("high");
        setAiTemperature(0.35);
        break;

      case AppMode.WEB_CREATIVE:
        setAiPreset("creative_seo");
        setAccuracyMode("creative");
        setResponseLength("normal");
        setJobSensitivity("important");
        setAiBehavior("creative");
        setAiVerbosity("medium");
        setAiTemperature(0.55);
        // Web Creative is the image/design worker. Keep Drive, Web, and Image ready when this mode opens.
        setDriveSyncEnabled(true);
        driveSyncEnabledRef.current = true;
        setSearchGroundingEnabled(true);
        setImageGenerationEnabled(true);
        break;

      case AppMode.FRONT_DESK:
        setAiPreset("front_counter");
        setAccuracyMode("balanced");
        setResponseLength("short");
        setJobSensitivity("critical");
        setAiBehavior("balanced");
        setAiVerbosity("minimum");
        setAiTemperature(0.2);
        break;

      case AppMode.LEAD_RESPONDER:
        setAiPreset("front_counter");
        setAccuracyMode("balanced");
        setResponseLength("short");
        setJobSensitivity("critical");
        setAiBehavior("balanced");
        setAiVerbosity("minimum");
        setAiTemperature(0.2);
        break;

      default:
        setAiPreset("bench_job");
        setAccuracyMode("strict");
        setResponseLength("short");
        setJobSensitivity("important");
        setAiBehavior("strict");
        setAiVerbosity("minimum");
        setAiTemperature(0.02);
        break;
    }
  };


  const addInternalStatusMessage = (content: string) => {
    const msg: ChatMessage = {
      id: `sys_${Date.now()}`,
      role: "assistant",
      content,
      timestamp: new Date().toLocaleTimeString(),
      commands: [],
      reports: [],
    };

    setMessages((prev) => [...prev, msg]);
  };

  const buildUploadDisplayName = (name: string): string => {
    const trimmed = (name || "").trim();

    if (trimmed) return trimmed;

    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const yyyy = now.getFullYear();

    return `RodgersPC Upload – ${mm}-${dd}-${yyyy}`;
  };

  const isVisionAttachmentFile = (file: File): boolean => {
    return isImageUploadFile(file) || isVideoUploadFile(file) || isPdfUploadFile(file);
  };

  const loadCapsuleAttachmentFromFile = async (
    file: File,
    label: "Uploaded" | "Pasted",
  ) => {
    try {
      const isImage = isImageUploadFile(file);
      const isVideo = isVideoUploadFile(file);
      const isPdf = isPdfUploadFile(file);
      const attachmentId = `att_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      let attachment: AttachedFile;
      let previewImage: string | null = null;

      if (isImage) {
        const dataUrl = await readFileAsDataUrlSafely(file);
        attachment = {
          id: attachmentId,
          name: file.name,
          type: "image",
          content: `[${label}: ${file.name}]`,
          base64: dataUrl,
          mimeType: file.type || undefined,
        };
        previewImage = dataUrl;
      } else if (isVideo) {
        const dataUrl = await readFileAsDataUrlSafely(file);
        attachment = {
          id: attachmentId,
          name: file.name,
          type: "video",
          content: `[${label} video clip: ${file.name}. Video payload included for model visual review. Use the actual video payload as evidence when answering.]`,
          base64: dataUrl,
          mimeType: file.type || "video/mp4",
          binaryPayloadKind: "video",
          evidenceStatus: "visual_payload_loaded",
          isRealPayload: true,
        };
      } else if (isPdf) {
        const dataUrl = await readFileAsDataUrlSafely(file);
        attachment = {
          id: attachmentId,
          name: file.name,
          type: "file",
          content: `[Uploaded PDF: ${file.name}. Binary PDF payload included for server/model document handling. Do not treat this placeholder as extracted text.]`,
          base64: dataUrl,
          mimeType: file.type || "application/pdf",
        };
      } else {
        const text = await readFileAsTextSafely(file);
        const shouldSendBinaryPayload = isBinaryOfficeUploadName(file) && looksLikeBinaryDocumentText(text);

        if (shouldSendBinaryPayload) {
          const dataUrl = await readFileAsDataUrlSafely(file);
          attachment = {
            id: attachmentId,
            name: file.name,
            type: "file",
            content: `[Uploaded binary document: ${file.name}. Binary payload included for server-side parsing. Do not treat this placeholder as extracted text.]`,
            base64: dataUrl,
            mimeType: file.type || undefined,
          };
        } else {
          attachment = {
            id: attachmentId,
            name: file.name,
            type: "file",
            content: text,
            base64: undefined,
            mimeType: file.type || undefined,
          };
        }
      }

      setCapsuleAttachment(attachment);
      setCapsuleAttachedFileName(file.name);
      setCapsuleImage(previewImage);
      setMiniInput(
        (prev) => prev + (prev ? " " : "") + `[${label}: ${file.name}]`,
      );
    } catch (err: any) {
      const message = `### Attachment failed\n\nCould not read ${file.name}. Try attaching it again.\n\nError: ${err?.message || err}`;
      setDriveNotice(message);
      addInternalStatusMessage(message);
    }
  };


  const normalizeDriveName = (name: string) =>
    name
      .toLowerCase()
      .replace(/[’‘]/g, "'")
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const isDriveVisionFile = (item: WorkspaceItem): boolean => {
    const name = (item.name || "").toLowerCase();
    const mime = (item.mimeType || "").toLowerCase();

    return (
      mime.startsWith("image/") ||
      mime.startsWith("video/") ||
      mime === "application/pdf" ||
      /\.(png|jpe?g|webp|gif|bmp|pdf|mp4|mov|m4v|webm|mpeg|mpg|avi)$/i.test(name)
    );
  };

  const getExplicitDriveRecordIntent = (prompt: string) => {
    const normalized = normalizeDriveName(prompt);
    const asksForDrive =
      normalized.includes("drive") ||
      normalized.includes("source of truth") ||
      normalized.includes("record") ||
      normalized.includes("file") ||
      normalized.includes("look up") ||
      normalized.includes("lookup") ||
      normalized.includes("loaded") ||
      normalized.includes("read");

    const asksForWarranty =
      normalized.includes("warranty") ||
      normalized.includes("service plan") ||
      normalized.includes("service plans") ||
      normalized.includes("customer responsibility") ||
      normalized.includes("pc sales warranty") ||
      normalized.includes("sales warranty") ||
      normalized.includes("paid add on") ||
      normalized.includes("paid add ons") ||
      normalized.includes("best value plan") ||
      normalized.includes("included warranty");

    const asksForVisual =
      normalized.includes("picture") ||
      normalized.includes("pictures") ||
      normalized.includes("screenshot") ||
      normalized.includes("screenshots") ||
      normalized.includes("image") ||
      normalized.includes("png") ||
      normalized.includes("visual") ||
      normalized.includes("what you can actually see") ||
      normalized.includes("what it shows") ||
      normalized.includes("visually inspect");

    const asksForPolicyOrPricing =
      normalized.includes("policy") ||
      normalized.includes("policies") ||
      normalized.includes("pricing") ||
      normalized.includes("price") ||
      normalized.includes("prices") ||
      normalized.includes("terms") ||
      normalized.includes("coverage") ||
      normalized.includes("add on") ||
      normalized.includes("add ons");

    return {
      asksForDrive,
      asksForWarranty,
      asksForVisual,
      asksForPolicyOrPricing,
      isExplicitDriveLookup:
        asksForDrive && (asksForWarranty || asksForPolicyOrPricing || asksForVisual),
    };
  };

  const scoreExplicitDriveRecordMatch = (
    file: WorkspaceItem,
    prompt: string,
  ): number => {
    const intent = getExplicitDriveRecordIntent(prompt);
    if (!intent.isExplicitDriveLookup) return 0;

    const haystack = normalizeDriveName(`${file.name || ""} ${file.path || ""}`);
    let score = 0;

    if (intent.asksForWarranty) {
      if (haystack.includes("warranty")) score += 260;
      if (haystack.includes("service plan")) score += 240;
      if (haystack.includes("service plans")) score += 240;
      if (haystack.includes("customer responsibility")) score += 220;
      if (haystack.includes("pc sales")) score += 180;
    }

    if (intent.asksForPolicyOrPricing) {
      if (haystack.includes("policy")) score += 120;
      if (haystack.includes("pricing")) score += 120;
      if (haystack.includes("price")) score += 90;
      if (haystack.includes("terms")) score += 90;
      if (haystack.includes("plan")) score += 80;
      if (haystack.includes("plans")) score += 80;
    }

    if (intent.asksForVisual && isDriveVisionFile(file)) {
      score += 180;
      if (haystack.includes("warranty") || haystack.includes("service plan")) {
        score += 180;
      }
    }

    if (file.sourceOfTruth) score += 120;
    if (haystack.includes("source") && haystack.includes("truth")) score += 60;

    return score;
  };

  const getModeDefaultDriveFolderNames = (modeToApply: AppMode): string[] => {
    switch (modeToApply) {
      case AppMode.TECH_ASSISTANT:
        return ["rodgerspc bench tech", "rodgerspc technician assistant", "rodgerspc tech assistant", "rodgerspc tech bench"];
      case AppMode.BLOG_ENGINE:
        return ["rodgerspc blog engine"];
      case AppMode.PERSONAL_VAULT:
        return ["joshua's personal vault", "joshuas personal vault"];
      case AppMode.INVENTORY_SCOUT:
        return ["rodgerspc inventory and vendor scout"];
      case AppMode.LEAD_RESPONDER:
        return ["rodgerspc lead and review responder"];
      case AppMode.SEO_LISTINGS:
        return ["rodgerspc seo and listings"];
      case AppMode.WEB_CREATIVE:
        return ["rodgerspc web and creative hub"];
      case AppMode.FRONT_DESK:
        return ["rodgerspc front desk and business"];
      default:
        return ["rodgerspc bench tech", "rodgerspc technician assistant", "rodgerspc tech assistant", "rodgerspc tech bench"];
    }
  };

  const getDrivePathSegments = (item: WorkspaceItem): string[] => {
    return (item.path || "")
      .split("/")
      .map((part) => part.trim())
      .filter(Boolean);
  };

  const isDirectChildOfCommandRoot = (item: WorkspaceItem): boolean => {
    // Expected shape: /RodgersPC Command Center/<file-or-folder>
    // Anything deeper is a nested/legacy/duplicate item and must not be auto-loaded.
    return getDrivePathSegments(item).length === 2;
  };

  const isCanonicalSourceOfTruthItem = (item: WorkspaceItem): boolean => {
    if (!isDirectChildOfCommandRoot(item)) return false;

    const normalizedName = normalizeDriveName(item.name || "");

    return (
      normalizedName.includes("rodgerspc") &&
      normalizedName.includes("source") &&
      normalizedName.includes("truth")
    );
  };

  const isLegacyDuplicateSourceScope = (item: WorkspaceItem): boolean => {
    const normalizedPath = normalizeDriveName(item.path || "");
    const normalizedName = normalizeDriveName(item.name || "");

    const looksLikeSourceTruth =
      (normalizedName.includes("source") && normalizedName.includes("truth")) ||
      (normalizedPath.includes("source") && normalizedPath.includes("truth")) ||
      (normalizedName.includes("identity") && normalizedName.includes("rodgerspc"));

    if (!looksLikeSourceTruth) return false;
    if (isCanonicalSourceOfTruthItem(item)) return false;

    return (
      normalizedPath.includes("gemini") ||
      normalizedPath.includes("gemini master folder") ||
      normalizedPath.includes("ashley s personal assistant") ||
      normalizedPath.includes("ashleys personal assistant") ||
      !isDirectChildOfCommandRoot(item)
    );
  };

  const isDefaultSourceOfTruthFolder = (item: WorkspaceItem): boolean => {
    return item.type === "folder" && isCanonicalSourceOfTruthItem(item);
  };

  const isDefaultSourceOfTruthFile = (item: WorkspaceItem): boolean => {
    return item.type === "file" && isCanonicalSourceOfTruthItem(item);
  };

  const isDefaultModeFolder = (item: WorkspaceItem, modeToApply: AppMode): boolean => {
    if (item.type !== "folder") return false;
    if (!isDirectChildOfCommandRoot(item)) return false;

    const normalizedName = normalizeDriveName(item.name || "");
    return getModeDefaultDriveFolderNames(modeToApply).some(
      (folderName) => normalizedName === normalizeDriveName(folderName),
    );
  };

  const isBrandCreativeMode = (modeToApply: AppMode): boolean => {
    return modeToApply === AppMode.BLOG_ENGINE || modeToApply === AppMode.WEB_CREATIVE;
  };

  const isRodgersPcBrandKitFolder = (item: WorkspaceItem): boolean => {
    if (item.type !== "folder") return false;

    const normalizedName = normalizeDriveName(item.name || "");
    const normalizedPath = normalizeDriveName(item.path || "");

    return (
      normalizedName.includes("rodgerspc brand kit") ||
      normalizedPath.includes("rodgerspc brand kit") ||
      (normalizedName.includes("brand kit") && normalizedPath.includes("rodgerspc"))
    );
  };

  const isDefaultBrandKitFolder = (
    item: WorkspaceItem,
    modeToApply: AppMode,
  ): boolean => {
    if (!isBrandCreativeMode(modeToApply)) return false;
    if (item.type !== "folder") return false;
    if (!isDirectChildOfCommandRoot(item)) return false;

    return isRodgersPcBrandKitFolder(item);
  };

  const isInsideRodgersPcBrandKit = (item: WorkspaceItem): boolean => {
    const normalizedPath = normalizeDriveName(`${item.path || ""} ${item.name || ""}`);
    return normalizedPath.includes("rodgerspc brand kit");
  };

  const isBrandKitRuleFile = (item: WorkspaceItem): boolean => {
    if (item.type !== "file") return false;

    const haystack = normalizeDriveName(`${item.path || ""} ${item.name || ""}`);

    return (
      isInsideRodgersPcBrandKit(item) &&
      (
        haystack.includes("brand rules") ||
        haystack.includes("blog formatting rules") ||
        haystack.includes("image prompt rules") ||
        haystack.includes("og image rules") ||
        haystack.includes("approved og examples instructions") ||
        haystack.includes("read me first") ||
        haystack.includes("asset manifest")
      )
    );
  };

  const isBrandKitVisualReferenceFile = (item: WorkspaceItem): boolean => {
    if (item.type !== "file") return false;
    if (!isInsideRodgersPcBrandKit(item)) return false;
    if (!isDriveVisionFile(item)) return false;

    const haystack = normalizeDriveName(`${item.path || ""} ${item.name || ""}`);

    return (
      haystack.includes("logo") ||
      haystack.includes("mascot") ||
      haystack.includes("coin") ||
      haystack.includes("website") ||
      haystack.includes("screen shot") ||
      haystack.includes("screenshot") ||
      haystack.includes("homepage") ||
      haystack.includes("about") ||
      haystack.includes("services") ||
      haystack.includes("footer") ||
      haystack.includes("og example") ||
      haystack.includes("approved")
    );
  };

  const scoreBrandKitFileForCreativeWork = (
    item: WorkspaceItem,
    wantsImage: boolean,
  ): number => {
    const haystack = normalizeDriveName(`${item.path || ""} ${item.name || ""}`);
    let score = 0;

    if (!isInsideRodgersPcBrandKit(item)) return 0;

    if (isBrandKitRuleFile(item)) score += 1000;
    if (isBrandKitVisualReferenceFile(item)) score += wantsImage ? 900 : 120;

    if (haystack.includes("image prompt rules")) score += 320;
    if (haystack.includes("og image rules")) score += 310;
    if (haystack.includes("brand rules")) score += 300;
    if (haystack.includes("blog formatting rules")) score += 260;
    if (haystack.includes("approved og examples")) score += 240;
    if (haystack.includes("read me first")) score += 160;

    if (haystack.includes("master") && haystack.includes("logo")) score += 260;
    if (haystack.includes("blue gold silver")) score += 240;
    if (haystack.includes("silver") && haystack.includes("logo")) score += 180;
    if (haystack.includes("coin")) score += 160;

    if (haystack.includes("homepage hero")) score += 220;
    if (haystack.includes("about page hero")) score += 200;
    if (haystack.includes("services")) score += 180;
    if (haystack.includes("meet the owner")) score += 170;
    if (haystack.includes("footer")) score += 150;
    if (haystack.includes("bell county")) score += 150;

    if (haystack.includes("mascot")) score += 180;
    if (haystack.includes("armscrossed")) score += 150;
    if (haystack.includes("thumbs up")) score += 130;
    if (haystack.includes("laptop")) score += 130;

    return score;
  };

  const isKnownModeDefaultFolder = (item: WorkspaceItem): boolean => {
    if (item.type !== "folder") return false;
    if (!isDirectChildOfCommandRoot(item)) return false;

    const normalizedName = normalizeDriveName(item.name || "");
    const allDefaultModeFolderNames = [
      AppMode.TECH_ASSISTANT,
      AppMode.BLOG_ENGINE,
      AppMode.PERSONAL_VAULT,
      AppMode.INVENTORY_SCOUT,
      AppMode.LEAD_RESPONDER,
      AppMode.SEO_LISTINGS,
      AppMode.WEB_CREATIVE,
      AppMode.FRONT_DESK,
    ].flatMap((modeOption) => getModeDefaultDriveFolderNames(modeOption));

    return allDefaultModeFolderNames.some(
      (folderName) => normalizedName === normalizeDriveName(folderName),
    );
  };

  const makeDriveContentFallbackText = (loaded: DriveLoadedContent): string => {
    const imageCount = loaded.images?.length || 0;
    const warningText = loaded.extractionWarnings?.length
      ? ` Warnings: ${loaded.extractionWarnings.join(" | ")}`
      : "";

    if (loaded.content && loaded.content.trim()) {
      return loaded.content;
    }

    if (imageCount > 0) {
      return `[Drive visual file loaded: ${loaded.path || loaded.name}. No readable text was extracted locally, but ${imageCount} visual payload${imageCount === 1 ? "" : "s"} will be sent for AI vision/OCR.]${warningText}`;
    }

    return `[Drive file loaded but no readable text or visual payload was extracted: ${loaded.path || loaded.name}.]${warningText}`;
  };

  const applyLoadedDriveContentToNode = (
    node: WorkspaceItem,
    loaded: DriveLoadedContent,
  ): WorkspaceItem => {
    return {
      ...node,
      content: makeDriveContentFallbackText(loaded),
      loadedContent: loaded,
      extractedImages: loaded.images || [],
      sourceType: loaded.sourceType,
      extractionStatus: loaded.extractionStatus,
      extractionWarnings: loaded.extractionWarnings || [],
      mimeType: loaded.mimeType || node.mimeType,
      size: loaded.size || node.size,
      indexingStatus:
        loaded.extractionStatus === "failed" ? "failed" : ("ready" as const),
    };
  };

  const getNodeLoadedContent = (file: WorkspaceItem): DriveLoadedContent | null => {
    if (file.loadedContent) return file.loadedContent;

    const images = file.extractedImages || [];
    if ((file.content && file.content.trim()) || images.length > 0) {
      return {
        fileId: file.id,
        path: file.path || file.name,
        name: file.name,
        size: file.size,
        mimeType: file.mimeType,
        sourceType: file.sourceType || "unknown",
        content: file.content || "",
        images,
        extractionStatus: file.extractionStatus || "ready",
        extractionWarnings: file.extractionWarnings || [],
      };
    }

    return null;
  };


  const countDriveVisualPayloads = (payloads: ActiveContextPayload[]): number => {
    return payloads.reduce((sum, payload) => sum + (payload.images?.length || 0), 0);
  };

  const getDriveEvidenceLabel = (
    loaded: DriveLoadedContent,
  ): {
    hasReadableText: boolean;
    visualPayloadCount: number;
    canClaimVisualInspection: boolean;
    evidenceLabel: string;
  } => {
    const hasReadableText = Boolean(loaded.content && loaded.content.trim());
    const visualPayloadCount = loaded.images?.length || 0;
    const canClaimVisualInspection = visualPayloadCount > 0;

    let evidenceLabel = "metadata_only";
    if (hasReadableText && visualPayloadCount > 0) {
      evidenceLabel = "text_and_visual_payload";
    } else if (hasReadableText) {
      evidenceLabel = "text_loaded";
    } else if (visualPayloadCount > 0) {
      evidenceLabel = "visual_payload_loaded";
    } else if (loaded.extractionStatus === "failed") {
      evidenceLabel = "load_failed";
    } else {
      evidenceLabel = "loaded_no_readable_content";
    }

    return {
      hasReadableText,
      visualPayloadCount,
      canClaimVisualInspection,
      evidenceLabel,
    };
  };

  /**
   * Drive focus contract:
   * - checked / gold = usable RodgersPC Drive knowledge
   * - unchecked = ignored
   * - Source of Truth is focused and preloaded by default
   * - Other focused folders/files are searched/retrieved on demand only
   */
  const markTreeFocused = (
    node: WorkspaceItem,
    checkVal = true,
    sourceOfTruth = false,
  ): WorkspaceItem => {
    return {
      ...node,
      checked: checkVal,
      sourceOfTruth: sourceOfTruth || node.sourceOfTruth,
      indexingStatus:
        node.type === "file" &&
          checkVal &&
          sourceOfTruth &&
          !node.content &&
          !node.loadedContent
          ? "loading"
          : node.indexingStatus,
      children: node.children?.map((child) =>
        markTreeFocused(child, checkVal, sourceOfTruth),
      ),
    };
  };

  const applyDefaultDriveContextForMode = (
    nodes: WorkspaceItem[],
    modeToApply: AppMode,
  ): WorkspaceItem[] => {
    return nodes.map((node) => {
      if (isLegacyDuplicateSourceScope(node)) {
        return {
          ...node,
          checked: false,
          sourceOfTruth: false,
          content: "",
          indexingStatus:
            node.indexingStatus === "loading" ? "idle" : node.indexingStatus,
          children: node.children
            ? applyDefaultDriveContextForMode(node.children, modeToApply)
            : undefined,
        };
      }

      if (isDefaultSourceOfTruthFolder(node) || isDefaultSourceOfTruthFile(node)) {
        return markTreeFocused(node, true, true);
      }

      if (isDefaultBrandKitFolder(node, modeToApply)) {
        return markTreeFocused(node, true, false);
      }

      if (isDefaultModeFolder(node, modeToApply)) {
        return markTreeFocused(node, true, false);
      }

      if (isKnownModeDefaultFolder(node)) {
        return markTreeFocused(node, false, false);
      }

      if (node.type === "folder" && node.children) {
        return {
          ...node,
          checked: false,
          sourceOfTruth: false,
          children: applyDefaultDriveContextForMode(node.children, modeToApply),
        };
      }

      return {
        ...node,
        checked: false,
        sourceOfTruth: false,
      };
    });
  };

  const downloadSourceOfTruthMissingFileContents = async (
    snapshot: WorkspaceItem[],
    accessTokenOverride?: string | null,
  ) => {
    let activeToken = accessTokenOverride || tokenRef.current;

    if (!driveSyncEnabled) return;

    if (!activeToken) {
      activeToken = await resolveDriveAccessToken("source_of_truth_preload", {
        allowPopup: true,
        forceAccountChooser: true,
        showNotice: true,
      });
    }

    if (!activeToken) {
      setDriveNotice(
        "### Drive reconnect required\n\nSource of Truth could not preload because Google Drive access is missing or expired.",
      );
      return;
    }

    const pendingFiles: { id: string; name: string; path?: string }[] = [];

    const collectPending = (nodes: WorkspaceItem[], insideSource = false) => {
      nodes.forEach((node) => {
        const nowInsideSource = insideSource || Boolean(node.sourceOfTruth);

        if (
          node.type === "file" &&
          node.checked &&
          nowInsideSource &&
          !node.content &&
          !node.loadedContent
        ) {
          pendingFiles.push({ id: node.id, name: node.name, path: node.path });
        }

        if (node.children) collectPending(node.children, nowInsideSource);
      });
    };

    collectPending(snapshot);

    pendingFiles.forEach(async (f) => {
      try {
        const loaded = await downloadDriveLoadedContentWithReconnect(
          activeToken,
          f.id,
          f.name,
          f.path,
          "source_of_truth_preload",
        );

        setItems((prev) => {
          const updateFile = (currentNodes: WorkspaceItem[]): WorkspaceItem[] => {
            return currentNodes.map((node) => {
              if (node.id === f.id) {
                return applyLoadedDriveContentToNode(node, loaded);
              }

              if (node.type === "folder" && node.children) {
                return { ...node, children: updateFile(node.children) };
              }

              return node;
            });
          };

          return updateFile(prev);
        });
      } catch (err) {
        console.warn("[Drive Context] Source of Truth download failed", f.name, err);

        setItems((prev) => {
          const updateFailed = (
            currentNodes: WorkspaceItem[],
          ): WorkspaceItem[] => {
            return currentNodes.map((node) => {
              if (node.id === f.id) {
                return { ...node, indexingStatus: "failed" as const };
              }

              if (node.type === "folder" && node.children) {
                return { ...node, children: updateFailed(node.children) };
              }

              return node;
            });
          };

          return updateFailed(prev);
        });
      }
    });
  };


  // Auto-resize mini floating textarea capsule
  useEffect(() => {
    if (miniTextareaRef.current) {
      miniTextareaRef.current.style.height = "auto";
      const scrollHeight = miniTextareaRef.current.scrollHeight;
      const newHeight = Math.max(32, Math.min(scrollHeight, 150));
      miniTextareaRef.current.style.height = `${newHeight}px`;
    }
  }, [miniInput]);

  const [dbLoaded, setDbLoaded] = useState(false);
  const [lastSavedState, setLastSavedState] = useState<any>(null);
  const [lastSessionCleanNotice, setLastSessionCleanNotice] = useState(false);

  // Manual saved chat memory. This is separate from the active workspace backup.
  // Nothing is added here unless Joshua clicks Save Chat / chooses a folder.
  const [leftPanelView, setLeftPanelView] = useState<LeftPanelView>("drive");
  const [savedChatFolders, setSavedChatFolders] = useState<SavedChatFolder[]>(DEFAULT_SAVED_CHAT_FOLDERS);
  const [savedChats, setSavedChats] = useState<SavedChatRecord[]>([]);
  const [activeSavedChatId, setActiveSavedChatId] = useState<string | null>(null);

  useEffect(() => {
    void persistMessageMediaToLocalVault(messages);
  }, [messages]);

  useEffect(() => {
    const allSavedMessages = savedChats.flatMap((chat) => chat.messages || []);
    void persistMessageMediaToLocalVault(allSavedMessages);
  }, [savedChats]);

  const isInitializingRef = useRef(false);
  const isFirstLoadDoneRef = useRef(false);
  const clearRequestedRef = useRef(false);
  const driveLoadInFlightRef = useRef(false);
  const emptyTreeAutoReloadAttemptedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    if (!user?.uid) {
      setSavedChatFolders(DEFAULT_SAVED_CHAT_FOLDERS);
      setSavedChats([]);
      setActiveSavedChatId(null);
      return;
    }

    const restoreSavedChatMemory = async () => {
      try {
        const [cloudFolders, cloudSummaries] = await Promise.all([
          loadSavedChatFolders(user.uid),
          listChatArchives(user.uid, 100),
        ]);

        if (cancelled) return;

        const customFolders = Array.isArray(cloudFolders)
          ? cloudFolders.filter((folder) => folder && !folder.builtIn)
          : [];

        const loadedCloudChats = (
          await Promise.all(
            cloudSummaries.map(async (summary) => {
              const archive = await loadChatArchive(user.uid, summary.id).catch(() => null);
              if (!archive) return null;

              const archiveMessages = Array.isArray(archive.messages)
                ? await hydrateMessagesFromLocalMediaVault(archive.messages.map(sanitizeRestoredMessageMedia))
                : [];

              return {
                id: archive.id || summary.id,
                title: archive.title || summary.title || "RodgersPC Chat",
                folderId: (archive as any).folderId || (summary as any).folderId || getDefaultSavedChatFolderId((archive.mode || summary.mode || AppMode.TECH_ASSISTANT) as AppMode),
                mode: (archive.mode || archive.currentMode || summary.mode || AppMode.TECH_ASSISTANT) as AppMode,
                messages: archiveMessages,
                createdAt: archive.createdAtIso || summary.createdAtIso || archive.archivedAtIso || new Date().toISOString(),
                updatedAt: archive.updatedAtIso || summary.updatedAtIso || archive.archivedAtIso || new Date().toISOString(),
              } as SavedChatRecord;
            }),
          )
        ).filter(Boolean) as SavedChatRecord[];

        const folderIds = new Set([
          ...DEFAULT_SAVED_CHAT_FOLDERS.map((folder) => folder.id),
          ...customFolders.map((folder) => folder.id),
        ]);
        const recoveredFolders: SavedChatFolder[] = [];
        loadedCloudChats.forEach((chat) => {
          if (chat.folderId && !folderIds.has(chat.folderId)) {
            folderIds.add(chat.folderId);
            recoveredFolders.push({
              id: chat.folderId,
              name: "Recovered Saved Chats",
              createdAt: chat.createdAt || new Date().toISOString(),
            });
          }
        });

        if (!cancelled) {
          setSavedChatFolders([...DEFAULT_SAVED_CHAT_FOLDERS, ...customFolders, ...recoveredFolders]);
          setSavedChats(
            loadedCloudChats.sort(
              (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
            ),
          );
          setActiveSavedChatId(null);
        }
      } catch (err) {
        console.error("Saved chat cloud restore failed:", err);
        if (!cancelled) {
          setSavedChatFolders(DEFAULT_SAVED_CHAT_FOLDERS);
          setSavedChats([]);
          setActiveSavedChatId(null);
        }
      }
    };

    void restoreSavedChatMemory();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;

    const customFolders = savedChatFolders.filter((folder) => !folder.builtIn);
    void saveSavedChatFolders(user.uid, customFolders).catch((err) => {
      console.error("Saved chat folders cloud write failed:", err);
    });
  }, [user?.uid, savedChatFolders]);

  const restoreStateToUi = (saved: any) => {
    if (!saved) return;

    const isActuallyCleared = saved.isCleared === true;

    if (
      Array.isArray(saved.messages) &&
      saved.messages.length > 0 &&
      !isActuallyCleared
    ) {
      const restoredMessages = saved.messages.map(sanitizeRestoredMessageMedia);
      setMessages(restoredMessages);
      void hydrateMessagesFromLocalMediaVault(restoredMessages).then((hydratedMessages) => {
        setMessages(hydratedMessages);
      });
    } else {
      setMessages([]);
    }

    // Always start the workstation in Bench mode. Saved chats can be continued manually, but the active operating defaults should not reopen in SEO/Creative.
    setCurrentMode(AppMode.TECH_ASSISTANT);
    setGlobalTheme(normalizeUiTheme(saved.globalTheme, "rodgerspc"));
    setChatTheme(normalizeUiTheme(saved.chatTheme, "rodgerspc"));
    setInputTheme(normalizeUiTheme(saved.inputTheme ?? saved.chatTheme, "rodgerspc"));

    setDriveSyncEnabled(saved.driveSyncEnabled ?? true);
    setSearchGroundingEnabled(saved.searchGroundingEnabled ?? false);
    setImageGenerationEnabled(saved.imageGenerationEnabled ?? false);
    setFastModeEnabled(saved.fastModeEnabled ?? false);
    setDriveSidebarVisible(saved.driveSidebarVisible ?? true);
    setBottomPanelVisible(saved.bottomPanelVisible ?? true);
    setAiTemperature(0.02);

    // Permanent default AI control state. Do not restore SEO/Creative from old saved workspace state.
    setAccuracyMode("strict");
    setResponseLength("short");
    setJobSensitivity("important");
    setAiPreset("bench_job");
    setAiBehavior("strict");
    setAiVerbosity("minimum");

    if (Array.isArray(saved.items) && saved.items.length > 0) {
      setItems(applyDefaultDriveContextForMode(saved.items, AppMode.TECH_ASSISTANT));
    }
  };

  const loadOrInitState = async (
    resolvedUser: User,
    resolvedToken: string | null,
  ) => {
    if (isInitializingRef.current) return;

    isInitializingRef.current = true;
    setDbLoaded(false);

    try {
      console.info("[App Restore] Loading cloud workspace", {
        uid: resolvedUser.uid,
        email: resolvedUser.email,
      });

      const saved = await loadWorkspaceState(resolvedUser.uid);
      const lastSaved = await loadLastSavedWorkspaceState(
        resolvedUser.uid,
      ).catch(() => null);

      if (
        lastSaved &&
        Array.isArray(lastSaved.messages) &&
        lastSaved.messages.length > 0
      ) {
        setLastSavedState(lastSaved);
        setLastSessionCleanNotice(false);
      }

      if (saved && saved.isCleared !== true) {
        restoreStateToUi(saved);

        if (resolvedToken && (saved.driveSyncEnabled ?? true)) {
          loadGoogleDriveHierarchy(
            resolvedToken,
            DEFAULT_DRIVE_FOLDER_ID,
            saved.items || [],
            true,
          );
        }
      } else {
        setMessages([]);

        if (resolvedToken) {
          loadGoogleDriveHierarchy(
            resolvedToken,
            DEFAULT_DRIVE_FOLDER_ID,
            undefined,
            true,
          );
        }
      }

      isFirstLoadDoneRef.current = true;
    } catch (err) {
      console.error(
        "Error restoring user workspace configuration from database:",
        err,
      );

      if (resolvedToken) {
        loadGoogleDriveHierarchy(
          resolvedToken,
          DEFAULT_DRIVE_FOLDER_ID,
          undefined,
          true,
        );
      }

      isFirstLoadDoneRef.current = true;
    } finally {
      setDbLoaded(true);
      isInitializingRef.current = false;
    }
  };

  // Auto-sync entire workspace configuration state to secure Firestore.
  // Important: normal empty startup state must not overwrite a real active chat.
  useEffect(() => {
    if (!user || !dbLoaded || !isFirstLoadDoneRef.current) return;

    const clearSaveRequested = clearRequestedRef.current === true;

    if (messages.length === 0 && !clearSaveRequested) {
      return;
    }

    const sanitizeItems = (list: WorkspaceItem[]): WorkspaceItem[] => {
      return list.map((item) => ({
        id: item.id,
        name: item.name,
        type: item.type,
        path: item.path,
        size: item.size,
        checked: item.checked,
        sourceOfTruth: item.sourceOfTruth,
        content: "",
        loadedContent: undefined,
        extractedImages: undefined,
        sourceType: item.sourceType,
        extractionStatus: item.extractionStatus,
        extractionWarnings: item.extractionWarnings,
        indexingStatus: item.indexingStatus,
        children: item.children ? sanitizeItems(item.children) : undefined,
      }));
    };

    const sanitizeMessages = (list: ChatMessage[]): ChatMessage[] => {
      // Never store fake/truncated data URLs in cloud state.
      // A string ending in "[Payload size optimized]" is not a valid image and later breaks export/PDF/DOC evidence.
      // Large media stays in the live React state for current-session export; cloud backup keeps metadata only.
      return list.map(sanitizeMessageForCloudSave);
    };

    const statePayload = {
      userId: user.uid,
      email: user.email || "",
      globalTheme,
      chatTheme,
      inputTheme,
      currentMode,
      driveSyncEnabled,
      searchGroundingEnabled,
      imageGenerationEnabled,
      fastModeEnabled,
      driveSidebarVisible,
      bottomPanelVisible,
      aiTemperature,
      aiBehavior,
      aiVerbosity,
      accuracyMode,
      responseLength,
      jobSensitivity,
      aiPreset,
      messages: sanitizeMessages(messages),
      items: sanitizeItems(items),
      isCleared: clearSaveRequested,
      clearedAt: clearSaveRequested ? new Date().toISOString() : null,
      activeChatId: `${user.uid}-active`,
    };

    const timer = setTimeout(() => {
      saveWorkspaceState(user.uid, statePayload)
        .then(() => {
          if (clearSaveRequested) {
            clearRequestedRef.current = false;
          }
        })
        .catch((err) => {
          console.error("Cloud workspace auto-save request failed:", err);
        });
    }, 800);

    return () => clearTimeout(timer);
  }, [
    user,
    dbLoaded,
    globalTheme,
    chatTheme,
    inputTheme,
    currentMode,
    driveSyncEnabled,
    searchGroundingEnabled,
    imageGenerationEnabled,
    fastModeEnabled,
    driveSidebarVisible,
    bottomPanelVisible,
    aiTemperature,
    aiBehavior,
    aiVerbosity,
    accuracyMode,
    responseLength,
    jobSensitivity,
    aiPreset,
    messages,
    items,
  ]);

  // Monitor Google OAuth Auth Session
  useEffect(() => {
    const unsubscribe = initAuth(
      (currentUser, accessToken) => {
        if (!currentUser) {
          setUser(null);
          setToken(null);
          setAuthLoading(false);
          setItems([]);
          setDbLoaded(false);
          isFirstLoadDoneRef.current = false;
          isInitializingRef.current = false;
          driveLoadInFlightRef.current = false;
          emptyTreeAutoReloadAttemptedRef.current = false;
          return;
        }

        setUser(currentUser);
        setToken(accessToken);
        setAuthLoading(false);

        const isAllowed = ALLOWED_EMAILS.includes(
          (currentUser.email || "").toLowerCase(),
        );

        if (isAllowed) {
          loadOrInitState(currentUser, accessToken);
        } else {
          setDbLoaded(true);
        }
      },
      () => {
        setUser(null);
        setToken(null);
        setAuthLoading(false);
        setItems([]);
        setDbLoaded(false);
        isFirstLoadDoneRef.current = false;
        isInitializingRef.current = false;
        driveLoadInFlightRef.current = false;
        emptyTreeAutoReloadAttemptedRef.current = false;
      },
    );

    return () => unsubscribe();
  }, []);

  const loadGoogleDriveHierarchy = async (
    accessToken: string | null,
    folderInput = DEFAULT_DRIVE_FOLDER_ID,
    overrideItems?: WorkspaceItem[],
    forceLoad = false,
    modeOverride?: AppMode,
  ) => {
    const modeForDriveContext = modeOverride || currentMode;
    let activeToken = accessToken;

    if (!activeToken) {
      activeToken = await resolveDriveAccessToken("drive_hierarchy_load", {
        allowPopup: true,
        forceAccountChooser: true,
        showNotice: true,
      });
    }

    if (!activeToken) {
      console.warn(
        "[Drive Restore] Cannot load Drive tree because Google access token is missing.",
      );
      setDriveNotice(
        "### Drive reconnect required\n\nDrive Sync is ON, but Google Drive access is missing or expired. Reconnect Drive, then refresh Drive Brain.",
      );
      return;
    }

    // Normal Drive Sync OFF still blocks Drive usage. Startup/manual refresh/upload use forceLoad.
    if (!forceLoad && !driveSyncEnabled) {
      console.info(
        "[Drive Restore] Drive Sync is OFF; Drive tree load skipped.",
      );
      return;
    }

    // Prevent duplicate loads, but do not let stale loading state create a permanent empty tree.
    if (driveLoadInFlightRef.current) {
      console.info(
        "[Drive Restore] Drive load already in progress; duplicate request skipped.",
      );
      return;
    }

    driveLoadInFlightRef.current = true;
    setDriveLoading(true);

    try {
      let extractedId = extractFolderId(folderInput || DEFAULT_DRIVE_FOLDER_ID);
      if (!extractedId || extractedId === "root") {
        extractedId = DEFAULT_DRIVE_FOLDER_ID;
      }

      console.info("[Drive Restore] Loading Drive hierarchy", {
        folderInput,
        extractedId,
        forceLoad,
        driveSyncEnabled,
        existingItems: items.length,
        overrideItems: overrideItems?.length || 0,
      });

      let rootFolderName = "RodgersPC Command Center";
      try {
        const dynamicName = await fetchDriveFolderName(
          activeToken,
          extractedId,
        );
        if (dynamicName && dynamicName.trim()) {
          rootFolderName = dynamicName.trim();
        }
      } catch (e) {
        console.warn(
          "[Drive Restore] Root folder name lookup failed. Using default name.",
          e,
        );
      }

      const fetchedItems = await fetchDriveFolderRecursively(
        activeToken,
        extractedId,
        `/${rootFolderName}`,
      );

      const rootNode: WorkspaceItem = {
        id: extractedId,
        name: rootFolderName,
        type: "folder",
        path: `/${rootFolderName}`,
        children: Array.isArray(fetchedItems) ? fetchedItems : [],
        checked: false,
        sourceOfTruth: false,
      };

      const incoming: WorkspaceItem[] = [rootNode];

      // Merge incoming Drive tree with existing focus state.
      const existingMap = new Map<string, WorkspaceItem>();
      const mapNodes = (nodes: WorkspaceItem[] = []) => {
        nodes.forEach((n) => {
          existingMap.set(n.id, n);
          if (n.children) mapNodes(n.children);
        });
      };
      mapNodes(
        overrideItems && overrideItems.length > 0 ? overrideItems : items,
      );

      const merge = (nodes: WorkspaceItem[]): WorkspaceItem[] => {
        return nodes.map((n) => {
          const ext = existingMap.get(n.id);
          const shouldDefaultCheck =
            isDefaultSourceOfTruthFolder(n) ||
            isDefaultSourceOfTruthFile(n) ||
            isDefaultModeFolder(n, modeForDriveContext);
          const forceUncheckedLegacyDuplicate = isLegacyDuplicateSourceScope(n);
          const forceUncheckedOtherModeDefault =
            isKnownModeDefaultFolder(n) && !isDefaultModeFolder(n, modeForDriveContext);
          const checked = forceUncheckedLegacyDuplicate || forceUncheckedOtherModeDefault
            ? false
            : ext
              ? Boolean(ext.checked || shouldDefaultCheck)
              : shouldDefaultCheck;
          const sourceOfTruth = forceUncheckedLegacyDuplicate
            ? false
            : Boolean(ext?.sourceOfTruth || n.sourceOfTruth);
          const content = forceUncheckedLegacyDuplicate ? "" : ext?.content || n.content || "";
          const loadedContent = forceUncheckedLegacyDuplicate ? undefined : ext?.loadedContent || n.loadedContent;
          const extractedImages = forceUncheckedLegacyDuplicate ? undefined : ext?.extractedImages || n.extractedImages;
          const sourceType = forceUncheckedLegacyDuplicate ? undefined : ext?.sourceType || n.sourceType;
          const extractionStatus = forceUncheckedLegacyDuplicate ? undefined : ext?.extractionStatus || n.extractionStatus;
          const extractionWarnings = forceUncheckedLegacyDuplicate ? undefined : ext?.extractionWarnings || n.extractionWarnings;

          if (n.type === "folder") {
            return {
              ...n,
              checked,
              sourceOfTruth,
              content,
              loadedContent,
              extractedImages,
              sourceType,
              extractionStatus,
              extractionWarnings,
              children: merge(n.children || []),
            };
          }

          return {
            ...n,
            checked,
            sourceOfTruth,
            content,
            loadedContent,
            extractedImages,
            sourceType,
            extractionStatus,
            extractionWarnings,
          };
        });
      };

      const nextItems = applyDefaultDriveContextForMode(merge(incoming), modeForDriveContext);
      console.info("[Drive Restore] Drive tree loaded", {
        rootFolderName,
        rootChildren: rootNode.children?.length || 0,
        topLevelItems: nextItems.length,
      });

      setItems(nextItems);
      emptyTreeAutoReloadAttemptedRef.current = false;

      // Only Source of Truth content preloads. Other focused Drive items are retrieved on demand.
      downloadSourceOfTruthMissingFileContents(nextItems, activeToken);
    } catch (err: any) {
      console.error("[Drive Restore] Failed to load Drive hierarchy", err);

      // Do not erase a previously loaded tree on failure. If there is no tree, show the root shell so the UI is stable.
      setItems((prev) => {
        if (prev.length > 0) return prev;
        const fallbackId =
          extractFolderId(folderInput || DEFAULT_DRIVE_FOLDER_ID) ||
          DEFAULT_DRIVE_FOLDER_ID;
        return [
          {
            id: fallbackId,
            name: "RodgersPC Command Center",
            type: "folder",
            path: "/RodgersPC Command Center",
            children: [],
            checked: false,
            sourceOfTruth: false,
            indexingStatus: "failed" as const,
          } as WorkspaceItem,
        ];
      });
    } finally {
      driveLoadInFlightRef.current = false;
      setDriveLoading(false);
    }
  };

  // If Drive Sync is ON after login and the tree is empty, attempt one safe reload.
  // Manual refresh can retry; this avoids twitch loops while still recovering from empty saved state.
  useEffect(() => {
    if (!user || !token || !dbLoaded || !driveSyncEnabled) return;

    if (items.length > 0) {
      emptyTreeAutoReloadAttemptedRef.current = false;
      return;
    }

    if (
      driveLoading ||
      driveLoadInFlightRef.current ||
      emptyTreeAutoReloadAttemptedRef.current
    )
      return;

    emptyTreeAutoReloadAttemptedRef.current = true;
    loadGoogleDriveHierarchy(token, DEFAULT_DRIVE_FOLDER_ID, undefined, true);
  }, [user, token, dbLoaded, driveSyncEnabled, items.length, driveLoading]);

  const handleGoogleLogin = async () => {
    if (loginBusy) return;

    setLoginBusy(true);
    setLoginError(null);

    try {
      // Customer Bench Device Mode must be applied immediately before Google sign-in.
      // This prevents stale checkbox state from causing the login button to appear dead.
      setCustomerBenchDeviceMode(Boolean(guestModeActive));

      const result = await googleSignIn(Boolean(guestModeActive));

      if (!result) {
        throw new Error(
          "Google sign-in did not return a session. Popup may have been blocked or cancelled.",
        );
      }

      setUser(result.user);
      setToken(result.accessToken);

      const isAllowed = ALLOWED_EMAILS.includes(
        (result.user.email || "").toLowerCase(),
      );

      if (isAllowed) {
        loadOrInitState(result.user, result.accessToken);
      } else {
        setDbLoaded(true);
      }
    } catch (err: any) {
      console.error("Interactive Google authentication failed:", err);

      setLoginError(
        guestModeActive
          ? "Customer Bench Device Mode login did not complete. Allow the Google popup/account chooser, then retry. Incognito/InPrivate is still recommended on customer devices."
          : "Google login did not complete. Allow the popup/account chooser, then retry.",
      );
    } finally {
      setLoginBusy(false);
    }
  };

  const handleGoogleLogout = async () => {
    try {
      await logout();
      setUser(null);
      setToken(null);
      setItems([]);
      setMessages([]);
      setDbLoaded(false);
      isFirstLoadDoneRef.current = false;
      driveLoadInFlightRef.current = false;
      emptyTreeAutoReloadAttemptedRef.current = false;
    } catch (err: any) {
      console.error("Interactive disconnection failed:", err);
    }
  };

  const handleClearChat = () => {
    handleCancelRequest();

    if (messages.length > 0) {
      const stateBackup = {
        messages: [...messages],
        globalTheme,
        chatTheme,
        inputTheme,
        currentMode,
        driveSyncEnabled,
        searchGroundingEnabled,
        imageGenerationEnabled,
        fastModeEnabled,
        driveSidebarVisible,
        bottomPanelVisible,
        aiTemperature,
        accuracyMode,
        responseLength,
        jobSensitivity,
        aiPreset,
        aiBehavior,
        aiVerbosity,
        items,
        isCleared: false,
        activeChatId: user ? `${user.uid}-active` : `local-${Date.now()}`,
      };
      setLastSavedState(stateBackup);
      setLastSessionCleanNotice(false);
    }

    clearRequestedRef.current = true;
    setMessages([]);
    setActiveSavedChatId(null);

    if (driveSyncEnabled) {
      const updatedDriveContext = applyDefaultDriveContextForMode(items, currentMode);
      setItems(updatedDriveContext);
      downloadSourceOfTruthMissingFileContents(updatedDriveContext);
    }
  };

  const handleContinueLastChat = () => {
    if (
      lastSavedState &&
      lastSavedState.messages &&
      lastSavedState.messages.length > 0
    ) {
      restoreStateToUi({
        ...lastSavedState,
        isCleared: false,
      });
      clearRequestedRef.current = false;
    }
  };

  const handleClearLastSavedChat = async () => {
    setLastSavedState(null);
    setLastSessionCleanNotice(true);

    if (!user) return;

    try {
      await clearLastSavedWorkspaceState(user.uid);
    } catch (err) {
      console.error("Failed to clear last saved chat backup:", err);
    }
  };

  const handleUpdateMessage = (id: string, newContent: string) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id === id) {
          return {
            ...m,
            content: newContent,
            originalContent:
              m.originalContent === undefined ? m.content : m.originalContent,
          };
        }
        return m;
      }),
    );
  };

  const handleUpdateReport = (
    msgId: string,
    reportId: string,
    newText: string,
  ) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id === msgId && m.reports) {
          const updatedReports = m.reports.map((rep) => {
            if (rep.id === reportId) {
              return {
                ...rep,
                text: newText,
                originalText:
                  rep.originalText === undefined ? rep.text : rep.originalText,
              };
            }
            return rep;
          });
          return { ...m, reports: updatedReports };
        }
        return m;
      }),
    );
  };

  const handleFetchContent = async (id: string): Promise<string> => {
    if (!driveSyncEnabled) return "";

    const fetchToken = await resolveDriveAccessToken("manual_drive_file_fetch", {
      allowPopup: true,
      forceAccountChooser: true,
      showNotice: true,
    });

    if (!fetchToken) return "[Drive file fetch blocked: missing Google Drive access token.]";

    let fetchedStr = "";

    const updateNode = async (
      nodes: WorkspaceItem[],
    ): Promise<WorkspaceItem[]> => {
      const result: WorkspaceItem[] = [];
      for (const node of nodes) {
        if (node.id === id) {
          const existingLoaded = getNodeLoadedContent(node);
          if (!existingLoaded) {
            setLoading(true);
            try {
              const activeToken = await resolveDriveAccessToken("manual_drive_file_fetch", {
                allowPopup: true,
                forceAccountChooser: true,
                showNotice: true,
              });

              if (!activeToken) {
                throw new Error("missing_google_access_token");
              }

              const loaded = await downloadDriveLoadedContentWithReconnect(
                activeToken,
                node.id,
                node.name,
                node.path,
                "manual_drive_file_fetch",
              );
              fetchedStr = makeDriveContentFallbackText(loaded);
              result.push(applyLoadedDriveContentToNode(node, loaded));
            } catch (err) {
              fetchedStr = `[Error downloading file content: ${err}]`;
              result.push({ ...node, content: fetchedStr, indexingStatus: "failed" as const });
            } finally {
              setLoading(false);
            }
          } else {
            fetchedStr = makeDriveContentFallbackText(existingLoaded);
            result.push(applyLoadedDriveContentToNode(node, existingLoaded));
          }
        } else if (node.type === "folder" && node.children) {
          result.push({
            ...node,
            children: await updateNode(node.children),
          });
        } else {
          result.push(node);
        }
      }
      return result;
    };

    const updated = await updateNode(items);
    setItems(updated);
    return fetchedStr;
  };

  const handleAttachDriveFile = async (id: string) => {
    if (!driveSyncEnabled) {
      const message =
        "### Drive file not loaded\n\nDrive Sync is OFF. Turn Drive Sync ON before loading Drive files into the chat context.";
      setDriveNotice(message);
      addInternalStatusMessage(message);
      return;
    }

    const target = findDriveItemById(items, id);

    if (!target) {
      const message =
        "### Drive file not found\n\nThat Drive item is no longer available in the current Drive tree. Refresh Drive Brain and try again.";
      setDriveNotice(message);
      addInternalStatusMessage(message);
      return;
    }

    if (target.type !== "file") {
      const message =
        "### Folder focused\n\nFolder focus is already handled by the Drive Brain. Pick a specific file if you want to load it into the visible chat context.";
      setDriveNotice(message);
      addInternalStatusMessage(message);
      return;
    }

    if (!target.checked) {
      const markChecked = (nodes: WorkspaceItem[]): WorkspaceItem[] => {
        return nodes.map((node) => {
          if (node.id === id) {
            return { ...node, checked: true, indexingStatus: "loading" as const };
          }

          if (node.children) {
            return { ...node, children: markChecked(node.children) };
          }

          return node;
        });
      };

      setItems((prev) => markChecked(prev));
    }

    const loadedText = await handleFetchContent(id);
    const preview = (loadedText || "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 900);

    const message = [
      "### Drive file loaded into chat context",
      "",
      `**File:** ${target.path || target.name}`,
      "",
      preview
        ? `**Preview:** ${preview}${loadedText.length > preview.length ? "…" : ""}`
        : "**Preview:** No readable text preview was extracted. If this is an image/PDF and visual payloads loaded, the model can use the visual payload on the next request.",
      "",
      "This file is now focused and loaded for the next message."
    ].join("\n");

    setDriveNotice(message);
    addInternalStatusMessage(message);
  };

  const handleToggleCheck = (id: string) => {
    if (!driveSyncEnabled) return;

    let nextChecked: boolean | null = null;

    const findTarget = (nodes: WorkspaceItem[]) => {
      for (const node of nodes) {
        if (node.id === id) {
          nextChecked = !node.checked;
          return;
        }

        if (node.children) findTarget(node.children);
      }
    };

    findTarget(items);
    if (nextChecked === null) return;

    const setFocusedRecursive = (
      node: WorkspaceItem,
      checkVal: boolean,
    ): WorkspaceItem => {
      return {
        ...node,
        checked: checkVal,
        indexingStatus:
          node.type === "file" &&
          checkVal &&
          node.sourceOfTruth &&
          !node.content &&
          !node.loadedContent
            ? "loading"
            : node.indexingStatus,
        children: node.children?.map((child) =>
          setFocusedRecursive(child, checkVal),
        ),
      };
    };

    const applyCheckingState = (nodes: WorkspaceItem[]): WorkspaceItem[] => {
      return nodes.map((node) => {
        if (node.id === id) {
          return setFocusedRecursive(node, Boolean(nextChecked));
        }

        if (node.children) {
          return {
            ...node,
            children: applyCheckingState(node.children),
          };
        }

        return node;
      });
    };

    const updatedItems = applyCheckingState(items);
    setItems(updatedItems);

    downloadSourceOfTruthMissingFileContents(updatedItems);
  };

  const handleTogglePin = (_id: string) => {
    return;
  };

  const handleImportDrive = async (url: string) => {
    if (!driveSyncEnabled) return;

    const activeToken = await resolveDriveAccessToken("drive_import_folder", {
      allowPopup: true,
      forceAccountChooser: true,
      showNotice: true,
    });

    if (!activeToken) return;

    await loadGoogleDriveHierarchy(activeToken, url, undefined, true);
  };

  // Uploads file directly to Google Drive Shared Root folder.
  // Important: when Drive Sync is ON, never create a fake local Drive item after upload failure.
  const handleUploadFile = async (
    name: string,
    content: string,
    mimeType?: string,
  ) => {
    const uploadName = buildUploadDisplayName(name);
    const uploadContent = content || "";
    const uploadMimeType = mimeType || "text/plain";

    if (driveSyncEnabled) {
      const activeToken = await resolveDriveAccessToken("drive_upload", {
        allowPopup: true,
        forceAccountChooser: true,
        showNotice: true,
      });

      if (!activeToken) {
        const message =
          "### Drive upload blocked\n\nDrive Sync is ON, but the Google Drive token is missing or expired. Reconnect Drive, then retry the upload. No fake local Drive file was created.";
        setDriveNotice(message);
        addInternalStatusMessage(message);
        throw new Error("Drive upload blocked because token is missing.");
      }

      setDriveLoading(true);
      setDriveNotice(null);

      try {
        await uploadFileToDriveFolder(
          activeToken,
          DEFAULT_DRIVE_FOLDER_ID,
          uploadName,
          uploadContent,
          uploadMimeType,
        );

        await loadGoogleDriveHierarchy(
          activeToken,
          DEFAULT_DRIVE_FOLDER_ID,
          undefined,
          true,
        );

        const message = `### Drive upload completed\n\n${uploadName} was sent to the RodgersPC Command Center Drive folder and the Drive tree was refreshed.`;
        setDriveNotice(message);
      } catch (err: any) {
        console.error("Google Drive upload failed:", err);

        const message = `### Drive upload failed\n\n${uploadName} was not confirmed in Google Drive. No fake local Drive file was created.\n\nError: ${err?.message || err}`;
        setDriveNotice(message);
        addInternalStatusMessage(message);
        throw err;
      } finally {
        setDriveLoading(false);
      }

      return;
    }

    // Drive Sync OFF means local-only attachment/context. It must not look like a real Drive upload.
    const localId = `local_${Date.now()}`;
    const localName = `[LOCAL ONLY] ${uploadName}`;
    const newItem: WorkspaceItem = {
      id: localId,
      name: localName,
      type: "file",
      path: `/Local Uploads/${uploadName}`,
      size: uploadContent.length,
      content: uploadContent,
      checked: true,
      sourceOfTruth: false,
      indexingStatus: "ready" as const,
    };

    setItems((prev) => [newItem, ...prev]);

    const message = `### Local-only file added\n\n${uploadName} was added to this workspace only because Drive Sync is OFF. It was not uploaded to Google Drive.`;
    setDriveNotice(message);
  };

  // Traverse recursive folders to list all focused Drive file instances.
  const getFocusedDriveFiles = (
    itemsGoal: WorkspaceItem[],
  ): WorkspaceItem[] => {
    let list: WorkspaceItem[] = [];

    itemsGoal.forEach((item) => {
      if (item.type === "file" && item.checked) {
        list.push(item);
      }

      if (item.children) {
        list = [...list, ...getFocusedDriveFiles(item.children)];
      }
    });

    return list;
  };

  const getAllDriveFiles = (
    itemsGoal: WorkspaceItem[],
  ): WorkspaceItem[] => {
    let list: WorkspaceItem[] = [];

    itemsGoal.forEach((item) => {
      if (item.type === "file") {
        list.push(item);
      }

      if (item.children) {
        list = [...list, ...getAllDriveFiles(item.children)];
      }
    });

    return list;
  };

  const findDriveItemById = (
    itemsGoal: WorkspaceItem[],
    id: string,
  ): WorkspaceItem | null => {
    for (const item of itemsGoal) {
      if (item.id === id) return item;

      if (item.children) {
        const found = findDriveItemById(item.children, id);
        if (found) return found;
      }
    }

    return null;
  };

  const activeContextList = useMemo(() => {
    return getFocusedDriveFiles(items);
  }, [items]);

  const totalCharsCount = useMemo(() => {
    return activeContextList.reduce(
      (sum, item) =>
        sum +
        (item.loadedContent?.content
          ? item.loadedContent.content.length
          : item.content
            ? item.content.length
            : 0),
      0,
    );
  }, [activeContextList]);

  const buildDriveOffScopeMetadata = (): string => {
    return [
      "DRIVE BRAIN / FOCUS PACKAGE",
      "",
      "Drive Sync is OFF for this request.",
      "- No Google Drive file content was loaded, read, summarized, quoted, or sent to the model.",
      "- Previously loaded Drive metadata may still exist in the UI tree, but it is not usable context while Drive Sync is OFF.",
      "- If RodgersPC Drive knowledge is needed, turn Drive Sync ON and focus the needed files/folders."
    ].join("\n");
  };

  const normalizeDriveLoadError = (err: unknown): string => {
    const raw =
      err instanceof Error
        ? err.message
        : typeof err === "string"
          ? err
          : String(err || "unknown_error");

    const lower = raw.toLowerCase();

    if (
      lower.includes("missing google drive access token") ||
      lower.includes("missing_google_access_token") ||
      lower.includes("no google drive access token")
    ) {
      return "missing_google_access_token — Missing Google Drive access token. Reconnect Drive.";
    }

    if (
      lower.includes("401") ||
      lower.includes("unauthorized") ||
      lower.includes("invalid credentials") ||
      lower.includes("auth")
    ) {
      return `google_drive_auth_failed — ${raw}`;
    }

    if (lower.includes("403") || lower.includes("forbidden")) {
      return `google_drive_forbidden — ${raw}`;
    }

    return raw;
  };

  const isDriveAuthError = (err: unknown): boolean => {
    const normalized = normalizeDriveLoadError(err).toLowerCase();
    return (
      normalized.includes("missing_google_access_token") ||
      normalized.includes("google_drive_auth_failed") ||
      normalized.includes("401") ||
      normalized.includes("unauthorized") ||
      normalized.includes("invalid credentials")
    );
  };

  const resolveDriveAccessToken = async (
    reason: string,
    options?: {
      allowPopup?: boolean;
      forceAccountChooser?: boolean;
      showNotice?: boolean;
    },
  ): Promise<string | null> => {
    if (!driveSyncEnabledRef.current) return null;

    if (tokenRef.current && tokenRef.current.trim()) {
      return tokenRef.current;
    }

    try {
      const result = await ensureGoogleDriveToken({
        reason,
        allowPopup: options?.allowPopup !== false,
        forceAccountChooser: options?.forceAccountChooser !== false,
        isCustomerBenchMode: guestModeActiveRef.current,
      });

      if (!result?.accessToken) {
        if (options?.showNotice !== false) {
          const message =
            "### Drive reconnect required\n\nDrive Sync is ON, but the Google Drive access token is missing or expired. Reconnect Drive before using Drive evidence.";
          setDriveNotice(message);
          addInternalStatusMessage(message);
        }
        return null;
      }

      setUser(result.user);
      setToken(result.accessToken);
      tokenRef.current = result.accessToken;

      if (options?.showNotice !== false && result.refreshed) {
        setDriveNotice(
          "### Drive reconnected\n\nGoogle Drive access was refreshed. Drive evidence loading can continue.",
        );
      }

      return result.accessToken;
    } catch (err: any) {
      const message = `### Drive reconnect failed\n\nDrive Sync is ON, but Google Drive access could not be refreshed. Finish the Google popup/account chooser, approve Drive access, then retry.\n\nError: ${err?.message || err}`;
      setDriveNotice(message);
      addInternalStatusMessage(message);
      return null;
    }
  };

  const downloadDriveLoadedContentWithReconnect = async (
    accessToken: string,
    fileId: string,
    fileName: string,
    filePath?: string,
    reason = "drive_file_content_load",
  ): Promise<DriveLoadedContent> => {
    try {
      return await downloadDriveLoadedContent(
        accessToken,
        fileId,
        fileName,
        filePath,
      );
    } catch (err) {
      if (!isDriveAuthError(err)) {
        throw err;
      }

      const refreshedToken = await resolveDriveAccessToken(reason, {
        allowPopup: true,
        forceAccountChooser: true,
        showNotice: true,
      });

      if (!refreshedToken) {
        throw new Error(normalizeDriveLoadError(err));
      }

      return await downloadDriveLoadedContent(
        refreshedToken,
        fileId,
        fileName,
        filePath,
      );
    }
  };

  const clipContinuityText = (value: unknown, maxChars = 1400): string => {
    const cleaned = String(value || "")
      .replace(/\s+/g, " ")
      .trim();

    if (!cleaned) return "";
    if (cleaned.length <= maxChars) return cleaned;

    return `${cleaned.slice(0, maxChars).trim()}…`;
  };

  const summarizeMessageAttachmentsForContinuity = (message: ChatMessage): string => {
    const attachments = message.attachments || [];
    const parts: string[] = [];

    if (message.imageUrl || message.imageAsset || (message.imageAssets && message.imageAssets.length > 0)) {
      parts.push("generated-image-present");
    }

    attachments.forEach((attachment, index) => {
      const type = attachment.type || "file";
      const name = attachment.name || `attachment-${index + 1}`;
      const mime = attachment.mimeType ? `; ${attachment.mimeType}` : "";
      const hasVisionPayload = attachment.base64 ? "; visual-payload=present" : "";
      parts.push(`${type}: ${name}${mime}${hasVisionPayload}`);
    });

    return parts.join(" | ");
  };

  const getMessageModeForContinuity = (message: ChatMessage & {
    modeAtSend?: AppMode;
    newMode?: AppMode;
    prevMode?: AppMode;
  }): string => {
    if (message.isModeChangeIndicator && message.newMode) {
      return `MODE SHIFT: ${getModeDisplayName(message.prevMode || currentMode)} → ${getModeDisplayName(message.newMode)}`;
    }

    if (message.modeAtSend) {
      return getModeDisplayName(message.modeAtSend);
    }

    return "mode not tagged";
  };

  const buildCrossModeContinuityPacket = (
    chatHistory: ChatMessage[],
    currentUserMessage: ChatMessage,
    activeMode: AppMode,
  ): string => {
    const historyWindow = [...chatHistory, currentUserMessage]
      .slice(-24)
      .filter((message) => {
        if (!message) return false;
        if (message.role !== "user" && message.role !== "assistant") return false;
        if (message.isModeChangeIndicator) return true;
        return Boolean(
          String(message.content || "").trim() ||
          message.imageUrl ||
          message.imageAsset ||
          (message.imageAssets && message.imageAssets.length > 0) ||
          (message.attachments && message.attachments.length > 0),
        );
      });

    const priorModeShifts = chatHistory
      .filter((message) => message.isModeChangeIndicator)
      .slice(-6)
      .map((message) => {
        const typed = message as ChatMessage & { newMode?: AppMode; prevMode?: AppMode };
        return `${getModeDisplayName(typed.prevMode || currentMode)} → ${getModeDisplayName(typed.newMode || currentMode)}`;
      });

    const priorAssistantOutputs = chatHistory
      .filter((message) => message.role === "assistant" && !message.isModeChangeIndicator && String(message.content || "").trim())
      .slice(-4)
      .map((message) => clipContinuityText(message.content, 900));

    const recentAttachmentLines = historyWindow
      .map((message, index) => {
        const attachmentSummary = summarizeMessageAttachmentsForContinuity(message);
        if (!attachmentSummary) return "";
        return `- Turn ${index + 1} (${message.role}; ${getMessageModeForContinuity(message as any)}): ${attachmentSummary}`;
      })
      .filter(Boolean);

    const recentCorrectionLines = historyWindow
      .filter((message) => message.role === "user")
      .map((message) => clipContinuityText(message.content, 700))
      .filter((line) => {
        const normalized = normalizeDriveName(line);
        return (
          normalized.includes("incorrect") ||
          normalized.includes("wrong") ||
          normalized.includes("not disabled") ||
          normalized.includes("not removed") ||
          normalized.includes("do not list") ||
          normalized.includes("do not repeat") ||
          normalized.includes("you cannot make mistakes") ||
          normalized.includes("you can not make mistakes") ||
          normalized.includes("that is not") ||
          normalized.includes("this is for record")
        );
      })
      .slice(-6);

    const turnLines = historyWindow.map((message, index) => {
      const attachmentSummary = summarizeMessageAttachmentsForContinuity(message);
      const attachmentNote = attachmentSummary ? ` Attachments: ${attachmentSummary}` : "";
      const modeLabel = getMessageModeForContinuity(message as any);
      const contentText = message.isModeChangeIndicator
        ? String(message.content || modeLabel)
        : clipContinuityText(message.content, message.role === "assistant" ? 1100 : 1300);

      return `${index + 1}. ${message.role.toUpperCase()} [${modeLabel}]: ${contentText || "[no text]"}${attachmentNote}`;
    });

    const packetSections = [
      "RODGERSPC CROSS-MODE CONTINUITY PACKET",
      "Purpose: preserve same-chat context when Joshua changes modes. The active mode must use the prior user inputs, screenshots, attachments, and previous mode outputs as current job context instead of treating the latest message as a fresh unrelated request.",
      `Current active mode: ${getModeDisplayName(activeMode)}.`,
      priorModeShifts.length
        ? `Recent mode shifts: ${priorModeShifts.join(" | ")}.`
        : "Recent mode shifts: none recorded.",
      "Continuity rules:",
      "- Read prior mode output as internal RodgersPC handoff context when it is relevant.",
      "- Joshua corrections override earlier assistant claims. If Joshua says an assistant claim was wrong, remove that claim from the active working facts instead of carrying it forward.",
      "- If Bench Tech analyzed a technical question, Lead Responder should convert that technical judgment into customer-safe wording instead of ignoring it.",
      "- If Lead Responder gathered customer/channel details, other modes should preserve those details when the task continues.",
      "- Use screenshots/attachments from earlier turns when their visual payload is still present in the current model request. If only attachment metadata remains, use only confirmed prior notes and do not claim fresh visual inspection.",
      "- Do not invent unsupported facts. If prior mode output is uncertain or corrected, carry the uncertainty/correction forward clearly.",
      "- Produce the deliverable requested by the current mode, but preserve the facts and conclusions already established in the chat.",
      recentCorrectionLines.length
        ? ["Recent user corrections / override instructions:", ...recentCorrectionLines.map((line) => `- ${line}`)].join("\n")
        : "Recent user corrections / override instructions: none detected.",
      recentAttachmentLines.length
        ? ["Recent attachment/visual context:", ...recentAttachmentLines].join("\n")
        : "Recent attachment/visual context: none listed in the recent window.",
      priorAssistantOutputs.length
        ? ["Recent assistant conclusions available for handoff:", ...priorAssistantOutputs.map((output, index) => `- Prior output ${index + 1}: ${output}`)].join("\n")
        : "Recent assistant conclusions available for handoff: none.",
      "Recent chat window:",
      ...turnLines,
      "END RODGERSPC CROSS-MODE CONTINUITY PACKET",
    ];

    return packetSections.join("\n").slice(0, 18000);
  };

  const isLeadOrFrontCounterDraftRequest = (
    prompt: string,
    activeMode: AppMode,
  ): boolean => {
    if (activeMode !== AppMode.LEAD_RESPONDER && activeMode !== AppMode.FRONT_DESK) {
      return false;
    }

    const normalized = normalizeDriveName(prompt);

    const asksForCustomerFacingOutput =
      normalized.includes("email") ||
      normalized.includes("reply") ||
      normalized.includes("respond") ||
      normalized.includes("response") ||
      normalized.includes("lead") ||
      normalized.includes("customer") ||
      normalized.includes("review") ||
      normalized.includes("yelp") ||
      normalized.includes("google review") ||
      normalized.includes("facebook") ||
      normalized.includes("nextdoor") ||
      normalized.includes("message back") ||
      normalized.includes("send to") ||
      normalized.includes("text response") ||
      normalized.includes("sms");

    const isOnlyShortTextRequest =
      normalized.includes("text response") ||
      normalized.includes("sms") ||
      normalized.includes("short text") ||
      normalized.includes("check his email") ||
      normalized.includes("check her email");

    return asksForCustomerFacingOutput && !isOnlyShortTextRequest;
  };

  const getEffectiveResponseLengthForRequest = (
    prompt: string,
    activeMode: AppMode,
    selectedLength: OutputLength,
  ): OutputLength => {
    // Do not let a global "Short" setting cripple copy-ready customer emails/replies.
    // Text/SMS remains short. Full/Long user choices remain respected.
    if (selectedLength === "long" || selectedLength === "full") return selectedLength;

    if (isLeadOrFrontCounterDraftRequest(prompt, activeMode)) {
      return selectedLength === "tight" || selectedLength === "short" ? "normal" : selectedLength;
    }

    return selectedLength;
  };


type ClientRequestRoutingProfile = {
  isLiveBenchRequest: boolean;
  isDataRecoveryRequest: boolean;
  isStorageRiskRequest: boolean;
  explicitRetailSourcingIntent: boolean;
  explicitSupportResearchIntent: boolean;
  explicitCurrentPublicInfoIntent: boolean;
  copyCommandIntent: boolean;
  copyScopeKnown: boolean;
  requestedRecoveryFolders: string[];
  webAllowedForRequest: boolean;
  requestAwareWebRoutingEnabled: boolean;
  retailSourcingBlockedForRequest: boolean;
  nextStepStyle: "one_safe_step" | "normal";
  reason: string;
};

  const getClientRequestRoutingProfile = (
    prompt: string,
    activeMode: AppMode,
  ): ClientRequestRoutingProfile => {
    const normalized = normalizeDriveName(prompt);

    const containsAny = (terms: string[]) => terms.some((term) => normalized.includes(normalizeDriveName(term)));

    const isBenchMode = activeMode === AppMode.TECH_ASSISTANT || activeMode === AppMode.FRONT_DESK;
    const isLiveBenchRequest =
      isBenchMode ||
      containsAny([
        "on the bench",
        "real job",
        "customer brought in",
        "customer device",
        "will not boot",
        "no boot",
        "not recognizing the drive",
        "bios",
        "uefi",
        "winre",
        "winpe",
        "installer cmd",
        "command prompt",
        "diskpart",
        "robocopy",
        "we are booted",
        "we are in business",
        "source drive",
        "target drive",
      ]);

    const isDataRecoveryRequest = containsAny([
      "data recovery",
      "recover data",
      "retrieve pictures",
      "retrieve documents",
      "copy files",
      "customer files",
      "user files",
      "documents folder",
      "pictures folder",
      "desktop documents pictures downloads",
      "onedrive local",
      "user profile",
      "full profile",
      "drive image",
      "image both drives",
      "clone the drive",
      "ddrescue",
    ]);

    const isStorageRiskRequest =
      isDataRecoveryRequest ||
      containsAny([
        "intel optane",
        "optane",
        "intel rst",
        "rst",
        "raid volume",
        "vmd",
        "m10 16gb",
        "no fixed disk",
        "no fixed disks",
        "drive not detected",
        "drive not recognized",
        "not recognizing the drive",
        "failing drive",
        "unstable drive",
        "ntuser",
        "appdata",
        "partition",
        "efi partition",
      ]);

    const explicitRetailSourcingIntent = containsAny([
      "price",
      "pricing",
      "quote a part",
      "source a part",
      "source the part",
      "find a part",
      "find me a part",
      "order",
      "buy",
      "amazon",
      "ebay",
      "vendor",
      "listing",
      "stock",
      "availability",
      "delivery",
      "replacement part",
    ]);

    const explicitSupportResearchIntent = containsAny([
      "bios recovery",
      "bios flash",
      "crisis recovery",
      "firmware recovery",
      "official support",
      "lenovo support",
      "driver",
      "rst driver",
      "manual",
      "look up to see",
      "lookup to see",
      "recover the bios",
      "flash or recover the bios",
    ]);

    const explicitCurrentPublicInfoIntent = containsAny([
      "current",
      "today",
      "latest",
      "now",
      "live",
      "real time",
      "real-time",
      "price",
      "stock",
      "availability",
      "delivery",
      "amazon",
      "ebay",
      "vendor",
      "official support",
      "driver",
      "firmware",
      "bios",
      "manual",
      "download link",
      "source link",
      "look up",
      "lookup",
      "search",
      "website",
    ]);

    const copyCommandIntent = containsAny([
      "robocopy",
      "copy",
      "copy command",
      "copy files",
      "copy folders",
      "transfer",
      "recover folders",
      "retrieve folders",
      "source",
      "target",
    ]);

    const requestedRecoveryFolders: string[] = [];
    if (containsAny(["desktop"])) requestedRecoveryFolders.push("Desktop");
    if (containsAny(["documents", "documents folder", "docs"])) requestedRecoveryFolders.push("Documents");
    if (containsAny(["pictures", "pictures folder", "photos"] )) requestedRecoveryFolders.push("Pictures");
    if (containsAny(["downloads", "downloads folder"])) requestedRecoveryFolders.push("Downloads");
    if (containsAny(["onedrive", "one drive"])) requestedRecoveryFolders.push("OneDrive local files");
    if (containsAny(["full profile", "whole profile", "everything", "entire profile"])) requestedRecoveryFolders.push("Full profile");
    if (containsAny(["drive image", "full image", "image the drive", "image both drives", "clone the drive"])) requestedRecoveryFolders.push("Full drive image");

    const copyScopeKnown = requestedRecoveryFolders.length > 0;
    const retailSourcingBlockedForRequest = (isLiveBenchRequest || isStorageRiskRequest || isDataRecoveryRequest) && !explicitRetailSourcingIntent;
    const requestAwareWebRoutingEnabled = true;
    const webAllowedForRequest = explicitCurrentPublicInfoIntent || explicitRetailSourcingIntent || explicitSupportResearchIntent;

    const reasons: string[] = [];
    if (isLiveBenchRequest) reasons.push("live bench context");
    if (isDataRecoveryRequest) reasons.push("data recovery context");
    if (isStorageRiskRequest) reasons.push("storage-risk context");
    if (retailSourcingBlockedForRequest) reasons.push("retail sourcing blocked unless Joshua explicitly asks to buy/source/price");
    if (explicitCurrentPublicInfoIntent) reasons.push("current public information may be needed; server WebRouter decides actual internet use");
    if (explicitSupportResearchIntent) reasons.push("support/driver/BIOS research may use official public web retrieval when needed");
    if (copyCommandIntent && !copyScopeKnown) reasons.push("copy command intent found but recovery folder scope is unknown");
    if (copyScopeKnown) reasons.push(`requested recovery folders: ${requestedRecoveryFolders.join(", ")}`);

    return {
      isLiveBenchRequest,
      isDataRecoveryRequest,
      isStorageRiskRequest,
      explicitRetailSourcingIntent,
      explicitSupportResearchIntent,
      explicitCurrentPublicInfoIntent,
      copyCommandIntent,
      copyScopeKnown,
      requestedRecoveryFolders,
      webAllowedForRequest,
      requestAwareWebRoutingEnabled,
      retailSourcingBlockedForRequest,
      nextStepStyle: isLiveBenchRequest || isDataRecoveryRequest || isStorageRiskRequest ? "one_safe_step" : "normal",
      reason: reasons.join("; ") || "normal request",
    };
  };

  const buildClientRequestRoutingPacket = (profile: ClientRequestRoutingProfile): string => {
    if (!profile.isLiveBenchRequest && !profile.isDataRecoveryRequest && !profile.isStorageRiskRequest) return "";

    const lines = [
      "RODGERSPC CLIENT REQUEST ROUTING SNAPSHOT",
      `Request class: ${profile.isDataRecoveryRequest ? "data recovery" : profile.isStorageRiskRequest ? "storage-risk bench work" : "live bench work"}.`,
      `Reason: ${profile.reason}.`,
      "Rules for this turn:",
      "- Treat this as real RodgersPC bench work first, not a generic shopping/search prompt.",
      "- Internet retrieval does not authorize retail sourcing. Do not search/buy/price parts unless Joshua explicitly asked for sourcing, pricing, stock, delivery, Amazon, eBay, vendor, or ordering.",
      "- If Joshua asks for BIOS/driver/manual/support research, use official support-style research only; do not convert the job into product shopping.",
      "- Preserve customer data first. Separate observation/read-only steps from write/destructive steps.",
      "- Do not suggest CHKDSK, Startup Repair, Reset this PC, System Restore, formatting, initializing, deleting partitions, clearing Optane/RST metadata, disabling/deconcatenating Optane, AHCI/RST switching, or BIOS flashing without a clear risk discussion and reason.",
      "- When Joshua is physically at the bench, give the next safe command only or the shortest safe command group. Do not dump long command blocks unless asked.",
    ];

    if (profile.copyCommandIntent || profile.isDataRecoveryRequest) {
      if (profile.copyScopeKnown) {
        lines.push(`- Recovery copy scope is known for this turn: ${profile.requestedRecoveryFolders.join(", ")}. Give commands only for those targets and in Joshua's priority order.`);
      } else {
        lines.push("- Before giving copy commands, ask what the customer actually needs recovered: Documents, Pictures, Desktop, Downloads, OneDrive local files, full profile, or full image.");
        lines.push("- Do not default to full user profile. Avoid NTUSER.DAT, AppData, registry hives, browser cache, temp files, and hidden profile internals unless full-profile recovery is explicitly chosen.");
      }
      lines.push("- For unstable drives, prefer /R:0 /W:0 so the copy does not hang on bad files.");
    }

    lines.push("END RODGERSPC CLIENT REQUEST ROUTING SNAPSHOT");
    return lines.join("\n");
  };

  const formatDiagnosticProgressEvent = (event: any, fallbackPrefix = "Server"): string => {
    const stage = String(event?.stage || event?.message || event?.event || "").trim();
    if (!stage) return "";

    const elapsedMs = Number(event?.elapsedMs || event?.durationMs || event?.elapsed || 0);
    const elapsedText = Number.isFinite(elapsedMs) && elapsedMs > 0
      ? ` (${Math.round(elapsedMs / 100) / 10}s)`
      : "";
    const level = String(event?.level || "info").toUpperCase();
    const code = event?.code ? ` code=${String(event.code)}` : "";
    const details = event?.details
      ? ` details=${JSON.stringify(event.details).slice(0, 600)}`
      : "";

    return `${fallbackPrefix}: [${level}] ${stage}${elapsedText}${code}${details}`;
  };

  const pushRequestProgressStep = (step: string) => {
    const cleanStep = String(step || "").trim();
    if (!cleanStep) return;
    setRequestProgressSteps((prev) => {
      if (prev[prev.length - 1] === cleanStep) return prev;
      const next = [...prev, cleanStep];
      return next.slice(-120);
    });
  };

  const startServerProgressPolling = (requestId: string, signal: AbortSignal): number => {
    let lastStageCount = 0;
    const poll = async () => {
      if (signal.aborted || activeChatRequestIdRef.current !== requestId) return;

      try {
        const response = await fetch(`/api/chat/progress/${encodeURIComponent(requestId)}`, {
          method: "GET",
          cache: "no-store",
          signal,
        });

        if (!response.ok) return;

        const data = await response.json();
        const events = Array.isArray(data?.events) ? data.events : [];
        if (events.length > lastStageCount) {
          events.slice(lastStageCount).forEach((event: any) => {
            const line = formatDiagnosticProgressEvent(event, "Server");
            if (line) pushRequestProgressStep(line);
          });
          lastStageCount = events.length;
        }
      } catch (err: any) {
        if (!signal.aborted) {
          // Progress polling is diagnostic only. Never fail the real chat request because polling missed.
          console.debug("[RodgersPC Progress] Poll skipped:", err?.message || err);
        }
      }
    };

    void poll();
    return window.setInterval(() => void poll(), 750);
  };

  // Primary messaging dispatch (Supports multi-file attachments)
  const handleSendMessage = async (
    content: string,
    attachments?: AttachedFile[] | string,
  ) => {
    const normalizedAttachments: AttachedFile[] = Array.isArray(attachments)
      ? attachments
      : typeof attachments === "string"
        ? [
            {
              id: `att_${Date.now()}`,
              name: capsuleAttachedFileName || "attached-image",
              type: "image",
              content: attachments,
              base64: attachments,
              mimeType: attachments.startsWith("data:")
                ? attachments.substring(5, attachments.indexOf(";"))
                : undefined,
            },
          ]
        : [];

    const userMsg: ChatMessage & { modeAtSend?: AppMode } = {
      id: `m_${Date.now()}`,
      role: "user",
      content: content,
      timestamp: new Date().toLocaleTimeString(),
      attachments: normalizedAttachments,
      modeAtSend: currentMode,
    };

    setMessages((prev) => [...prev, userMsg]);

    if (activeChatAbortControllerRef.current && !activeChatAbortControllerRef.current.signal.aborted) {
      activeChatAbortControllerRef.current.abort();
    }

    const requestId = `chat_request_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const abortController = new AbortController();
    activeChatRequestIdRef.current = requestId;
    activeChatAbortControllerRef.current = abortController;

    const isCurrentRequestActive = () =>
      activeChatRequestIdRef.current === requestId && !abortController.signal.aborted;

    const stopIfCancelled = () => {
      if (!isCurrentRequestActive()) {
        throw new DOMException("Chat request cancelled", "AbortError");
      }
    };

    setRequestProgressSteps([
      normalizedAttachments.length > 0
        ? `Captured ${normalizedAttachments.length} attachment/media payload${normalizedAttachments.length === 1 ? "" : "s"}.`
        : "Queued message for RodgersPC model dispatch.",
      driveSyncEnabled ? "Checking focused Drive context and evidence scope." : "Drive Sync is OFF; skipping Drive evidence.",
      "Web routing is request-aware; server will decide whether current public internet retrieval is required.",
    ]);
    setLoading(true);
    const progressPollTimer = startServerProgressPolling(requestId, abortController.signal);

    try {
      stopIfCancelled();
      // Create request messages stack. Keep the whole visible chat available, then add a
      // compact cross-mode continuity packet so mode switching behaves like one shared job brain.
      const chatStack = [...messages],
        userMsgClone = { ...userMsg };
      const crossModeContinuityPacket = buildCrossModeContinuityPacket(
        chatStack,
        userMsgClone,
        currentMode,
      );
      const clientRequestRoutingPacket = buildClientRequestRoutingPacket(
        getClientRequestRoutingProfile(content, currentMode),
      );
      pushRequestProgressStep("Building mode rules, safety routing, and continuity packet.");
      const transientRoutingMessages: ModelPayloadMessage[] = clientRequestRoutingPacket
        ? [
            {
              id: `routing_${Date.now()}`,
              role: "assistant",
              content: clientRequestRoutingPacket,
              imageUrl: undefined,
              attachments: [],
              isContinuityPacket: true,
              modeAtSend: currentMode,
            },
          ]
        : [];
      // Include current message with attachments payload so it context-grounds fully on server.
      // Extra mode fields are intentionally included for server-side continuity/handoff logic.
      const rawFinalStack: ModelPayloadMessage[] = [
        ...transientRoutingMessages,
        {
          id: `continuity_${Date.now()}`,
          role: "assistant",
          content: crossModeContinuityPacket,
          imageUrl: undefined,
          attachments: [],
          isContinuityPacket: true,
          modeAtSend: currentMode,
        },
        ...[...chatStack, userMsgClone].map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          imageUrl: m.imageUrl,
          imageAsset: m.imageAsset,
          imageAssets: m.imageAssets,
          attachments: m.attachments || [],
          modeAtSend: (m as ChatMessage & { modeAtSend?: AppMode }).modeAtSend,
          isModeChangeIndicator: m.isModeChangeIndicator,
          newMode: (m as ChatMessage & { newMode?: AppMode }).newMode,
          prevMode: (m as ChatMessage & { prevMode?: AppMode }).prevMode,
        })),
      ];

      const finalStack = buildModelPayloadMessages(rawFinalStack, userMsg.id);

      const normalizedPrompt = content.trim().toLowerCase();
      const clientRequestRoutingProfile = getClientRequestRoutingProfile(
        content,
        currentMode,
      );
      const effectiveSearchGroundingEnabled = true; // Phase 19: server WebRouter decides actual internet use from the request; this is no longer the UI toggle gate.
      const effectiveResponseLength = getEffectiveResponseLengthForRequest(
        content,
        currentMode,
        responseLength,
      );
      const effectiveLegacyAiVerbosity = outputLengthToVerbosity(effectiveResponseLength);

      let requestDriveToken: string | null = tokenRef.current;

      if (driveSyncEnabled) {
        requestDriveToken = await resolveDriveAccessToken("chat_submit_drive_evidence", {
          allowPopup: true,
          forceAccountChooser: true,
          showNotice: true,
        });
        stopIfCancelled();

        if (!requestDriveToken) {
          const blockedMsg: ChatMessage = {
            id: `ai_drive_block_${Date.now()}`,
            role: "assistant",
            content:
              "### Drive reconnect required\n\nDrive Sync is ON, but Google Drive access is missing or expired. I stopped the request instead of sending the model zero Drive evidence. Reconnect Drive/sign in again, then retry the message.",
            timestamp: new Date().toLocaleTimeString(),
            commands: [],
            reports: [],
          };

          setMessages((prev) => [...prev, blockedMsg]);
          return;
        }
      }

      const normalizeSearchText = (value: string): string => {
        return value
          .toLowerCase()
          .replace(/[’‘]/g, "'")
          .replace(/&/g, " and ")
          .replace(/[^a-z0-9]+/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      };

      const tokenDistance = (a: string, b: string): number => {
        const left = a.toLowerCase();
        const right = b.toLowerCase();

        if (left === right) return 0;
        if (!left.length) return right.length;
        if (!right.length) return left.length;

        const rows: number[][] = [];

        for (let i = 0; i <= left.length; i++) {
          rows[i] = [i];
        }

        for (let j = 1; j <= right.length; j++) {
          rows[0][j] = j;
        }

        for (let i = 1; i <= left.length; i++) {
          for (let j = 1; j <= right.length; j++) {
            const cost = left[i - 1] === right[j - 1] ? 0 : 1;
            rows[i][j] = Math.min(
              rows[i - 1][j] + 1,
              rows[i][j - 1] + 1,
              rows[i - 1][j - 1] + cost,
            );
          }
        }

        return rows[left.length][right.length];
      };

      const tokenSimilarity = (a: string, b: string): number => {
        if (!a || !b) return 0;
        if (a === b) return 1;

        const maxLen = Math.max(a.length, b.length);
        if (maxLen <= 2) return a === b ? 1 : 0;

        return 1 - tokenDistance(a, b) / maxLen;
      };

      const stopWords = new Set([
        "the",
        "and",
        "for",
        "with",
        "you",
        "that",
        "this",
        "our",
        "are",
        "what",
        "how",
        "why",
        "about",
        "need",
        "should",
        "your",
        "does",
        "have",
        "from",
        "into",
        "when",
        "then",
        "will",
        "would",
        "could",
        "there",
        "their",
        "them",
        "they",
        "tell",
        "give",
        "show",
        "find",
        "look",
        "lookup",
        "pull",
        "load",
        "read",
        "review",
        "ended",
        "ending",
        "doing",
        "done",
        "did",
        "was",
        "were",
        "had",
        "has",
        "been",
        "all",
        "any",
        "some",
        "same",
        "specific",
        "response",
      ]);

      const weakDriveTerms = new Set([
        "drive",
        "file",
        "files",
        "folder",
        "folders",
        "focused",
        "loaded",
        "content",
        "context",
        "reviewed",
        "read",
        "audit",
        "metadata",
      ]);

      const genericBusinessTerms = new Set([
        "job",
        "jobs",
        "log",
        "logs",
        "history",
        "historical",
        "record",
        "records",
        "customer",
        "customers",
        "client",
        "clients",
        "repair",
        "repairs",
        "work",
        "worked",
        "working",
        "shop",
        "rodgerspc",
        "command",
        "center",
      ]);

      const promptTokens = normalizeSearchText(content)
        .split(/\s+/)
        .map((w) => w.trim())
        .filter((w) => w.length > 1);

      const retrievalTokens = promptTokens.filter(
        (w) =>
          w.length > 2 &&
          !stopWords.has(w) &&
          !weakDriveTerms.has(w) &&
          !genericBusinessTerms.has(w),
      );

      const fallbackRetrievalTokens = promptTokens.filter(
        (w) =>
          w.length > 3 &&
          !stopWords.has(w) &&
          !weakDriveTerms.has(w),
      );

      const searchTokens =
        retrievalTokens.length > 0 ? retrievalTokens : fallbackRetrievalTokens;

      const searchPhrases: string[] = [];
      for (let i = 0; i < searchTokens.length - 1; i++) {
        searchPhrases.push(`${searchTokens[i]} ${searchTokens[i + 1]}`);
      }

      const ensureDriveContentForRequest = async (
        file: WorkspaceItem,
        engine: "immediate" | "background" | "source_of_truth_preload" = "background",
      ): Promise<DriveLoadedContent | null> => {
        stopIfCancelled();
        const existingLoaded = getNodeLoadedContent(file);
        if (existingLoaded) return existingLoaded;
        if (!requestDriveToken || !driveSyncEnabled) {
          driveLoadFailures.push({
            file,
            engine,
            error: !requestDriveToken
              ? "missing_google_access_token — Missing Google Drive access token. Reconnect Drive."
              : "drive_sync_off",
          });
          return null;
        }

        setItems((prev) => {
          const markLoading = (currNodes: WorkspaceItem[]): WorkspaceItem[] => {
            return currNodes.map((node) => {
              if (node.id === file.id) {
                return { ...node, indexingStatus: "loading" as const };
              }
              if (node.type === "folder" && node.children) {
                return { ...node, children: markLoading(node.children) };
              }
              return node;
            });
          };
          return markLoading(prev);
        });

        let lastError: unknown = null;

        for (let attempt = 1; attempt <= 2; attempt++) {
          try {
            const activeToken = requestDriveToken || await resolveDriveAccessToken("chat_drive_evidence_load", {
              allowPopup: true,
              forceAccountChooser: true,
              showNotice: true,
            });

            if (!activeToken) {
              throw new Error("missing_google_access_token");
            }

            const loaded = await downloadDriveLoadedContentWithReconnect(
              activeToken,
              file.id,
              file.name,
              file.path,
              "chat_drive_evidence_load",
            );
            stopIfCancelled();

            setItems((prev) => {
              const updateFileContent = (
                currNodes: WorkspaceItem[],
              ): WorkspaceItem[] => {
                return currNodes.map((node) => {
                  if (node.id === file.id) {
                    return applyLoadedDriveContentToNode(node, loaded);
                  }
                  if (node.type === "folder" && node.children) {
                    return {
                      ...node,
                      children: updateFileContent(node.children),
                    };
                  }
                  return node;
                });
              };
              return updateFileContent(prev);
            });

            return loaded;
          } catch (err) {
            lastError = err;
            console.warn("[Drive Brain] On-demand file retrieval failed", {
              path: file.path,
              attempt,
              engine,
              error: err,
            });

            if (attempt < 2) {
              await new Promise((resolve) => window.setTimeout(resolve, 300));
            }
          }
        }

        const errorText = normalizeDriveLoadError(lastError);
        driveLoadFailures.push({ file, engine, error: errorText });

        setItems((prev) => {
          const markFailed = (currNodes: WorkspaceItem[]): WorkspaceItem[] => {
            return currNodes.map((node) => {
              if (node.id === file.id) {
                return {
                  ...node,
                  indexingStatus: "failed" as const,
                  extractionStatus: "failed",
                  extractionWarnings: [errorText],
                };
              }
              if (node.type === "folder" && node.children) {
                return { ...node, children: markFailed(node.children) };
              }
              return node;
            });
          };
          return markFailed(prev);
        });

        return null;
      };

      const scoreFileForPrompt = (file: WorkspaceItem): number => {
        if (searchTokens.length === 0) return 0;

        let score = 0;
        const normalizedName = normalizeSearchText(file.name || "");
        const normalizedPath = normalizeSearchText(file.path || "");
        const nameTokens = normalizedName.split(/\s+/).filter(Boolean);
        const pathTokens = normalizedPath.split(/\s+/).filter(Boolean);
        const searchableTokens = [...new Set([...nameTokens, ...pathTokens])];

        searchPhrases.forEach((phrase) => {
          if (normalizedName.includes(phrase)) score += 180;
          if (normalizedPath.includes(phrase)) score += 120;
        });

        searchTokens.forEach((token) => {
          if (!token) return;

          if (normalizedName.includes(token)) score += 70;
          if (normalizedPath.includes(token)) score += 45;

          let bestSimilarity = 0;
          searchableTokens.forEach((candidate) => {
            if (candidate.length < 3) return;
            bestSimilarity = Math.max(bestSimilarity, tokenSimilarity(token, candidate));
          });

          if (bestSimilarity >= 0.92) score += 55;
          else if (bestSimilarity >= 0.84) score += 35;
          else if (bestSimilarity >= 0.76 && token.length >= 5) score += 18;
        });

        const exactTokenMatches = searchTokens.filter((token) =>
          searchableTokens.some((candidate) => candidate === token),
        ).length;

        const fuzzyTokenMatches = searchTokens.filter((token) =>
          searchableTokens.some((candidate) => tokenSimilarity(token, candidate) >= 0.84),
        ).length;

        if (searchTokens.length >= 2) {
          if (exactTokenMatches >= 2) score += 220;
          else if (fuzzyTokenMatches >= 2) score += 140;
        }

        if (searchTokens.length === 1 && (exactTokenMatches > 0 || fuzzyTokenMatches > 0)) {
          score += 60;
        }

        const contentLower = normalizeSearchText(
          file.loadedContent?.content || file.content || "",
        );
        if (contentLower) {
          searchTokens.forEach((token) => {
            if (!token) return;
            let idx = contentLower.indexOf(token);
            let count = 0;
            while (idx !== -1 && count < 12) {
              count++;
              score += 8;
              idx = contentLower.indexOf(token, idx + token.length);
            }
          });
        }

        const explicitScore = scoreExplicitDriveRecordMatch(file, content);
        if (explicitScore > 0) {
          score += explicitScore;
        }

        return score;
      };

      const allDriveFiles = driveSyncEnabled ? getAllDriveFiles(items) : [];
      const focusedDriveFiles = driveSyncEnabled ? getFocusedDriveFiles(items) : [];
      const activeFilePayloads: ActiveContextPayload[] = [];
      const explicitDriveIntent = getExplicitDriveRecordIntent(content);
      const loadedFileIds = new Set<string>();
      const retrievalHits: { file: WorkspaceItem; score: number; engine: "immediate" | "background" }[] = [];
      const driveLoadFailures: { file: WorkspaceItem; error: string; engine: "immediate" | "background" | "source_of_truth_preload" }[] = [];

      const pushLoadedDrivePayload = (
        file: WorkspaceItem,
        loaded: DriveLoadedContent,
        sourceOverride?: "source_of_truth" | "focused_drive",
        retrievalEngine: "immediate_request" | "background_drive_brain" = "background_drive_brain",
      ) => {
        if (loadedFileIds.has(file.id)) return;

        const evidenceStatus = getDriveEvidenceLabel(loaded);

        loadedFileIds.add(file.id);
        activeFilePayloads.push({
          path: loaded.path || file.path,
          name: loaded.name || file.name,
          size: loaded.size || file.size,
          // Keep activeContext.content as actual extracted text only.
          // Visual-only files travel through images[]; metadata/extraction notes go in the evidence summary.
          // This prevents fallback sentences from being counted as readable Drive evidence.
          content: loaded.content || "",
          fileId: loaded.fileId || file.id,
          mimeType: loaded.mimeType || file.mimeType,
          source: sourceOverride || (file.sourceOfTruth ? "source_of_truth" : "focused_drive"),
          sourceType: loaded.sourceType,
          images: loaded.images || [],
          extractionStatus: loaded.extractionStatus,
          extractionWarnings: loaded.extractionWarnings || [],
          evidenceStatus: evidenceStatus.evidenceLabel,
          hasReadableText: evidenceStatus.hasReadableText,
          visualPayloadCount: evidenceStatus.visualPayloadCount,
          canClaimVisualInspection: evidenceStatus.canClaimVisualInspection,
          retrievalEngine,
          evidenceLoadedAt: new Date().toISOString(),
        } as ActiveContextPayload & {
          evidenceStatus?: string;
          hasReadableText?: boolean;
          visualPayloadCount?: number;
          canClaimVisualInspection?: boolean;
          retrievalEngine?: "immediate_request" | "background_drive_brain";
          evidenceLoadedAt?: string;
        });
      };

      const isDriveAuditPrompt =
        normalizedPrompt.includes("what drive files") ||
        normalizedPrompt.includes("actually loaded") ||
        normalizedPrompt.includes("loaded as content") ||
        normalizedPrompt.includes("loaded/read") ||
        normalizedPrompt.includes("focused drive data") ||
        normalizedPrompt.includes("focused drive files") ||
        normalizedPrompt.includes("not reviewed") ||
        normalizedPrompt.includes("not read") ||
        normalizedPrompt.includes("tell me what drive") ||
        normalizedPrompt.includes("drive files are actually") ||
        normalizedPrompt.includes("do not claim you reviewed");

      const isFullDriveAuditPrompt =
        normalizedPrompt.includes("show full focused drive file audit") ||
        normalizedPrompt.includes("full focused drive audit") ||
        normalizedPrompt.includes("every focused file path");

      const isSimpleSmokePrompt = ["test", "hi", "hello", "ping"].includes(normalizedPrompt);

      const looksLikeDriveNeeded =
        driveSyncEnabled &&
        searchTokens.length > 0 &&
        !isSimpleSmokePrompt &&
        !isDriveAuditPrompt;

      const sourceFiles = focusedDriveFiles.filter((file) => file.sourceOfTruth);
      const nonSourceFocusedFiles = focusedDriveFiles.filter((file) => !file.sourceOfTruth);

      const immediateRequestCandidates = explicitDriveIntent.isExplicitDriveLookup
        ? allDriveFiles
            .map((file) => {
              const explicitScore = scoreExplicitDriveRecordMatch(file, content);

              // Critical: Source-of-Truth / checked status is only a tie-breaker after a real
              // prompt-to-file match. Do not let generic Source-of-Truth membership pull every
              // checked policy file into the request or claim it was loaded.
              const contextBonus = explicitScore > 0
                ? (file.checked ? 90 : 0) +
                  (file.sourceOfTruth ? 120 : 0) +
                  (isCanonicalSourceOfTruthItem(file) ? 120 : 0)
                : 0;

              return {
                file,
                explicitScore,
                score: explicitScore + contextBonus,
              };
            })
            .filter((item) => item.explicitScore > 0 && item.score >= 180)
            .sort((a, b) => {
              if (b.score !== a.score) return b.score - a.score;
              if (a.file.sourceOfTruth !== b.file.sourceOfTruth) {
                return a.file.sourceOfTruth ? -1 : 1;
              }
              if (a.file.checked !== b.file.checked) {
                return a.file.checked ? -1 : 1;
              }
              return a.file.path.length - b.file.path.length;
            })
            .slice(0, explicitDriveIntent.asksForVisual ? 4 : 5)
        : [];

      if (immediateRequestCandidates.length > 0) {
        retrievalHits.push(
          ...immediateRequestCandidates.map((hit) => ({ ...hit, engine: "immediate" as const })),
        );

        // Immediate Drive Request Engine: direct file fetch for the user's current request.
        // This is intentionally separate from the background Drive Brain preload/cache path.
        for (const hit of immediateRequestCandidates) {
          const loaded = await ensureDriveContentForRequest(hit.file, "immediate");
          if (loaded) {
            pushLoadedDrivePayload(
              hit.file,
              loaded,
              hit.file.sourceOfTruth || isCanonicalSourceOfTruthItem(hit.file)
                ? "source_of_truth"
                : "focused_drive",
              "immediate_request",
            );
          }
        }
      }

      // Background Drive Brain context: use already-loaded Source of Truth cache without blocking the request.
      for (const item of sourceFiles) {
        if (loadedFileIds.has(item.id)) continue;
        const existingLoaded = getNodeLoadedContent(item);
        if (existingLoaded) {
          pushLoadedDrivePayload(item, existingLoaded, "source_of_truth", "background_drive_brain");
        }
      }

      // Blog Engine needs the actual prior blog library, not just folder/file metadata.
      // For topic planning, draft continuation, and duplicate-topic prevention, load the focused
      // published blog archive before the model answers.
      const isBlogEngineLibraryRequest =
        currentMode === AppMode.BLOG_ENGINE &&
        driveSyncEnabled &&
        !isSimpleSmokePrompt &&
        !isDriveAuditPrompt;

      if (isBlogEngineLibraryRequest) {
        const blogLibraryCandidates = nonSourceFocusedFiles
          .filter((file) => !loadedFileIds.has(file.id))
          .filter((file) => {
            const haystack = normalizeSearchText(`${file.path || ""} ${file.name || ""}`);
            return (
              haystack.includes("final published blog post") ||
              haystack.includes("published blog post") ||
              haystack.includes("blog engine chat logs") ||
              haystack.includes("blog") ||
              /^\d{1,2}\s+\d{1,2}\s+\d{2,4}/.test(haystack)
            );
          })
          .sort((a, b) => {
            const aPath = normalizeSearchText(`${a.path || ""} ${a.name || ""}`);
            const bPath = normalizeSearchText(`${b.path || ""} ${b.name || ""}`);

            const aPublished = aPath.includes("final published blog post") ? 1 : 0;
            const bPublished = bPath.includes("final published blog post") ? 1 : 0;
            if (aPublished !== bPublished) return bPublished - aPublished;

            return Number(b.size || 0) - Number(a.size || 0);
          })
          .slice(0, 10);

        retrievalHits.push(
          ...blogLibraryCandidates.map((file) => ({
            file,
            score: 999,
            engine: "background" as const,
          })),
        );

        for (const file of blogLibraryCandidates) {
          const loaded = await ensureDriveContentForRequest(file, "background");
          if (loaded) {
            pushLoadedDrivePayload(file, loaded, "focused_drive", "background_drive_brain");
          }
        }
      }

      const isCreativeBrandKitRequest =
        driveSyncEnabled &&
        isBrandCreativeMode(currentMode) &&
        !isSimpleSmokePrompt &&
        !isDriveAuditPrompt;

      const wantsCreativeImageNow =
        isCreativeBrandKitRequest &&
        imageGenerationEnabled &&
        (
          normalizedPrompt.includes("image") ||
          normalizedPrompt.includes("picture") ||
          normalizedPrompt.includes("og") ||
          normalizedPrompt.includes("thumbnail") ||
          normalizedPrompt.includes("visual") ||
          normalizedPrompt.includes("graphic") ||
          normalizedPrompt.includes("generate") ||
          normalizedPrompt.includes("make it") ||
          normalizedPrompt.includes("make the")
        );

      if (isCreativeBrandKitRequest) {
        const allBrandKitFiles = allDriveFiles
          .filter((file) => !loadedFileIds.has(file.id))
          .filter((file) => isBrandKitRuleFile(file) || (wantsCreativeImageNow && isBrandKitVisualReferenceFile(file)))
          .map((file) => ({
            file,
            score: scoreBrandKitFileForCreativeWork(file, wantsCreativeImageNow),
          }))
          .filter((hit) => hit.score > 0)
          .sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            return Number(b.file.size || 0) - Number(a.file.size || 0);
          })
          // Keep this capped. Brand Kit images are useful, but too many large images can blow up the request.
          .slice(0, wantsCreativeImageNow ? 8 : 7);

        retrievalHits.push(
          ...allBrandKitFiles.map((hit) => ({
            file: hit.file,
            score: hit.score,
            engine: "background" as const,
          })),
        );

        for (const hit of allBrandKitFiles) {
          const loaded = await ensureDriveContentForRequest(hit.file, "background");
          if (loaded) {
            pushLoadedDrivePayload(hit.file, loaded, "focused_drive", "background_drive_brain");
          }
        }
      }

      if (looksLikeDriveNeeded) {
        const rankedFocusedFiles = nonSourceFocusedFiles
          .filter((file) => !loadedFileIds.has(file.id))
          .map((file) => ({ file, score: scoreFileForPrompt(file) }))
          .filter((item) => item.score >= 60)
          .sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            return a.file.path.length - b.file.path.length;
          })
          .slice(0, 8);

        retrievalHits.push(
          ...rankedFocusedFiles.map((hit) => ({ ...hit, engine: "background" as const })),
        );

        for (const hit of rankedFocusedFiles) {
          const loaded = await ensureDriveContentForRequest(hit.file, "background");
          if (loaded) {
            pushLoadedDrivePayload(hit.file, loaded, "focused_drive", "background_drive_brain");
          }
        }
      }

      stopIfCancelled();

      const safeActiveFilePayloads = buildDriveChatPayload(
        activeFilePayloads as DriveLoadedContent[],
        content,
        {
          maxFiles: wantsCreativeImageNow ? 14 : currentMode === AppMode.BLOG_ENGINE ? 18 : 24,
          maxTotalTextChars: wantsCreativeImageNow ? 65000 : currentMode === AppMode.BLOG_ENGINE ? 90000 : 110000,
          maxVisualPayloads: wantsCreativeImageNow ? 5 : 3,
          maxSingleBase64Chars: 1800000,
          maxTotalBase64Chars: wantsCreativeImageNow ? 6500000 : 3500000,
        },
      ) as ActiveContextPayload[];

      const inlineSentDriveFileIds = new Set(
        safeActiveFilePayloads
          .map((file) => file.fileId)
          .filter(Boolean) as string[],
      );

      const focusedButNotLoadedFiles = focusedDriveFiles.filter(
        (file) => !inlineSentDriveFileIds.has(file.id),
      );

      const getTopFocusedFolders = (nodes: WorkspaceItem[]): WorkspaceItem[] => {
        const folders: WorkspaceItem[] = [];

        const walk = (nodeList: WorkspaceItem[], ancestorFocused = false) => {
          nodeList.forEach((node) => {
            const isFocusedFolder = node.type === "folder" && Boolean(node.checked);

            if (isFocusedFolder && !ancestorFocused) {
              folders.push(node);
              return;
            }

            if (node.children) {
              walk(node.children, ancestorFocused || isFocusedFolder);
            }
          });
        };

        walk(nodes);
        return folders;
      };

      const getFocusedFolderFileCount = (folder: WorkspaceItem): number => {
        let count = 0;

        const walk = (nodes: WorkspaceItem[] = []) => {
          nodes.forEach((node) => {
            if (node.type === "file" && node.checked && !loadedFileIds.has(node.id)) {
              count++;
            }

            if (node.children) {
              walk(node.children);
            }
          });
        };

        walk(folder.children || []);
        return count;
      };

      const requestDriveEvidenceSummary = {
        driveSyncEnabled,
        loadedDriveFileCount: safeActiveFilePayloads.length,
        loadedDriveTextFileCount: safeActiveFilePayloads.filter((file) =>
          Boolean(file.content && file.content.trim()),
        ).length,
        loadedDriveVisualPayloadCount: countDriveVisualPayloads(safeActiveFilePayloads),
        originalLoadedDriveFileCountBeforePayloadSafety: activeFilePayloads.length,
        payloadSafetyCapApplied: safeActiveFilePayloads.length < activeFilePayloads.length,
        canClaimVisualInspectionFileCount: safeActiveFilePayloads.filter((file) => {
          const typedFile = file as ActiveContextPayload & { canClaimVisualInspection?: boolean };
          return Boolean(typedFile.canClaimVisualInspection || (file.images?.length || 0) > 0);
        }).length,
        loadedDriveFiles: safeActiveFilePayloads.map((file) => {
          const typedFile = file as ActiveContextPayload & {
            evidenceStatus?: string;
            visualPayloadCount?: number;
            hasReadableText?: boolean;
            canClaimVisualInspection?: boolean;
            retrievalEngine?: string;
          };

          return {
            fileId: file.fileId,
            name: file.name,
            path: file.path,
            source: file.source,
            sourceType: file.sourceType,
            mimeType: file.mimeType,
            extractionStatus: file.extractionStatus,
            extractionWarnings: file.extractionWarnings || [],
            evidenceStatus:
              typedFile.evidenceStatus ||
              ((file.images?.length || 0) > 0
                ? "visual_payload_loaded"
                : file.content && file.content.trim()
                  ? "text_loaded"
                  : "loaded_no_readable_content"),
            hasReadableText: Boolean(typedFile.hasReadableText || (file.content && file.content.trim())),
            visualPayloadCount: typedFile.visualPayloadCount ?? (file.images?.length || 0),
            canClaimVisualInspection:
              Boolean(typedFile.canClaimVisualInspection) || (file.images?.length || 0) > 0,
            retrievalEngine: typedFile.retrievalEngine || "unknown",
          };
        }),
        failedDriveLoadCount: driveLoadFailures.length,
        failedDriveLoads: driveLoadFailures.map((failure) => ({
          fileId: failure.file.id,
          name: failure.file.name,
          path: failure.file.path,
          engine: failure.engine,
          error: failure.error,
        })),
        metadataOnlyFocusedFileCount: focusedDriveFiles.filter(
          (file) => !loadedFileIds.has(file.id),
        ).length,
      };

      const focusedMetadata: string[] = [];

      focusedMetadata.push("DRIVE BRAIN / FOCUS PACKAGE");
      focusedMetadata.push("");
      focusedMetadata.push("Loaded/read Drive files for this request:");
      if (safeActiveFilePayloads.length > 0) {
        safeActiveFilePayloads.forEach((file) => {
          const typedFile = file as ActiveContextPayload & {
            evidenceStatus?: string;
            visualPayloadCount?: number;
            hasReadableText?: boolean;
            canClaimVisualInspection?: boolean;
            retrievalEngine?: string;
          };
          const imageCount = typedFile.visualPayloadCount ?? (file.images?.length || 0);
          const status = file.extractionStatus ? `; extraction=${file.extractionStatus}` : "";
          const textNote = typedFile.hasReadableText || (file.content && file.content.trim()) ? "; text=loaded" : "; text=not_extracted";
          const imageNote = imageCount > 0 ? `; vision payloads=${imageCount}` : "; vision payloads=0";
          const evidenceNote = typedFile.evidenceStatus ? `; evidence=${typedFile.evidenceStatus}` : "";
          const engineNote = typedFile.retrievalEngine ? `; engine=${typedFile.retrievalEngine}` : "";
          const claimNote = imageCount > 0 ? "; visual-inspection-claim=allowed" : "; visual-inspection-claim=not_allowed";
          focusedMetadata.push(`- ${file.path}${status}${textNote}${imageNote}${evidenceNote}${engineNote}${claimNote}`);
        });
      } else {
        focusedMetadata.push("- None");
      }

      focusedMetadata.push("");
      focusedMetadata.push("Drive load failures for this request:");
      if (driveLoadFailures.length > 0) {
        driveLoadFailures.forEach((failure) => {
          focusedMetadata.push(`- ${failure.file.path || failure.file.name} (${failure.engine}): ${failure.error}`);
        });
      } else {
        focusedMetadata.push("- None");
      }

      focusedMetadata.push("");
      focusedMetadata.push("Focused Drive retrieval status:");
      if (explicitDriveIntent.isExplicitDriveLookup) {
        if (immediateRequestCandidates.length > 0) {
          focusedMetadata.push("- Explicit Drive record lookup detected. Immediate Request Drive Engine attempted to load the strongest matching Drive files before the model response.");
          immediateRequestCandidates.forEach((hit) => {
            const wasLoaded = loadedFileIds.has(hit.file.id);
            focusedMetadata.push(`- Immediate retrieval candidate (${hit.score}; ${wasLoaded ? "loaded" : "not loaded"}): ${hit.file.path}`);
          });
        } else {
          focusedMetadata.push("- Explicit Drive record lookup detected, but no Drive file name/path confidently matched the requested record in the currently loaded tree metadata.");
        }
      }
      if (isDriveAuditPrompt) {
        focusedMetadata.push("- Audit prompt detected. Only Source of Truth was loaded/read.");
      } else if (looksLikeDriveNeeded && retrievalHits.length > 0) {
        focusedMetadata.push("- Focused Drive brain search ran for this prompt.");
        retrievalHits.forEach((hit) => {
          const wasLoaded = loadedFileIds.has(hit.file.id);
          focusedMetadata.push(`- Retrieval match (${hit.score}; ${hit.engine} engine; ${wasLoaded ? "loaded" : "not loaded"}): ${hit.file.path}`);
        });
      } else if (looksLikeDriveNeeded && retrievalHits.length === 0) {
        focusedMetadata.push(
          `- Focused Drive brain search ran, but no confident file match was found for: ${searchTokens.join(", ")}`,
        );
      } else if (isSimpleSmokePrompt) {
        focusedMetadata.push("- Simple smoke-test prompt. Non-source Drive retrieval skipped.");
      } else {
        focusedMetadata.push("- Non-source Drive retrieval skipped.");
      }

      focusedMetadata.push("");
      focusedMetadata.push("Focused but not loaded/read Drive data:");
      if (focusedButNotLoadedFiles.length > 0) {
        if (isDriveAuditPrompt && !isFullDriveAuditPrompt) {
          const topFocusedFolders = getTopFocusedFolders(items).filter(
            (folder) => !folder.sourceOfTruth,
          );

          if (topFocusedFolders.length > 0) {
            topFocusedFolders.forEach((folder) => {
              const fileCount = getFocusedFolderFileCount(folder);
              focusedMetadata.push(
                `- ${folder.path} (${fileCount} focused files available, not loaded/read for this response)`,
              );
            });
          } else {
            focusedMetadata.push(
              `- ${focusedButNotLoadedFiles.length} focused non-source Drive files available, not loaded/read for this response.`,
            );
          }

          focusedMetadata.push(
            "- Full focused file list intentionally hidden from audit prompts to keep the response readable.",
          );
          focusedMetadata.push(
            '- To see every focused file path, ask exactly: "show full focused Drive file audit."',
          );
        } else if (isFullDriveAuditPrompt) {
          focusedButNotLoadedFiles.forEach((file) => {
            focusedMetadata.push(`- ${file.path}`);
          });
        } else {
          focusedMetadata.push(
            `- ${focusedButNotLoadedFiles.length} focused files remain available as RodgersPC brain data but were not loaded/read for this response.`,
          );
        }
      } else {
        focusedMetadata.push("- None");
      }

      focusedMetadata.push("");
      focusedMetadata.push(
        `Drive retrieval for this prompt: ${
          isDriveAuditPrompt
            ? "audit prompt — Source of Truth only; non-source focused files intentionally not retrieved"
            : looksLikeDriveNeeded
              ? retrievalHits.length > 0
                ? "enabled; matching focused Drive files retrieved"
                : "enabled; no confident focused Drive match found"
              : "skipped"
        }`,
      );
      focusedMetadata.push("");
      focusedMetadata.push("Evidence contract for this request:");
      focusedMetadata.push("- Files listed under Loaded/read are the only Drive files whose contents were supplied as evidence.");
      focusedMetadata.push("- Focused metadata, folder names, and file names are locators only. They are not evidence for policy terms, prices, warranty coverage, dates, or customer-facing claims.");
      focusedMetadata.push("- A model may say it read/summarized a Drive file only when that file appears under Loaded/read.");
      focusedMetadata.push("- A model may say it visually inspected a Drive image/PDF only when that same file line shows vision payloads greater than 0 and visual-inspection-claim=allowed.");
      focusedMetadata.push("- If a policy/warranty/price/date/rule is not clearly supported by loaded text or loaded visual payload evidence, the answer must mark it unverified instead of interpreting it.");
      focusedMetadata.push("- For policy records, preserve date ranges, prices, caps, labor terms, and exclusions exactly. Do not calculate new total durations unless the loaded source explicitly states that total.");
      focusedMetadata.push("- Keep separate source ranges separate. Do not move a first-period rule into a later-period rule or a later-period labor rule outside its source range.");
      focusedMetadata.push(
        "Important rule: checked/gold Drive data is the RodgersPC brain. Files listed under Loaded/read are actual content supplied to the model. Focused Drive data is available knowledge metadata unless it also appears under Loaded/read. Immediate request-loaded files are prioritized ahead of background Drive Brain cache.",
      );
      focusedMetadata.push(
        "Vision rule: if a Loaded/read Drive file lists vision payloads, those images/PDF visual payloads were extracted and sent with activeContext for the server to pass to the model. Otherwise visual inspection must not be claimed.",
      );

      const searchScopeMetadataStr = focusedMetadata.join("\n");

      stopIfCancelled();
      pushRequestProgressStep(
        driveSyncEnabled
          ? `Sending request with ${safeActiveFilePayloads.length} loaded Drive evidence file${safeActiveFilePayloads.length === 1 ? "" : "s"}.`
          : "Sending request to model without Drive evidence."
      );

      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: abortController.signal,
        body: JSON.stringify({
          clientRequestId: requestId,
          messages: finalStack,
          activeContext: driveSyncEnabled ? safeActiveFilePayloads : [],
          searchScopeMetadata: driveSyncEnabled ? searchScopeMetadataStr : buildDriveOffScopeMetadata(),
          mode: currentMode,
          searchGroundingEnabled: effectiveSearchGroundingEnabled,
          userSelectedSearchGroundingEnabled: searchGroundingEnabled,
          requestAwareWebRoutingEnabled: true,
          clientRequestRouting: clientRequestRoutingProfile,
          imageGenerationEnabled: imageGenerationEnabled,
          driveSyncEnabled: driveSyncEnabled,
          fastModeEnabled: fastModeEnabled,
          aiTemperature: aiTemperature,
          aiBehavior: accuracyMode, // Backward compatibility mapping
          aiVerbosity: effectiveLegacyAiVerbosity, // Adaptive output length mapped for backward compatibility
          accuracyMode,
          responseLength: effectiveResponseLength,
          selectedResponseLength: responseLength,
          crossModeContinuityPacket,
          jobSensitivity,
          aiPreset,
          driveEvidenceSummary: requestDriveEvidenceSummary,
        }),
      });

      stopIfCancelled();
      pushRequestProgressStep("Server responded; validating JSON and model payload.");

      const apiPayload = await readApiResponseSafely(response);
      const data = apiPayload.data || {};

      const serverDiagnosticLog = Array.isArray(data?.diagnosticLog) ? data.diagnosticLog : [];
      if (serverDiagnosticLog.length > 0) {
        serverDiagnosticLog.forEach((event: any) => {
          const line = formatDiagnosticProgressEvent(event, "WebDiag");
          if (line) pushRequestProgressStep(line);
        });
      }

      if (response.ok && apiPayload.contentType.toLowerCase().includes("application/json") && !data?.error) {
        stopIfCancelled();
        const imageAsset = buildGeneratedImageAssetFromResponse(data);
        const renderImageUrl = getRenderableImageFromAsset(imageAsset) || (isRenderableImageUrl(data.imageUrl) ? data.imageUrl : undefined);
        const aiMsg = {
          id: `ai_${Date.now()}`,
          role: "assistant",
          content: stripAutomaticReportBlocksFromAssistantText(data.text || "Diagnostic compilation completed empty."),
          timestamp: new Date().toLocaleTimeString(),
          commands: data.commands || [],
          reports: [],
          imageUrl: renderImageUrl,
          imageAsset,
          imageAssets: imageAsset ? [imageAsset] : data.imageAssets,
          modeAtSend: currentMode,
          driveEvidenceSummary: data.driveEvidenceSummary || requestDriveEvidenceSummary,
          diagnosticLog: serverDiagnosticLog,
        } as ChatMessage & { driveEvidenceSummary?: typeof requestDriveEvidenceSummary; modeAtSend?: AppMode; diagnosticLog?: any[] };
        pushRequestProgressStep("Model answer received; rendering response in chat.");
        if (isCurrentRequestActive()) {
          setMessages((prev) => [...prev, aiMsg]);
        }
      } else {
        const errorText = String(data?.error || data?.message || data?.rawText || apiPayload.rawText || "Unknown Server Connection Error");
        let userFacingError = errorText;
        if (
          response.status === 413 ||
          errorText.toLowerCase().includes("too large") ||
          errorText.toLowerCase().includes("413")
        ) {
          userFacingError = `### ❌ REQUEST PAYLOAD TOO LARGE (HTTP 413)

The app still sent more Drive/image/document data than the server accepted. This is a payload-packing bug, not a Drive-focus problem.

**Action Item:** Keep Drive scope as-is. The fix belongs in request evidence caps/document packing, not manual Drive reduction.`;
        } else if (
          isLikelyHtmlResponse(apiPayload.rawText) ||
          apiPayload.contentType.toLowerCase().includes("text/html")
        ) {
          userFacingError = `### ⚠️ API RETURNED HTML INSTEAD OF JSON (HTTP ${response.status})

/api/chat did not return a valid JSON response. That usually means the server route crashed, Vite served the app shell, a gateway/proxy returned HTML, or the request failed before the API error handler.

**Server preview:** ${clipApiText(apiPayload.rawText)}

The model did not read the document from this request. Fix the /api/chat route/error handling or reduce only the malformed document payload path.`;
        } else {
          userFacingError = `### ⚠️ API ERROR (HTTP ${response.status})

${errorText}

The response was handled safely without trying to parse HTML as JSON.`;
        }

        const aiMsg: ChatMessage = {
          id: `ai_err_${Date.now()}`,
          role: "assistant",
          content: userFacingError,
          timestamp: new Date().toLocaleTimeString(),
          commands: [],
          reports: [],
        };
        if (isCurrentRequestActive()) {
          setMessages((prev) => [...prev, aiMsg]);
        }
      }
    } catch (err: any) {
      if (err?.name === "AbortError" || abortController.signal.aborted) {
        return;
      }

      console.error("Failure submitting chat query:", err);
      const aiMsg: ChatMessage = {
        id: `ai_err_${Date.now()}`,
        role: "assistant",
        content: `### ⚠️ SYSTEM SUBMISSION TIMEOUT / FAILURE\n\nFailed to dispatch transmission query to RodgersPC workstation.\n\n**Error Outline:** ${err.message || err}\n\n*Please check network/server status. Do not reduce Drive Brain scope; payload packing should handle large RodgersPC Drive context.*`,
        timestamp: new Date().toLocaleTimeString(),
        commands: [],
        reports: [],
      };
      if (isCurrentRequestActive()) {
        setMessages((prev) => [...prev, aiMsg]);
      }
    } finally {
      window.clearInterval(progressPollTimer);
      if (activeChatRequestIdRef.current === requestId) {
        activeChatRequestIdRef.current = null;
        activeChatAbortControllerRef.current = null;
        setLoading(false);
        window.setTimeout(() => setRequestProgressSteps([]), 900);
        if (!bottomPanelVisible) {
          refocusMiniInput();
        }
      }
    }
  };

  const handleCancelRequest = () => {
    pushRequestProgressStep("Client: request cancellation requested by Joshua.");
    const controller = activeChatAbortControllerRef.current;

    if (controller && !controller.signal.aborted) {
      controller.abort();
    }

    activeChatAbortControllerRef.current = null;
    activeChatRequestIdRef.current = null;
    setLoading(false);
    setRequestProgressSteps([]);
  };

  const handleRetryMessage = (assistantMessageId: string) => {
    const failedIndex = messages.findIndex((m) => m.id === assistantMessageId);
    if (failedIndex < 0) return;

    for (let i = failedIndex - 1; i >= 0; i--) {
      const candidate = messages[i];
      if (candidate.role === "user") {
        handleSendMessage(candidate.content, candidate.attachments || []);
        return;
      }
    }
  };

  const handleModeChange = async (newMode: AppMode) => {
    const oldMode = currentMode;
    if (newMode === oldMode) return;

    const modeNeedsDriveByDefault =
      newMode === AppMode.BLOG_ENGINE || newMode === AppMode.WEB_CREATIVE;

    setCurrentMode(newMode);
    applyModeDefaultSettings(newMode);

    const shouldUseDriveForMode = modeNeedsDriveByDefault || driveSyncEnabled;

    if (shouldUseDriveForMode) {
      driveSyncEnabledRef.current = true;
      const updatedDriveContext = applyDefaultDriveContextForMode(items, newMode);
      setItems(updatedDriveContext);

      const activeToken = await resolveDriveAccessToken("mode_switch_source_of_truth_preload", {
        allowPopup: true,
        forceAccountChooser: true,
        showNotice: true,
      });

      if (activeToken) {
        void downloadSourceOfTruthMissingFileContents(updatedDriveContext, activeToken);
      }
    }

    // Support mid-chat mode switching: append Mode Shift indicator inline to avoid wiping history.
    // Mode defaults are automatically applied at the same time, but Joshua can manually override after the switch.
    if (messages.length > 0) {
      const modeIndicatorMsg: ChatMessage = {
        id: `mode_shift_${Date.now()}`,
        role: "assistant",
        content: `Mode shifted from ${getModeDisplayName(oldMode)} to ${getModeDisplayName(newMode)}. Prior chat, screenshots, and previous mode conclusions remain part of this job context.`,
        timestamp: new Date().toLocaleTimeString(),
        isModeChangeIndicator: true,
        newMode: newMode,
        prevMode: oldMode,
      };
      setMessages((prev) => [...prev, modeIndicatorMsg]);
    }
  };

  const handleDriveSyncToggle = async () => {
    const nextValue = !driveSyncEnabled;
    setDriveSyncEnabled(nextValue);
    driveSyncEnabledRef.current = nextValue;

    if (!nextValue) {
      setDriveNotice("### Drive Sync OFF\n\nDrive Brain is disabled. No Drive file content will be loaded, read, summarized, quoted, or sent to the model while this toggle is OFF.");
      return;
    }

    setDriveNotice("### Drive Sync ON\n\nDrive Brain is reconnecting. Checked/gold Source of Truth files will be loaded as needed.");

    const activeToken = await resolveDriveAccessToken("drive_sync_toggle_on", {
      allowPopup: true,
      forceAccountChooser: true,
      showNotice: true,
    });

    if (activeToken) {
      await loadGoogleDriveHierarchy(activeToken, DEFAULT_DRIVE_FOLDER_ID, undefined, true);
    }
  };

  const isGlobalLight = globalTheme === "light";
  const isGlobalDark = globalTheme === "dark";
  const isInputLight = inputTheme === "light";
  const isInputDark = inputTheme === "dark";
  const isInputRodgers = inputTheme === "rodgerspc";
  const appBgClassName = isGlobalLight
    ? "bg-[#F6F7FA] text-[#111827]"
    : isGlobalDark
      ? "bg-[#090A0E] text-[#F4F6FA]"
      : "bg-[#071A3D] text-white";

  const isAllowedUser =
    user && user.email && ALLOWED_EMAILS.includes(user.email.toLowerCase());

  // 1. Loading Gateway
  if (authLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-[#071A3D] text-white">
        <div className="flex flex-col items-center gap-4 rounded-[24px] border border-white/10 bg-white/[0.06] px-8 py-7 text-center shadow-2xl backdrop-blur-xl">
          <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl border border-[#F6A400]/30 bg-[#071A3D] shadow-lg">
            <div className="absolute inset-0 rounded-2xl border border-[#F6A400]/20 animate-pulse" />
            <Lock className="h-5 w-5 text-[#F6A400]" />
          </div>
          <div>
            <h2 className="text-[12px] font-semibold tracking-[0.20em] uppercase">
              RodgersPC Command Center
            </h2>
            <p className="mt-1.5 text-[11px] text-white/55">
              Verifying secure workspace session
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 2. Sign-in Screen
  if (!user) {
    const gatewayShellClass = isGlobalLight
      ? "bg-[#F6F7FA] text-[#111827]"
      : isGlobalDark
        ? "bg-[#090A0E] text-[#F4F6FA]"
        : "bg-[#071A3D] text-white";

    const gatewayCardClass = isGlobalLight
      ? "border-black/10 bg-white text-[#111827] shadow-[0_24px_80px_rgba(15,23,42,0.12)]"
      : isGlobalDark
        ? "border-white/10 bg-[#111318] text-[#F4F6FA] shadow-[0_24px_80px_rgba(0,0,0,0.55)]"
        : "border-[#F6A400]/20 bg-[#081F49]/96 text-white shadow-[0_24px_90px_rgba(0,0,0,0.45)]";

    const mutedTextClass = isGlobalLight ? "text-slate-500" : "text-white/56";
    const panelClass = isGlobalLight
      ? "border-black/10 bg-[#F6F7FA]"
      : isGlobalDark
        ? "border-white/10 bg-white/[0.04]"
        : "border-[#F6A400]/15 bg-[#071A3D]/65";

    return (
      <div className={`relative flex h-screen w-screen items-center justify-center overflow-hidden p-6 ${gatewayShellClass}`}>
        <div className="pointer-events-none absolute inset-0 opacity-80">
          <div className="absolute left-1/2 top-[-220px] h-[520px] w-[760px] -translate-x-1/2 rounded-full bg-[#F6A400]/10 blur-3xl" />
          <div className="absolute bottom-[-260px] right-[-220px] h-[560px] w-[560px] rounded-full bg-[#143F8F]/20 blur-3xl" />
        </div>

        <div className={`relative w-full max-w-[380px] rounded-[24px] border p-6 backdrop-blur-xl ${gatewayCardClass}`}>
          <div className="flex flex-col items-center text-center">
            <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border ${isGlobalLight ? "border-black/10 bg-white text-[#143F8F]" : "border-[#F6A400]/25 bg-black/18 text-[#F6A400]"}`}>
              <Lock className="h-5 w-5" />
            </div>

            <div className="mb-5">
              <div className={`mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] ${isGlobalLight ? "text-[#143F8F]" : "text-[#F6A400]"}`}>
                Secure Internal Workspace
              </div>
              <h1 className="text-[19px] font-semibold tracking-[-0.02em]">
                RodgersPC Command Center
              </h1>
              <p className={`mx-auto mt-2 max-w-xs text-[12px] leading-5 ${mutedTextClass}`}>
                Bench diagnostics, Drive records, sourcing, customer wording, and business work in one private RodgersPC console.
              </p>
            </div>

            <div className={`mb-4 w-full rounded-2xl border p-3 text-left ${panelClass}`}>
              <label htmlFor="safe-guest-mode-chk" className="flex cursor-pointer items-start gap-2.5">
                <input
                  type="checkbox"
                  id="safe-guest-mode-chk"
                  checked={guestModeActive}
                  onChange={(e) => handleCustomerBenchModeChange(e.target.checked)}
                  className={`mt-0.5 h-3.5 w-3.5 rounded ${isGlobalLight ? "accent-[#143F8F]" : "accent-[#F6A400]"}`}
                />
                <span>
                  <span className="block text-[12px] font-semibold">
                    Customer / unknown device
                  </span>
                  <span className={`mt-1 block text-[11px] leading-5 ${mutedTextClass}`}>
                    Use this when the browser is not already signed into an approved RodgersPC account. It forces a fresh Google sign-in flow and avoids app session persistence.
                  </span>
                </span>
              </label>
            </div>

            {loginError && (
              <div className={`mb-4 w-full rounded-2xl border px-3 py-2.5 text-left text-[11px] leading-5 ${
                isGlobalLight
                  ? "border-rose-200 bg-rose-50 text-rose-800"
                  : "border-rose-400/25 bg-rose-950/25 text-rose-100"
              }`}>
                {loginError}
              </div>
            )}

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loginBusy}
              className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl border px-4 py-2.5 text-[12px] font-semibold transition disabled:cursor-wait disabled:opacity-60 ${
                isGlobalLight
                  ? "border-[#143F8F] bg-[#143F8F] text-white hover:bg-[#0B2F73]"
                  : "border-[#F6A400] bg-[#F6A400] text-[#071A3D] hover:bg-[#FFB72B]"
              }`}
              id="gateway-signin-btn"
            >
              <LogIn className="h-4 w-4" />
              <span>{loginBusy ? "Opening Google sign-in..." : "Sign in with Google"}</span>
            </button>

            <div className="mt-4 grid w-full grid-cols-3 gap-2">
              {[
                ["rodgerspc", "RodgersPC"],
                ["dark", "Dark"],
                ["light", "Light"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setGlobalTheme(value as UiTheme)}
                  className={`rounded-xl border px-3 py-1.5 text-[10px] font-medium transition ${
                    globalTheme === value
                      ? isGlobalLight
                        ? "border-[#143F8F] bg-[#143F8F] text-white"
                        : "border-[#F6A400] bg-[#F6A400]/14 text-[#F6A400]"
                      : isGlobalLight
                        ? "border-black/10 bg-white text-slate-500 hover:text-slate-900"
                        : "border-white/10 bg-white/[0.04] text-white/50 hover:text-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <p className={`mt-4 text-[10px] ${mutedTextClass}`}>
              Approved RodgersPC Google account required.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 3. Unauthorized Access Attempt
  if (user && !isAllowedUser) {
    const deniedShellClass = isGlobalLight
      ? "bg-[#F6F7FA] text-[#111827]"
      : isGlobalDark
        ? "bg-[#090A0E] text-[#F4F6FA]"
        : "bg-[#071A3D] text-white";

    const deniedCardClass = isGlobalLight
      ? "border-rose-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.12)]"
      : "border-rose-400/25 bg-[#111318] shadow-[0_24px_80px_rgba(0,0,0,0.55)]";

    return (
      <div className={`flex h-screen w-screen items-center justify-center p-6 ${deniedShellClass}`}>
        <div className={`w-full max-w-[430px] rounded-[30px] border p-8 text-center ${deniedCardClass}`}>
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-rose-400/30 bg-rose-500/10 text-rose-500">
            <ShieldAlert className="h-8 w-8" />
          </div>

          <div className="mb-6">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-rose-500">
              Access denied
            </div>
            <h1 className="text-[21px] font-semibold tracking-[-0.02em]">
              Account not authorized
            </h1>
            <p className={`mx-auto mt-2 max-w-xs text-[12px] leading-5 ${isGlobalLight ? "text-slate-500" : "text-white/56"}`}>
              This Google account is not approved for RodgersPC Command Center. Sign out, then use an approved RodgersPC account.
            </p>
          </div>

          <button
            type="button"
            onClick={handleGoogleLogout}
            className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl border px-5 py-3 text-[13px] font-semibold transition ${
              isGlobalLight
                ? "border-black/10 bg-[#111827] text-white hover:bg-black"
                : "border-white/10 bg-white/[0.06] text-white hover:bg-white/[0.1]"
            }`}
          >
            <LogOut className="h-4 w-4" />
            <span>Sign out and use another account</span>
          </button>
        </div>
      </div>
    );
  }

    return (
    <div
      className={`relative flex flex-col h-screen overflow-hidden font-sans transition-all ${appBgClassName}`}
    >
      {driveNotice && (
        <div
          className={`absolute right-4 top-4 z-50 max-w-md rounded-2xl border px-4 py-3 text-[12px] shadow-2xl backdrop-blur-xl ${
            isGlobalLight
              ? "border-black/10 bg-white text-slate-800 shadow-slate-300/40"
              : isGlobalDark
                ? "border-white/10 bg-[#111318]/95 text-white shadow-black/60"
                : "border-[#F6A400]/20 bg-[#081F49]/95 text-white shadow-black/50"
          }`}
        >
          <div className="flex items-start gap-3">
            <Activity className={`mt-0.5 h-4 w-4 shrink-0 ${isGlobalLight ? "text-[#143F8F]" : "text-[#F6A400]"}`} />
            <div className="min-w-0 whitespace-pre-wrap leading-relaxed">
              {driveNotice}
            </div>
            <button
              type="button"
              onClick={() => setDriveNotice(null)}
              className={`ml-1 rounded-full p-1 transition ${isGlobalLight ? "text-slate-400 hover:bg-slate-100 hover:text-slate-900" : "text-white/50 hover:bg-white/10 hover:text-white"}`}
              title="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* MAIN LAYOUT */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* LEFT PANEL: shared space for Drive Brain or manually saved Chats. */}
        {driveSidebarVisible && (
          <div className="w-80 shrink-0 hidden md:flex md:flex-col">
            <div
              className={`relative flex h-full min-h-0 flex-col border-r ${
                isGlobalLight
                  ? "border-slate-200 bg-[#EEF3FA] text-slate-900"
                  : isGlobalDark
                    ? "border-white/10 bg-[#10131B] text-white"
                    : "border-[#F6A400]/18 bg-[#071A3D] text-white"
              }`}
              id="left-memory-panel"
              data-left-panel-view={leftPanelView}
            >


              {leftPanelView === "drive" ? (
                driveSyncEnabled ? (
                  <div className="min-h-0 flex-1">
                    <DriveWorkspace
                      user={user}
                      token={token}
                      authLoading={authLoading}
                      onLogin={handleGoogleLogin}
                      onLogout={handleGoogleLogout}
                      items={items}
                      driveLoading={driveLoading}
                      onToggleCheck={handleToggleCheck}
                      onTogglePin={handleTogglePin}
                      onUploadFile={handleUploadFile}
                      onFetchContent={handleFetchContent}
                      onAttachDriveFile={handleAttachDriveFile}
                      activeContextIds={activeContextList.map((i) => i.id)}
                      pinnedIds={[]}
                      globalTheme={globalTheme}
                      onRefreshDrive={async () => {
                        emptyTreeAutoReloadAttemptedRef.current = false;

                        const activeToken = await resolveDriveAccessToken("manual_drive_refresh", {
                          allowPopup: true,
                          forceAccountChooser: true,
                          showNotice: true,
                        });

                        if (activeToken) {
                          await loadGoogleDriveHierarchy(
                            activeToken,
                            DEFAULT_DRIVE_FOLDER_ID,
                            undefined,
                            true,
                          );
                        } else {
                          console.warn(
                            "[Drive Restore] Refresh clicked but Drive token is missing. Re-authentication required.",
                          );
                        }
                      }}
                      dbLoaded={dbLoaded}
                      leftPanelView={leftPanelView}
                      onLeftPanelViewChange={setLeftPanelView}
                      savedChatCount={savedChats.length}
                    />
                  </div>
                ) : (
                  <div className="flex flex-1 items-center justify-center p-5 text-center">
                    <div className={`rounded-2xl border p-4 text-[11px] leading-5 ${
                      isGlobalLight
                        ? "border-slate-200 bg-white text-slate-600"
                        : "border-white/10 bg-white/[0.04] text-white/58"
                    }`}>
                      Drive Sync is OFF. Turn Drive on from the input controls to use Drive Brain.
                    </div>
                  </div>
                )
              ) : (
                <SavedChatsPanel
                  folders={savedChatFolders}
                  chats={savedChats}
                  activeChatId={activeSavedChatId}
                  globalTheme={globalTheme}
                  leftPanelView={leftPanelView}
                  onLeftPanelViewChange={setLeftPanelView}
                  onOpenChat={handleOpenSavedChat}
                  onRenameChat={handleRenameSavedChat}
                  onMoveChat={handleMoveSavedChat}
                  onDeleteChat={handleDeleteSavedChat}
                  onCreateFolder={handleCreateSavedChatFolder}
                  onDeleteFolder={handleDeleteSavedChatFolder}
                />
              )}
            </div>
          </div>
        )}

        {/* CENTER PANEL (Chat outputs + commands) */}
        <div className="flex-1 flex flex-col h-full min-w-0 relative">
          {/* Chat outputs stream section */}
          <div className="flex-1 flex flex-col min-h-0">
            <ThemedChatWorkspace
              messages={messages}
              currentMode={currentMode}
              onModeChange={handleModeChange}
              activeContextCount={
                driveSyncEnabled ? activeContextList.length : 0
              }
              totalContextCharacters={driveSyncEnabled ? totalCharsCount : 0}
              loading={loading}
              requestProgressSteps={requestProgressSteps}
              driveSyncEnabled={driveSyncEnabled}
              driveSidebarVisible={driveSidebarVisible}
              onToggleDriveSidebar={() =>
                setDriveSidebarVisible(!driveSidebarVisible)
              }
              onOpenExporter={() => setIsExporterOpen(true)}
              onCreateNotes={handleCreateNotes}
              savedChatFolders={savedChatFolders}
              savedChats={savedChats}
              activeSavedChatId={activeSavedChatId}
              defaultSavedChatFolderId={getDefaultSavedChatFolderId(currentMode)}
              suggestedSavedChatTitle={suggestSavedChatTitle()}
              onSaveCurrentChatToFolder={handleSaveCurrentChatToFolder}
              onCreateSavedChatFolder={handleCreateSavedChatFolder}
              globalTheme={globalTheme}
              onChangeGlobalTheme={setGlobalTheme}
              chatTheme={chatTheme}
              onChangeChatTheme={setChatTheme}
              inputTheme={inputTheme}
              onChangeInputTheme={setInputTheme}
              inputPanelVisible={bottomPanelVisible}
              onToggleInputPanel={() =>
                setBottomPanelVisible(!bottomPanelVisible)
              }
              onClearChat={handleClearChat}
              onRetryMessage={handleRetryMessage}
              onCancelRequest={handleCancelRequest}
              onUpdateMessage={handleUpdateMessage}
              onUpdateReport={handleUpdateReport}
              dbLoaded={dbLoaded}
              hasUnclearedChat={
                lastSavedState &&
                lastSavedState.messages &&
                lastSavedState.messages.length > 0
              }
              onContinueLastChat={handleContinueLastChat}
              onClearLastChatBackup={handleClearLastSavedChat}
              lastChatBackupClean={lastSessionCleanNotice}
              lastSavedChatCount={
                lastSavedState && lastSavedState.messages
                  ? lastSavedState.messages.length
                  : 0
              }
            />
          </div>

          {/* BOTTOM PANEL: Multi-line InputEngine with the 4 control toggles */}
          {bottomPanelVisible ? (
            <div
              className={`relative shrink-0 border-t ${
                isInputLight
                  ? "border-slate-200 bg-[#EEF3FA]"
                  : isInputDark
                    ? "border-white/10 bg-[#11141D]"
                    : "border-[#F6A400]/18 bg-[#071A3D]"
              }`}
              id="bottom-input-panel-shell"
              data-input-theme={inputTheme}
            >
              <div
                ref={outputLengthPopoverRef}
                className="absolute right-3 top-2 z-40"
                id="output-length-floating-control"
              >
                <button
                  type="button"
                  onClick={() => setOutputLengthPanelVisible((prev) => !prev)}
                  className={`flex h-[24px] items-center gap-1.5 rounded-[8px] border px-2 py-0 text-[10px] font-semibold uppercase tracking-[0.04em] shadow-sm backdrop-blur-xl transition ${
                    isInputLight
                      ? "border-slate-200 bg-white/92 text-[#143F8F] shadow-slate-300/35 hover:bg-slate-50"
                      : isInputDark
                        ? "border-white/10 bg-[#171C27]/92 text-white/72 shadow-black/50 hover:bg-[#202636] hover:text-white"
                        : "border-[#F6A400]/25 bg-[#071A3D]/90 text-[#F6A400] shadow-black/45 hover:bg-[#F6A400]/10"
                  }`}
                  aria-expanded={outputLengthPanelVisible}
                  aria-controls="output-length-popover-panel"
                  title={outputLengthPanelVisible ? "Hide output length control" : "Open output length control"}
                >
                  <span className="text-[9px] leading-none">{outputLengthPanelVisible ? "▾" : "▴"}</span>
                  <span
                    className="text-[10px] leading-none"
                    style={{ fontSize: "10px", lineHeight: "10px", letterSpacing: "0.02em" }}
                  >
                    Len: {activeResponseLength.label}
                  </span>
                </button>

                {outputLengthPanelVisible && (
                  <div
                    id="output-length-popover-panel"
                    className={`absolute bottom-full right-0 mb-2 w-[390px] max-w-[calc(100vw-380px)] rounded-xl border p-2.5 shadow-xl backdrop-blur-xl ${
                      isInputLight
                        ? "border-slate-200 bg-white/96 text-slate-800 shadow-slate-300/45"
                        : isInputDark
                          ? "border-white/10 bg-[#171C27]/96 text-white shadow-black/60"
                          : "border-[#F6A400]/20 bg-[#081F49]/96 text-white shadow-black/58"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div
                          className={`text-[9px] font-semibold uppercase tracking-[0.12em] ${
                            isInputLight
                              ? "text-[#143F8F]"
                              : "text-[#F6A400]"
                          }`}
                        >
                          Output Length
                        </div>
                        <div
                          className={`mt-1 text-[10px] leading-4 ${
                            isInputLight ? "text-slate-500" : "text-white/56"
                          }`}
                        >
                          Talk less / talk more only. Same intelligence and truth rules.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setOutputLengthPanelVisible(false)}
                        className={`rounded-md p-0.5 transition ${
                          isInputLight
                            ? "text-slate-400 hover:bg-slate-100 hover:text-slate-900"
                            : "text-white/45 hover:bg-white/10 hover:text-white"
                        }`}
                        title="Hide output length control"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="mt-2">
                      <input
                        type="range"
                        min={0}
                        max={OUTPUT_LENGTH_LEVELS.length - 1}
                        step={1}
                        value={responseLengthIndex}
                        onChange={handleOutputLengthSliderChange}
                        className="h-1 w-full cursor-pointer accent-[#F6A400]"
                        aria-label="RodgersPC output length"
                      />

                      <div className="mt-2 grid grid-cols-5 gap-1.5">
                        {OUTPUT_LENGTH_LEVELS.map((option) => {
                          const isActive = option.value === responseLength;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => handleResponseLengthChange(option.value)}
                              className={`flex h-[20px] min-w-0 items-center justify-center rounded-[6px] px-1 text-[10px] font-semibold uppercase tracking-[0.02em] leading-none transition ${
                                isActive
                                  ? isInputLight
                                    ? "bg-[#143F8F] text-white"
                                    : "bg-[#F6A400] text-[#071A3D]"
                                  : isInputLight
                                    ? "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                                    : "text-white/48 hover:bg-white/[0.07] hover:text-white"
                              }`}
                              style={{
                                fontSize: "10px",
                                lineHeight: "10px",
                                letterSpacing: "0.02em",
                              }}
                              title={option.helper}
                            >
                              <span className="block truncate">{option.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div
                      className={`mt-2 rounded-lg border px-2 py-1 text-[10px] leading-4 ${
                        isInputLight
                          ? "border-slate-200 bg-[#F8FAFC] text-slate-700"
                          : isInputDark
                            ? "border-white/10 bg-white/[0.04] text-white/70"
                            : "border-[#F6A400]/16 bg-[#071A3D]/45 text-white/72"
                      }`}
                    >
                      <span className="font-semibold">{activeResponseLength.label}:</span>{" "}
                      {activeResponseLength.helper}
                    </div>
                  </div>
                )}
              </div>

              <ThemedInputEngine
                onSendMessage={handleSendMessage}
                currentMode={currentMode}
                activeContextCount={
                  driveSyncEnabled ? activeContextList.length : 0
                }
                searchGroundingEnabled={searchGroundingEnabled}
                onToggleSearchGrounding={() =>
                  setSearchGroundingEnabled(!searchGroundingEnabled)
                }
                imageGenerationEnabled={imageGenerationEnabled}
                onToggleImageGeneration={() =>
                  setImageGenerationEnabled(!imageGenerationEnabled)
                }
                driveSyncEnabled={driveSyncEnabled}
                onToggleDriveSync={handleDriveSyncToggle}
                fastModeEnabled={fastModeEnabled}
                onToggleFastMode={() => setFastModeEnabled(!fastModeEnabled)}
                aiTemperature={aiTemperature}
                onTemperatureChange={setAiTemperature}
                accuracyMode={accuracyMode}
                onAccuracyModeChange={handleAccuracyModeChange}
                responseLength={responseLength}
                onResponseLengthChange={handleResponseLengthChange}
                jobSensitivity={jobSensitivity}
                onJobSensitivityChange={handleJobSensitivityChange}
                aiPreset={aiPreset}
                onSelectPreset={handleSelectPreset}
                onOpenExporter={() => setIsExporterOpen(true)}
                globalTheme={globalTheme}
                chatTheme={chatTheme}
                inputTheme={inputTheme}
                onUploadFile={handleUploadFile}
              />
            </div>
          ) : (
            <div className="absolute bottom-5 left-1/2 z-40 w-full max-w-4xl -translate-x-1/2 px-4">
              {(capsuleAttachment || capsuleImage || capsuleAttachedFileName) && (
                <div
                  className={`mb-2 flex items-center justify-between rounded-2xl border px-3 py-2 shadow-lg backdrop-blur-xl ${
                    isInputLight
                      ? "border-slate-200 bg-white/92 text-slate-950 shadow-slate-300/50"
                      : isInputDark
                        ? "border-white/10 bg-[#171C27]/96 text-white shadow-black/60"
                        : "border-[#F6A400]/22 bg-[#081F49]/96 text-white shadow-black/52"
                  }`}
                  data-input-theme={isInputLight ? "light" : isInputDark ? "dark" : "rodgerspc"}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {capsuleImage ? (
                      <img
                        src={capsuleImage}
                        alt="Attached preview"
                        className="h-8 w-8 rounded-xl border border-white/10 object-cover"
                      />
                    ) : (
                      <FileText className={`h-4 w-4 ${isInputLight ? "text-[#143F8F]" : "text-[#F6A400]"}`} />
                    )}
                    <span
                      className="truncate text-[12px]"
                      title={capsuleAttachedFileName || "attachment"}
                    >
                      {capsuleAttachedFileName || "Attached file"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setCapsuleImage(null);
                      setCapsuleAttachedFileName(null);
                      setCapsuleAttachment(null);
                    }}
                    className={`rounded-md p-0.5 transition ${isInputLight ? "text-slate-400 hover:bg-slate-100 hover:text-slate-900" : "text-white/45 hover:bg-white/10 hover:text-white"}`}
                    title="Remove attachment"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              <div
                className={`flex w-full items-end gap-2 rounded-[26px] border px-3 py-2 shadow-2xl backdrop-blur-xl transition ${
                  isInputLight
                    ? "border-slate-300/80 bg-white/94 text-slate-950 shadow-[0_18px_48px_rgba(15,23,42,0.18),inset_0_1px_0_rgba(255,255,255,0.9)]"
                    : isInputDark
                      ? "border-white/12 bg-[#171C27]/96 text-white shadow-black/62"
                      : "border-[#F6A400]/26 bg-[#081F49]/96 text-white shadow-black/54"
                }`}
                id="floating-capsule-input"
                data-input-theme={isInputLight ? "light" : isInputDark ? "dark" : "rodgerspc"}
              >
                <button
                  type="button"
                  onClick={() => setBottomPanelVisible(true)}
                  className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border transition ${
                    isInputLight
                      ? "border-slate-200 bg-slate-50 text-slate-600 hover:bg-white hover:text-[#143F8F]"
                      : "border-white/10 bg-white/[0.05] text-white/60 hover:bg-white/[0.09] hover:text-[#F6A400]"
                  }`}
                  title="Open full command console"
                  id="capsule-plus-btn"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>

                <textarea
                  ref={miniTextareaRef}
                  rows={1}
                  value={miniInput}
                  onChange={(e) => setMiniInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (loading) return;
                      if (miniInput.trim() || capsuleAttachment) {
                        handleSendMessage(
                          miniInput,
                          capsuleAttachment ? [capsuleAttachment] : undefined,
                        );
                        setMiniInput("");
                        setCapsuleImage(null);
                        setCapsuleAttachedFileName(null);
                        setCapsuleAttachment(null);
                        refocusMiniInput();
                      }
                    }
                  }}
                  onPaste={(e) => {
                    const clipboardData = e.clipboardData;
                    if (!clipboardData) return;

                    if (clipboardData.files && clipboardData.files.length > 0) {
                      e.preventDefault();
                      const file = clipboardData.files[0];
                      loadCapsuleAttachmentFromFile(file, "Pasted");
                      return;
                    }

                    const items = clipboardData.items;
                    for (let i = 0; i < items.length; i++) {
                      if (items[i].kind === "file") {
                        const file = items[i].getAsFile();
                        if (file) {
                          e.preventDefault();
                          loadCapsuleAttachmentFromFile(file, "Pasted");
                          return;
                        }
                      }
                    }
                  }}
                  placeholder="Ask RodgersPC Command Center..."
                  className="min-h-[38px] flex-1 resize-none border-0 bg-transparent px-1.5 py-2 text-[13px] leading-5 text-inherit outline-none placeholder:text-slate-400 focus:outline-none focus:ring-0"
                  style={{
                    height: "38px",
                    minHeight: "38px",
                    maxHeight: "150px",
                  }}
                  id="capsule-prompt-textarea"
                />

                <input
                  type="file"
                  id="capsule-inline-file-upload"
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files && files.length > 0) {
                      const file = files[0];
                      loadCapsuleAttachmentFromFile(file, "Uploaded");
                      e.currentTarget.value = "";
                    }
                  }}
                  accept="*"
                  className="hidden"
                />
                <label
                  htmlFor="capsule-inline-file-upload"
                  className={`mb-0.5 flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-2xl border transition ${
                    isInputLight
                      ? "border-slate-200 bg-slate-50 text-slate-500 hover:bg-white hover:text-[#143F8F]"
                      : "border-white/10 bg-white/[0.05] text-white/55 hover:bg-white/[0.09] hover:text-white"
                  }`}
                  title="Attach file or screenshot"
                >
                  <Paperclip className="h-4 w-4" />
                </label>

                {loading && (
                  <button
                    type="button"
                    onClick={handleCancelRequest}
                    className={`mb-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-all duration-200 transform hover:scale-110 active:scale-95 shadow-sm cursor-pointer ${
                      isInputLight
                        ? "border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100 shadow-rose-100"
                        : "border-rose-500/30 bg-rose-950/20 hover:bg-rose-950/45 text-rose-300 shadow-black"
                    }`}
                    title="Cancel response"
                    id="capsule-cancel-circle-btn"
                  >
                    <X className="h-3.5 w-3.5 stroke-[2.5px]" />
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    if (miniInput.trim() || capsuleAttachment) {
                      handleSendMessage(
                        miniInput,
                        capsuleAttachment ? [capsuleAttachment] : undefined,
                      );
                      setMiniInput("");
                      setCapsuleImage(null);
                      setCapsuleAttachedFileName(null);
                      setCapsuleAttachment(null);
                    }
                  }}
                  disabled={loading || (!miniInput.trim() && !capsuleAttachment)}
                  className={`mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl transition disabled:cursor-not-allowed ${
                    loading
                      ? isInputLight
                        ? "bg-slate-100 text-slate-400"
                        : "bg-white/[0.04] text-white/20"
                      : miniInput.trim() || capsuleAttachment
                        ? isInputLight
                          ? "bg-[#143F8F] text-white hover:bg-[#0B2F73]"
                          : "bg-[#F6A400] text-[#071A3D] hover:bg-[#FFB72B]"
                        : isInputLight
                          ? "bg-slate-100 text-slate-300"
                          : "bg-white/[0.04] text-white/25"
                  }`}
                  title={loading ? "Thinking..." : "Send"}
                  id="capsule-send-btn"
                >
                  {loading ? <RefreshCw className="h-4 w-4 animate-spin text-[#F6A400]" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Premium Drawer/Modal Exporter Overlay */}
      <AIExporterModal
        isOpen={isExporterOpen}
        onClose={() => setIsExporterOpen(false)}
        messages={messages}
        currentMode={currentMode}
        onUploadFile={handleUploadFile}
        driveSyncEnabled={driveSyncEnabled}
        globalTheme={globalTheme}
      />
    </div>
  );
}
